<template>
  <f7-page name="facturacion-control-diario" @page:init="onPageInit">
    <f7-navbar>
      <f7-nav-left><f7-link back icon-f7="arrow_left"></f7-link></f7-nav-left>
      <!-- #248 · el rótulo que fijó dirección el 05/10/2026. Es el MISMO texto que la tarjeta de
           entrada de la pestaña (`facturacion-menu.vue`) y que el papel del PDF: con dos nombres
           para una pantalla, quien la busca no sabe que es la misma. -->
      <f7-nav-title>CONTROL DIARIO FACTURACIÓN</f7-nav-title>
      <f7-nav-right>
        <!--
          PROGRAMAR REPORTE (#549) · en la barra y NO en la fila de la cabecera, a propósito. Esa
          fila la fijó dirección el 18/10/2026 —«Enviar reporte» a la izquierda de «Exportar a PDF»
          y «Guardar» el último, los tres en una línea— y un cuarto botón la reorganiza: con
          `nowrap` ya desborda en móvil a partir de ~390 px, y dejándolo envolver vuelven a
          apilarse, que es justo lo que dirección rechazó. CLAUDE.md §9: lo nuevo no reorganiza lo
          que ya funcionaba. Aquí además coincide con «Abonos abiertos», que tiene su «Programar
          reporte» en la barra desde la #193: la misma acción en el mismo sitio en las dos
          pantallas.

          `v-roles` con la lista del módulo de permisos: la barra está FUERA del `v-roles` de la
          página, así que sin esto el punto de entrada se le dibujaría a quien no puede usarlo y el
          diálogo contestaría 403 (§9: lo que no es alcanzable no se dibuja).
        -->
        <f7-link v-roles="rolesPestana" class="navbar-programar" @click="abrirProgramaciones">
          Programar reporte
        </f7-link>
        <!-- Apagado mientras se guarda o se recalcula un objetivo: `recargar` vacía el borrador y
             hay peticiones en vuelo cuya respuesta habla de este mes. -->
        <f7-link @click="recargar" icon-f7="arrow_2_circlepath"
          :class="{ 'enlace-apagado': ocupado }" style="color:#c8d3e5"></f7-link>
      </f7-nav-right>
    </f7-navbar>
    <!--
      #16 · el sello de «de cuándo son estos datos», en el mismo sitio que en el resto de las
      pantallas, y solo cuando hay cifras en pantalla: con la pestaña cerrada, con un 403 de rol o
      con un mes sin ficha no tiene de qué hablar, y un «Datos en vivo» encima de un aviso de que no
      hay cifras contradice al propio aviso (mismo criterio que la Tarjeta B). Y cuando las cifras
      están en pantalla pero no se han podido releer, el sello lo dice: ver `estadoDelSello`.
    -->
    <div class="sello-datos-bar" v-if="hayCifras"><sello-datos :estado="estadoDelSello" /></div>
    <f7-page-content style="padding-top:0" v-roles="['ADMIN','DIRECTOR']">

      <!--
        PESTAÑA CERRADA (#169) · el servidor no puede responder de las cifras porque la tabla de los
        meses no garantiza una sola ficha por mes. El texto lo escribe la API y aquí NO se reescribe
        ni se resume: eso convertiría un aviso accionable en un «ha habido un problema». Se enseña
        solo esto, porque una pantalla de cifras a medias haría dudar de las que se ven.
      -->
      <f7-block v-if="pestanaCerrada" class="aviso-cerrada">
        {{ pestanaCerrada }}
      </f7-block>

      <template v-if="!pestanaCerrada">

        <!-- ================================================================ 1 · CABECERA -->
        <div class="cabecera">
          <div class="selector">
            <button type="button" class="flecha" :disabled="!hayAnterior || ocupado"
              aria-label="Mes anterior" @click="irAlMesAnterior">
              <f7-icon f7="chevron_left"></f7-icon>
            </button>
            <span class="selector-mes">{{ etiquetaMes }}</span>
            <button type="button" class="flecha" :disabled="!haySiguiente || ocupado"
              aria-label="Mes siguiente" @click="irAlMesSiguiente">
              <f7-icon f7="chevron_right"></f7-icon>
            </button>
            <span class="marca-estado" v-if="mes" :class="mes.enCurso ? 'marca-en-curso' : 'marca-cerrado'">
              {{ mes.etiquetaEstado || '—' }}
            </span>
          </div>
          <div class="cabecera-derecha">
            <!-- El guion de «Recalculado» no se explica solo: un mes en curso se recalcula al abrir
                 la pantalla, así que ahí debería haber una hora. El porqué lo manda la API, que es
                 quien sabe de qué mes se trata; aquí solo se enseña. -->
            <span class="recalculado" :title="(mes && mes.notaRecalculo) || ''">
              Recalculado: <strong>{{ formatInstante(mes && mes.recalculadoEn) }}</strong>
            </span>
            <!--
              LOS TRES BOTONES, EN UNA SOLA FILA (dirección, 18/10/2026) · están juntos en su propia
              caja `nowrap` para que no puedan repartirse en varias líneas: lo que dirección pidió al
              probarlo es que «Enviar reporte» esté en la misma fila que «Exportar a PDF», a su
              izquierda, y que «Guardar» siga siendo el último sin moverse. Con los tres sueltos en
              `cabecera-derecha`, que envuelve, el reparto dependía del ancho de la ventana.
              El orden es el del marcado, no `order` del CSS: lo que se lee en el HTML es lo que se ve.
            -->
            <div class="cabecera-acciones">
              <!--
                ENVIAR REPORTE (#547) · los dos botones arriba a la derecha, como en «Abonos
                abiertos», y este A LA IZQUIERDA de «Exportar a PDF». El correo lo compone la API con
                el mismo mes que esta pantalla está pintando —el que está abierto—; aquí no se
                maqueta nada ni se manda ninguna cifra.

                `v-roles` con la lista del módulo de permisos y no escrita a mano: el contenido de la
                pantalla ya está detrás de los mismos roles, pero este botón manda un correo con las
                cifras de la empresa dentro y su puerta se lee mejor al lado del control. Y la lista
                sale de `permisos-facturacion.js` porque una quinta copia es la que acaba contestando
                distinto (es lo que explica ese fichero).
              -->
              <button type="button" class="btn-enviar" v-roles="rolesPestana"
                :disabled="!sePuedeEnviar" @click="abrirEnvio"
                :title="motivoNoEnviable || 'Manda el mes por correo, con lo que esté guardado al enviarlo'">
                <f7-icon f7="envelope" class="btn-enviar-icono"></f7-icon>
                {{ enviandoReporte ? 'Enviando…' : 'Enviar reporte' }}
              </button>
              <!--
                EXPORTAR A PDF (#173) · el documento lo genera la API con el mismo mes que esta
                pantalla está pintando, así que el papel enseña estas cifras y no unas recalculadas.
                Aquí no se maqueta nada: se pide y se guarda el fichero.
              -->
              <button type="button" class="btn-pdf" :disabled="!sePuedeExportar" @click="exportar"
                :title="motivoNoExportable || 'Descarga el mes en PDF, tal como se ve'">
                <f7-icon f7="arrow_down_doc" class="btn-pdf-icono"></f7-icon>
                {{ exportando ? 'Generando…' : 'Exportar a PDF' }}
              </button>
              <!--
                Guardar manda SOLO lo que se ha tocado, un dato por petición y en orden. No se manda
                la tabla entera: la auditoría (D-AUDIT) registra qué cambió quién, y con la tabla
                completa en una petición habría que deducir los cambios comparando.
              -->
              <button type="button" class="btn-guardar" :disabled="!sePuedeGuardar" @click="guardar">
                <f7-icon f7="checkmark_alt" class="btn-guardar-icono"></f7-icon>
                {{ textoBotonGuardar }}
              </button>
            </div>
            <!-- Por qué está apagado habiendo algo que guardar, aquí y no en un aviso del principio
                 de la pantalla: quien pulsa no relaciona lo uno con lo otro (dirección, 05/10). -->
            <p class="guardar-espera" v-if="porQueNoSePuedeGuardar">{{ porQueNoSePuedeGuardar }}</p>
          </div>
        </div>

        <f7-block v-if="cargando" class="text-align-center"><f7-preloader></f7-preloader></f7-block>

        <f7-block v-if="error" class="aviso-error">{{ error }}</f7-block>

        <!--
          POR QUÉ NO SE PUEDE SACAR EL MES AHORA MISMO (#173, y el correo desde la #547) · el botón
          apagado sin explicación obliga a adivinar, y el caso frecuente —hay cifras tecleadas sin
          guardar— tiene arreglo en un clic. Importa porque el PDF y el correo los genera el servidor
          con lo que CONSTA GUARDADO: sacarlo con datos pendientes daría un documento que no es lo
          que se está viendo, que es justo lo que las dos cosas tienen que garantizar.

          Es UN solo aviso para los dos botones, que están uno al lado del otro y se apagan por lo
          mismo: dos párrafos largos diciendo lo mismo con otro sustantivo se leen como dos problemas
          distintos.
        -->
        <div class="nota-exportar" v-if="motivoNoSaleElDocumento">
          {{ motivoNoSaleElDocumento }}
        </div>

        <!-- El PDF no ha salido. Aparte de `error`, que es el de la carga: aquí la pantalla sigue
             entera y lo único que ha fallado es la descarga. -->
        <div class="aviso-error" v-if="errorExportar">{{ errorExportar }}</div>

        <!--
          CÓMO ACABÓ EL ÚLTIMO CORREO DE ESTE MES (#547) · sale del registro del módulo, no de esta
          instancia, y por eso sigue aquí al salir al menú y volver a entrar: es la señal que dice si
          el informe ya salió, y sin ella se vuelve a pulsar «Enviar reporte» y llega el segundo
          correo a los mismos buzones (P2 Codex, ronda 2).

          En verde o en naranja con las clases que ya usa la pantalla para lo mismo, y se va solo
          cuando se vuelve a mandar ese mes o se cambia de mes: el aviso es del mes del que habla.
        -->
        <div v-if="avisoEnvio"
          :class="avisoEnvio.ok ? 'aviso-guardado-ok'
            : (avisoEnvio.incierto ? 'aviso-envio-incierto' : 'aviso-error')">
          {{ avisoEnvio.texto }}
          <!--
            LA SALIDA DE UN ENVÍO INCIERTO (P2 Codex ronda 3) · va aquí, pegada al aviso que lo
            explica y junto al botón que está apagado, no dentro del diálogo de envío: §9 pide que un
            control que no se puede usar diga por qué, quién lo desbloquea y dónde, y las tres cosas
            son esto. En ámbar y no en rojo porque no es un fallo: es un «no se sabe».

            No manda nada por su cuenta. Levanta la retención y deja «Enviar reporte» disponible, que
            es la decisión que la pantalla no puede tomar: solo quien mire el buzón sabe si llegó.
          -->
          <div class="aviso-envio-salida" v-if="envioIncierto">
            <button type="button" class="btn-envio-otra-vez" @click="repetirAunqueNoSeSepa">
              Mandarlo otra vez de todas formas
            </button>
            <span class="aviso-envio-salida-nota">Si el primero sí salió, llegará una segunda
              copia.</span>
          </div>
        </div>

        <!--
          EL MES EN CURSO SIN HORA DE RECÁLCULO · abrir esta pantalla recalcula el mes en curso
          (D-GUARDADO), así que un guion ahí significa que el recálculo no ha terminado y las cifras
          pueden no estar al día. El servidor absorbe ese fallo para que la pestaña abra igual, y sin
          esto no se vería en ninguna parte. Solo en el mes en curso: en un mes migrado o cerrado el
          guion es normal y lo explica el tooltip de la cabecera.

          Y desde la #1205, también cuando el refresco de ESTE mes no ha llegado a escribir aunque
          haya hora de una vez anterior (`notaRefresco`): el servidor manda el motivo, la hora del
          intento y quién lo desbloquea. Va aquí, junto al «Recalculado» al que se refiere.
        -->
        <div class="aviso-recalculo" v-if="avisoRecalculo">
          <f7-icon f7="clock" class="aviso-recalculo-icono"></f7-icon>
          <span>{{ avisoRecalculo }}</span>
        </div>

        <!--
          NO SE HA PODIDO RELEER EL MES DESPUÉS DE GUARDAR (#215) · va ANTES de los dos avisos de
          abajo porque es lo que los enmarca: son ellos los que hablan de las casillas, y este dice
          por qué las cifras de la tabla no son las de ahora. Aparte de `error`, que acompaña a una
          pantalla sin cifras: aquí la tabla sigue entera a propósito, porque es donde está escrito
          lo que no ha entrado.
        -->
        <div class="aviso-relectura" v-if="errorRelectura">
          <f7-icon f7="arrow_2_circlepath" class="aviso-relectura-icono"></f7-icon>
          <span>{{ errorRelectura }}</span>
        </div>

        <!--
          EL GUARDADO FALLÓ (#164) · la pantalla lo dice, con el motivo que manda el servidor, y los
          datos que no entraron se quedan escritos en sus casillas para no perderlos. Un fallo
          silencioso en una pantalla de cifras es inaceptable.
        -->
        <div class="aviso-guardado-error" v-if="erroresGuardado.length">
          <p class="aviso-guardado-titulo">
            <f7-icon f7="exclamationmark_triangle" class="aviso-revisar-icono"></f7-icon>
            {{ erroresGuardado.length === 1 ? 'Un dato no se ha guardado' : erroresGuardado.length + ' datos no se han guardado' }}
          </p>
          <ul class="aviso-guardado-lista">
            <li v-for="(fallo, i) in erroresGuardado" :key="'eg-' + i">
              <strong>{{ fallo.columna }}</strong> del {{ formatFecha(fallo.fecha) }}: {{ fallo.motivo }}
            </li>
          </ul>
          <p class="aviso-guardado-nota">
            Lo que no ha entrado sigue escrito en su casilla, en naranja. Lo demás sí se ha guardado.
          </p>
        </div>

        <!--
          OTRA PERSONA HA MOVIDO UNA CASILLA QUE ESTABAS ESCRIBIENDO · no es un fallo: lo tecleado
          sigue escrito. Pero la cifra sobre la que se decidió ya no es la que hay, y si se vuelve a
          guardar se escribe encima de la de la otra persona. Se dice, porque hacerlo en silencio es
          el fallo callado que #164 no admite.
        -->
        <div class="aviso-base-movida" v-if="basesMovidas.length">
          <p class="aviso-guardado-titulo">
            <f7-icon f7="exclamationmark_triangle" class="aviso-revisar-icono"></f7-icon>
            {{ basesMovidas.length === 1 ? 'Una casilla que estabas escribiendo ha cambiado' : basesMovidas.length + ' casillas que estabas escribiendo han cambiado' }}
          </p>
          <ul class="aviso-guardado-lista">
            <li v-for="(movida, i) in basesMovidas" :key="'bm-' + i">
              <strong>{{ movida.columna }}</strong> del {{ formatFecha(movida.fecha) }}:
              ahora hay guardado {{ movida.guardado === null ? 'un hueco' : formatEuros(movida.guardado) + ' €' }}.
            </li>
          </ul>
          <p class="aviso-guardado-nota">
            Tu cifra sigue escrita en su casilla, en azul. Si vuelves a pulsar «Guardar» se guardará
            la tuya encima; si no la quieres, recarga la pantalla.
          </p>
        </div>

        <div class="aviso-guardado-ok" v-if="avisoGuardado">{{ avisoGuardado }}</div>

        <!--
          FACTURAS PARA REVISAR (#169) · es un aviso de la pestaña entera, no de una tarjeta, y el
          texto del porqué lo manda la API. Aquí NO se recalcula el indicio ni se descuenta nada:
          estas facturas cuentan en las cifras de la pantalla igual que las demás.
        -->
        <div class="aviso-revisar" v-if="facturasARevisar.length">
          <p class="aviso-revisar-titulo">
            <f7-icon f7="exclamationmark_triangle" class="aviso-revisar-icono"></f7-icon>
            {{ facturasARevisar.length }}
            {{ facturasARevisar.length === 1 ? 'factura para revisar' : 'facturas para revisar' }}
          </p>
          <ul class="aviso-revisar-lista">
            <li v-for="f in facturasARevisar" :key="f.codigo || f.numero">
              <strong>{{ f.codigo || ('nº ' + f.numero) }}</strong>
              <span v-if="f.tipo === 'ABONO'" class="aviso-revisar-abono">abono</span>
              · emitida el {{ formatFecha(f.fechaEmision) }}
              · registrada el {{ formatFecha(f.registrada) }}
              <!--
                El importe puede no venir: la API lo deja sin dato cuando el «Total factura» de la
                factura no se puede leer. Un «0 €» ahí diría que la factura no vale nada, que es
                justo lo contrario de lo que esta lista avisa.
              -->
              · {{ f.importe == null ? 'importe que no se puede leer' : formatEuros(f.importe) + ' €' }}
            </li>
          </ul>
          <p class="aviso-revisar-nota">{{ notaFacturasARevisar }}</p>
        </div>

        <template v-if="mes && !cargando">

          <!-- ======================================================= 2 · LOS DOS RESÚMENES -->
          <div class="resumenes">
            <div class="resumen" v-for="r in resumenes" :key="'res-' + r.clave"
              :style="{ borderLeftColor: r.color }">
              <p class="resumen-rotulo">{{ r.etiqueta }}</p>
              <div class="resumen-cifras">
                <div class="resumen-celda">
                  <span class="resumen-eti">Objetivo del mes</span>
                  <!-- Un objetivo no calculable se dice, nunca sale como 0 € (#169): un cero
                       diría que el mes está cumplido al 100 %. Y «sin calcular» no es lo mismo que
                       «no calculable» (#249): el primero se arregla pulsando el botón de abajo y el
                       segundo es que falta un dato de origen, así que se distinguen aquí. -->
                  <span class="resumen-val" v-if="r.objetivo != null">{{ formatEuros(r.objetivo) }} €</span>
                  <span class="resumen-val resumen-val-nc" v-else-if="r.sinCalcular">Sin calcular</span>
                  <span class="resumen-val resumen-val-nc" v-else>No calculable</span>
                </div>
                <div class="resumen-celda">
                  <span class="resumen-eti">Conseguido</span>
                  <span class="resumen-val">{{ formatEuros(r.conseguido) }} €</span>
                </div>
                <div class="resumen-celda">
                  <span class="resumen-eti">Falta</span>
                  <span class="resumen-val" :class="claseFalta(r.falta)">{{ formatEurosConSigno(r.falta) }} €</span>
                </div>
                <div class="resumen-celda">
                  <span class="resumen-eti">Cumplimiento</span>
                  <span class="resumen-val" :class="claseCumplimiento(r.porcentaje)">
                    {{ formatPorcentaje(r.porcentaje) }}
                  </span>
                </div>
              </div>
              <span class="resumen-pista">
                <span class="resumen-barra" :style="{ width: r.anchoBarra + '%', background: colorCumplimiento(r.porcentaje) }"></span>
              </span>

              <!--
                ESTA CIFRA NO RECOGE TODO (#295) · va AQUÍ, pegado a la cifra, y no en un rótulo del
                principio de la pantalla: quien lee «Conseguido» no relaciona lo uno con lo otro
                (regla de pantalla de dirección, 05/10/2026).

                Acompaña y no bloquea, que es como lo pidió dirección: la cifra de arriba se sigue
                leyendo entera, no se sustituye por un guion y no se deshabilita nada. Y solo en el
                bloque de facturación —`r.descuadre` llega a null en el de cobro—, porque es la
                cifra de facturación la que está corta; repetirlo en el de cobro haría dudar del
                cobro real sin motivo.

                Cuando no hay descuadre no se dibuja nada: ni un «todo cuadra» ni un hueco
                reservado (punto 5 de la ficha).
              -->
              <p class="resumen-descuadre" v-if="r.descuadre">
                <f7-icon f7="exclamationmark_triangle" class="resumen-descuadre-icono"></f7-icon>
                <span>
                  <strong>{{ tituloDescuadre(r.descuadre) }}</strong>
                  {{ resumenDelDescuadre(r.descuadre) }}
                </span>
              </p>

              <!--
                EL SELLO DE ESTE OBJETIVO Y SU BOTÓN (#249, D-OBJ-BOTON) · la fecha va AQUÍ, junto a
                la cifra, y no solo al final del desglose: dirección lo pidió con esas palabras. Y es
                un botón por objetivo, independientes: el de facturación no mueve la fecha del de
                cobro.

                El botón no se dibuja cuando no lleva a ninguna parte —un mes migrado, un mes sin
                ficha—, y entonces lo que se enseña es el motivo, también aquí al lado: un botón que
                recibe un rechazo al pulsarlo es peor que no tener botón.
              -->
              <div class="resumen-objetivo">
                <span class="resumen-sello" v-if="r.calculadoEn">
                  Calculado el {{ formatInstante(r.calculadoEn) }}{{ r.calculadoPor ? ' por ' + r.calculadoPor : '' }}
                </span>
                <span class="resumen-sello resumen-sello-sin" v-else>Sin calcular</span>
                <button type="button" class="btn-recalcular" v-if="r.recalculable"
                  :disabled="!sePuedeRecalcular(r)" @click="recalcularObjetivo(r)">
                  <f7-icon f7="arrow_2_circlepath" class="btn-recalcular-icono"></f7-icon>
                  {{ r.recalculando ? 'Calculando…' : 'Recalcular' }}
                </button>
              </div>
              <!-- Por qué el botón está apagado, junto al botón y no en un aviso de arriba: quien
                   pulsa no relaciona lo uno con lo otro. -->
              <p class="resumen-espera" v-if="porQueEsperar(r)">{{ porQueEsperar(r) }}</p>
              <p class="resumen-nc" v-if="r.motivoNoRecalculable">{{ r.motivoNoRecalculable }}</p>
              <p class="resumen-nc" v-else-if="r.notaSinCalcular">{{ r.notaSinCalcular }}</p>
              <!-- El recálculo no entró: se dice con el motivo del servidor. Un botón que se queda
                   callado después de pulsarlo es el fallo silencioso que #164 no admite. -->
              <p class="resumen-fallo" v-if="r.fallo">{{ r.fallo }}</p>
              <p class="resumen-nc" v-if="r.motivoNoCalculable">{{ r.motivoNoCalculable }}</p>
            </div>
          </div>

          <!-- NOTA D-OBJ-BOTON · por qué la fecha de un objetivo puede ser de hace días, y qué la
               mueve. La manda el servidor, como la de D-COBRO-FUENTE: es una decisión sobre el dato
               y la pantalla no la cuenta con palabras propias. -->
          <div class="nota-objetivos" v-if="mes.notaObjetivosABoton">
            <f7-icon f7="info_circle" class="nota-objetivos-icono"></f7-icon>
            <span>{{ mes.notaObjetivosABoton }}</span>
          </div>

          <!-- NOTA D-COBRO-FUENTE · la misma que la Tarjeta B, y la manda el servidor -->
          <div class="nota-cobro" v-if="mes.notaCobroReal">
            <f7-icon f7="info_circle" class="nota-cobro-icono"></f7-icon>
            <span>{{ mes.notaCobroReal }}</span>
          </div>

          <!--
            MES SIN DÍA A DÍA · «vacío no: explicado» (dirección, 03/10/2026). Un usuario que abre
            agosto de 2022 y ve una tabla en blanco cree que el sistema ha perdido datos. El texto lo
            manda la API, que es quien sabe por qué no hay detalle.
          -->
          <div class="bloque" v-if="mes.sinDetalleDiario">
            <p class="bloque-titulo">Día a día</p>
            <div class="aviso-sin-detalle">
              <f7-icon f7="calendar_badge_minus" class="aviso-sin-detalle-icono"></f7-icon>
              <span>{{ mes.notaSinDetalleDiario }}</span>
            </div>
            <!--
              Y EL PORQUÉ DEL DESCUADRE TAMBIÉN AQUÍ (#295) · el aviso no es de la tabla, es de la
              cifra del mes, que sin día a día se sigue enseñando arriba. Un mes cuyas facturas
              traen todas la fecha de emisión ilegible se queda sin un solo día y con descuadre a la
              vez: colgado solo de la rama de la tabla, esa pantalla conservaba el renglón corto de
              la cifra y perdía los motivos y la nota, que son los que dicen por qué faltan
              documentos o por qué no se sabe cuánto.

              El marcado está en los dos sitios porque la colocación es distinta —ahí va pegado al
              TOTAL y aquí debajo de la explicación—, pero los textos salen de los mismos métodos,
              así que las dos mitades no pueden acabar diciendo cosas distintas.
            -->
            <div class="aviso-descuadre" v-if="descuadre">
              <p class="aviso-descuadre-titulo">
                <f7-icon f7="exclamationmark_triangle" class="aviso-revisar-icono"></f7-icon>
                {{ tituloAvisoDescuadre() }}
              </p>
              <ul class="aviso-descuadre-lista">
                <li v-for="(m, i) in descuadre.motivos" :key="'descuadre-sin-tabla-' + i">
                  {{ m.motivo }}{{ detalleDelMotivo(m) }}
                </li>
              </ul>
              <p class="aviso-descuadre-nota">{{ notaDescuadre(descuadre) }}</p>
            </div>
          </div>

          <!-- ========================================================== 3 · LA TABLA -->
          <div class="bloque" v-else>
            <p class="bloque-titulo">Día a día — {{ etiquetaMes }}</p>
            <!-- POR QUÉ LAS CASILLAS ESTÁN APAGADAS · junto a la tabla y no en un aviso del
                 principio de la pantalla, que es lo que quien va a teclear no relaciona con la
                 casilla que no le responde (dirección, 05/10/2026). Mientras se guarda no hace
                 falta: lo dice el propio botón «Guardando…», que está a la vista. -->
            <p class="casillas-espera" v-if="porQueLasCasillasEsperan">
              {{ porQueLasCasillasEsperan }}
            </p>
            <div class="tabla-scroll">
              <table class="tabla-cd">
                <thead>
                  <tr>
                    <th class="col-sticky col-dia">DÍA</th>
                    <th>OBJ. DIARIO</th>
                    <!-- «IVA y suplidos incluidos» pegado al rótulo, que es donde dirección lo pidió
                         el 05/10/2026 (D-FACT-IVA). El texto lo manda la API, que es quien sabe qué
                         columna se suma —el total de la factura, con los suplidos dentro—: escrito
                         aquí, el día que cambiara lo que se mide la pantalla seguiría afirmándolo. -->
                    <th>FACTURADO<span class="th-nota" v-if="mes.rotuloFacturado">{{ mes.rotuloFacturado }}</span></th>
                    <th>DIFERENCIA</th>
                    <th>DIF. ACUM.</th>
                    <th class="col-separador">OBJ. COBRO</th>
                    <th class="col-manual-th">POEDD</th>
                    <th class="col-manual-th">COBRO REAL</th>
                    <th class="col-manual-th">DEPÓSITO</th>
                    <th>COBRO + DEP.</th>
                    <th>DIFERENCIA</th>
                    <th class="col-manual-th">TOTAL DEPÓSITO</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="fila in mes.dias" :key="'tr-' + fila.fecha"
                    :class="{ 'fila-hoy': fila.hoy, 'fila-no-habil': !fila.habil }">
                    <td class="col-sticky col-dia">
                      <strong>{{ formatDia(fila.fecha) }}</strong>
                      <span class="dia-habil" v-if="fila.habil">{{ fila.diaHabil }}</span>
                      <!-- Un día no laborable solo tiene fila si tiene facturas imputadas: se marca
                           para que no parezca una fila sin explicar. -->
                      <span class="dia-no-habil" v-else title="No es día laborable en Valladolid">no lab.</span>
                    </td>
                    <td>{{ formatEuros(fila.objetivoDiarioFacturacion) }}</td>
                    <!--
                      FACTURADO · lo emitido ESE día, igual que la consulta de Facturación (#289). Lo
                      que se registró en él pero se emitió en un mes ya cerrado no está aquí: va en la
                      línea de abajo, que es una fila más de esta misma columna.
                    -->
                    <td>{{ formatEuros(fila.facturado) }}</td>
                    <td :class="claseDiferencia(fila.diferenciaFacturacion)">
                      {{ formatEurosConSigno(fila.diferenciaFacturacion) }}
                    </td>
                    <td :class="claseDiferencia(fila.diferenciaFacturacionAcumulada)">
                      {{ formatEurosConSigno(fila.diferenciaFacturacionAcumulada) }}
                    </td>
                    <td class="col-separador">{{ formatEuros(fila.objetivoDiarioCobro) }}</td>
                    <td class="col-manual" v-for="dato in datosDeCobro" :key="'m-' + fila.fecha + '-' + dato"
                      :class="{ 'col-manual-guardada': seHaGuardado(fila, dato), 'col-manual-tocada': estaTocada(fila, dato), 'col-manual-fallida': haFallado(fila, dato), 'col-manual-base-movida': laBaseSeHaMovido(fila, dato), 'col-manual-a-medias': estaAMedias(fila, dato) }">
                      <!-- `@input` y no `@change`: `change` no salta hasta que la casilla pierde el
                           foco, así que lo tecleado vivía solo en el DOM y el contador del botón no
                           lo contaba. Y apagadas mientras hay algo escribiendo —`ocupado`, o sea el
                           guardado Y el recálculo de un objetivo (#249)—, como las flechas, el
                           recargar y el propio «Guardar»: el lote manda una instantánea del
                           borrador, y teclear mientras vuelve el mes entero es teclear en una tabla
                           que está a punto de repintarse. Con `guardando` a secas se podía teclear
                           en lo que no se podía guardar (P2 Codex #1777, ronda 2). -->
                      <input type="number" step="0.01" class="entrada-manual"
                        :disabled="!mes.editable || ocupado"
                        :aria-label="rotulo(dato) + ' del ' + formatFecha(fila.fecha)"
                        :value="valorEnPantalla(fila, dato)" @input="editar(fila, dato, $event)" />
                    </td>
                    <td>{{ formatEuros(fila.cobroMasDeposito) }}</td>
                    <td :class="claseDiferencia(fila.diferenciaCobro)">
                      {{ formatEurosConSigno(fila.diferenciaCobro) }}
                    </td>
                    <td class="col-manual"
                      :class="{ 'col-manual-guardada': seHaGuardado(fila, datoTotalDeposito), 'col-manual-tocada': estaTocada(fila, datoTotalDeposito), 'col-manual-fallida': haFallado(fila, datoTotalDeposito), 'col-manual-base-movida': laBaseSeHaMovido(fila, datoTotalDeposito), 'col-manual-a-medias': estaAMedias(fila, datoTotalDeposito) }">
                      <input type="number" step="0.01" class="entrada-manual"
                        :disabled="!mes.editable || ocupado"
                        :aria-label="rotulo(datoTotalDeposito) + ' del ' + formatFecha(fila.fecha)"
                        :value="valorEnPantalla(fila, datoTotalDeposito)" @input="editar(fila, datoTotalDeposito, $event)" />
                    </td>
                  </tr>
                  <!--
                    LA LÍNEA DE REGISTRADO FUERA DE MES (#289, decisión de dirección del 06/10/2026)
                    · va DENTRO de la tabla, entre los días y el TOTAL, porque es una fila más de la
                    columna FACTURADO: el pie suma los días MÁS esta línea, así que tiene que verse
                    justo donde se suma. Ni en un aviso al principio de la pantalla ni en una nota al
                    pie, que es lo que haría que el TOTAL no cuadrara con lo que hay encima.
                  -->
                  <tr class="fila-fuera-de-mes" v-if="mes.registradoFueraDeMes">
                    <td class="col-sticky col-dia">
                      <strong>Registrado fuera de mes</strong>
                    </td>
                    <td></td>
                    <td><strong>{{ formatEurosConSigno(mes.registradoFueraDeMes.importe) }}</strong></td>
                    <td :colspan="COLUMNAS - 3" class="celda-fuera-de-mes">
                      {{ mes.registradoFueraDeMes.nota }}.
                      Cuentan en el mes, no en ningún día.
                      <button type="button" class="fuera-de-mes-detalle"
                        :aria-expanded="String(detalleFueraDeMesAbierto)"
                        @click="alternarDetalleFueraDeMes()">
                        {{ detalleFueraDeMesAbierto ? 'ocultar el detalle' : 'ver el detalle' }}
                      </button>
                    </td>
                  </tr>
                  <tr class="fila-fuera-de-mes-detalle"
                    v-if="mes.registradoFueraDeMes && detalleFueraDeMesAbierto">
                    <td :colspan="COLUMNAS">
                      <ul class="fuera-de-mes-lista">
                        <li v-for="d in mes.registradoFueraDeMes.registros" :key="'fdm-' + d.codigo">
                          <strong>{{ d.codigo }}</strong>
                          <span v-if="d.tipo === 'ABONO'" class="aviso-revisar-abono">abono</span>
                          · emitida el {{ formatFecha(d.fechaEmision) }}
                          · registrada el {{ formatFecha(d.registrada) }}
                          · {{ formatEurosConSigno(d.importe) }} €
                        </li>
                      </ul>
                      <p class="fuera-de-mes-nota">{{ notaFueraDeMes() }}</p>
                    </td>
                  </tr>
                </tbody>
                <!--
                  EL PIE · lo calcula el servidor sumando las mismas filas de arriba con sus
                  céntimos. No se suma aquí: sumar los importes ya redondeados a euros enteros para
                  enseñarlos es lo que produce los descuadres de uno o dos euros.
                -->
                <tfoot v-if="mes.totales">
                  <tr>
                    <!-- Y el TOTAL dice qué le falta cuando hay descuadre (#295): sin esta marca,
                         el pie se presentaría como el total cerrado del mes justo debajo de un
                         aviso que dice que falta algo, que es la contradicción que prohíbe el punto
                         6 de la #289. La cifra no se toca: se sigue leyendo.

                         Y la marca no dice siempre lo mismo, por lo mismo que el rótulo del aviso:
                         si lo que se ha quedado fuera no llevaba importe, al TOTAL en euros no le
                         falta nada y llamarlo «incompleto» le atribuiría un defecto que no tiene. -->
                    <td class="col-sticky col-dia">
                      <strong>TOTAL</strong>
                      <span class="total-incompleto" v-if="descuadre">{{ marcaDelTotal(descuadre) }}</span>
                    </td>
                    <td><strong>{{ formatEuros(mes.totales.objetivoDiarioFacturacion) }}</strong></td>
                    <!-- El TOTAL de FACTURADO suma los días MÁS la línea de registrado fuera de
                         mes: lo calcula el servidor y es la misma cifra que el «Conseguido» del
                         bloque de resumen de arriba (#289). -->
                    <td><strong>{{ formatEuros(mes.totales.facturado) }}</strong></td>
                    <td :class="claseDiferencia(mes.totales.diferenciaFacturacion)">
                      <strong>{{ formatEurosConSigno(mes.totales.diferenciaFacturacion) }}</strong>
                    </td>
                    <td :class="claseDiferencia(mes.totales.diferenciaFacturacionAcumulada)">
                      <strong>{{ formatEurosConSigno(mes.totales.diferenciaFacturacionAcumulada) }}</strong>
                    </td>
                    <td class="col-separador"><strong>{{ formatEuros(mes.totales.objetivoDiarioCobro) }}</strong></td>
                    <td class="col-manual-th"><strong>{{ formatEuros(mes.totales.poedd) }}</strong></td>
                    <td class="col-manual-th"><strong>{{ formatEuros(mes.totales.cobroReal) }}</strong></td>
                    <td class="col-manual-th"><strong>{{ formatEuros(mes.totales.deposito) }}</strong></td>
                    <td><strong>{{ formatEuros(mes.totales.cobroMasDeposito) }}</strong></td>
                    <td :class="claseDiferencia(mes.totales.diferenciaCobro)">
                      <strong>{{ formatEurosConSigno(mes.totales.diferenciaCobro) }}</strong>
                    </td>
                    <td class="col-manual-th"><strong>{{ formatEuros(mes.totales.totalDeposito) }}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <!--
              AL PIE DE LA TABLA, EL PORQUÉ DEL DESCUADRE (#295) · un motivo por línea, pegado al
              TOTAL que está corto. Es la otra mitad del aviso: la de arriba acompaña a la cifra y
              esta dice por qué, que es lo que permite ir a buscarlo.

              La frase de cada motivo la manda la API —es la que tiene el criterio de qué entra en
              el reparto por días y qué cuenta en el mes—, y los importes los da formato la
              pantalla, igual que en la línea de registrado fuera de mes.
            -->
            <div class="aviso-descuadre" v-if="descuadre">
              <p class="aviso-descuadre-titulo">
                <f7-icon f7="exclamationmark_triangle" class="aviso-revisar-icono"></f7-icon>
                {{ tituloAvisoDescuadre() }}
              </p>
              <ul class="aviso-descuadre-lista">
                <li v-for="(m, i) in descuadre.motivos" :key="'descuadre-' + i">
                  {{ m.motivo }}{{ detalleDelMotivo(m) }}
                </li>
              </ul>
              <p class="aviso-descuadre-nota">{{ notaDescuadre(descuadre) }}</p>
            </div>
            <!-- LA LEYENDA de D-MANUAL · la manda el servidor, que es quien tiene la lista de las
                 cuatro columnas manuales. -->
            <div class="leyenda">
              <span class="leyenda-muestra leyenda-muestra-manual"></span>
              <span>{{ mes.leyendaDatosManuales }}</span>
            </div>
            <p class="bloque-pie">
              <!-- La frase entera de D-FACT-IVA, en el pie de SU tabla y la primera del pie: es lo
                   que hay que saber para leer la columna, no una aclaración de las de después. La
                   escribe el servidor igual que el rótulo de la cabecera, así que las dos no pueden
                   decir cosas distintas. -->
              <span v-if="mes.notaFacturado">{{ mes.notaFacturado }}</span>
              Las diferencias van en <span class="var-positivo">verde</span> por encima del objetivo y
              en <span class="var-negativo">rojo</span> por debajo. Los importes se enseñan en euros
              enteros; las sumas se hacen con los céntimos guardados, así que un total puede no ser la
              suma exacta de los redondeos que se ven.
              <!-- La tabla trae todos los días hábiles del mes, también los que no han llegado, así
                   que en el mes en curso el objetivo del pie es el del mes entero y las diferencias
                   solo cubren lo transcurrido. Se dice aquí para que nadie lo lea como un descuadre:
                   cada celda del pie es la suma de su propia columna. -->
              <span v-if="mes.enCurso">
                La tabla trae todos los días hábiles del mes, también los que aún no han llegado: por
                eso el objetivo del pie es el del mes completo, mientras que las dos diferencias solo
                suman los días transcurridos.
              </span>
              <!-- Solo cuando hay línea: explicar algo que no está en pantalla es ruido (#289). -->
              <span v-if="mes.registradoFueraDeMes">
                Cada día cuenta <strong>lo que se emitió ese día</strong>, igual que la consulta de
                FACTURACIÓN. Los documentos de un mes anterior ya cerrado que se registraron en este
                mes no caen en ningún día: van en la línea «Registrado fuera de mes», que
                <strong>sí</strong> cuenta en el TOTAL.
              </span>
            </p>
          </div>

          <!-- ==================================== 4 · AL PIE, EL DESGLOSE DEL OBJETIVO -->
          <!-- D-VISIBILIDAD · dirección pidió expresamente que esto esté ABAJO, no arriba. -->
          <div class="bloque">
            <p class="bloque-titulo">Cómo se ha calculado cada objetivo</p>
            <div class="desgloses">
              <div class="desglose" v-for="d in desgloses" :key="'des-' + d.etiqueta">
                <p class="desglose-titulo">{{ d.etiqueta }}</p>
                <template v-for="(l, i) in d.lineas">
                  <div class="desglose-linea" :key="'dl-' + i">
                    <div class="desglose-concepto">
                      <span class="desglose-eti">{{ l.etiqueta }}</span>
                      <!-- La «línea pequeña debajo que dice de dónde sale el dato», que es la mitad de
                           D-VISIBILIDAD: sin ella el desglose dice cuánto suma cada línea pero no por qué. -->
                      <span class="desglose-origen">{{ l.origen }}</span>
                    </div>
                    <span class="desglose-base">{{ formatEuros(l.datoBase) }}</span>
                    <span class="desglose-factor">{{ l.factorTexto || '—' }}</span>
                    <span class="desglose-importe">{{ formatEuros(l.importe) }}</span>
                  </div>
                  <!-- Qué ha dejado fuera de ESTE dato la regla de exclusión, y por qué (#257).
                       Va pegado a su línea y no en un aviso al principio del desglose: lo que ha
                       encogido es este sumando, no el objetivo entero, y un aviso arriba obliga a
                       quien lo lee a adivinar a cuál de las tres líneas se refiere.
                       Dirección lo pidió el 06/10/2026: una base que encoge sin explicar por qué
                       es el mismo problema que esto viene a arreglar. -->
                  <div class="desglose-excluidas"
                       v-if="l.exclusiones && l.exclusiones.length"
                       :key="'dx-' + i">
                    <p class="desglose-excluidas-tit">{{ rotuloExcluidas(l) }}</p>
                    <p class="desglose-excluida" v-for="x in l.exclusiones"
                       :key="'dx-' + i + '-' + x.clave">{{ textoExclusion(x) }}</p>
                  </div>
                </template>
                <p class="desglose-vacio" v-if="!d.lineas.length">
                  {{ d.motivoNoCalculable || 'Este mes no tiene objetivos calculados.' }}
                </p>
                <div class="desglose-total" v-if="d.lineas.length">
                  <span>Objetivo del mes</span>
                  <strong v-if="d.total != null">{{ formatEuros(d.total) }} €</strong>
                  <strong class="resumen-val-nc" v-else>No calculable</strong>
                </div>
                <div class="desglose-total desglose-diario" v-if="d.lineas.length">
                  <span>÷ {{ d.diasLaborables != null ? d.diasLaborables : '—' }} días laborables en Valladolid</span>
                  <strong v-if="d.objetivoDiario != null">{{ formatEuros(d.objetivoDiario) }} €</strong>
                  <strong class="resumen-val-nc" v-else>—</strong>
                </div>
                <p class="desglose-nc" v-if="d.lineas.length && d.motivoNoCalculable">
                  {{ d.motivoNoCalculable }}
                </p>
                <p class="desglose-sello" v-if="d.calculadoEn">
                  Calculado el {{ formatInstante(d.calculadoEn) }}{{ d.calculadoPor ? ' por ' + d.calculadoPor : '' }}
                </p>
              </div>
            </div>
            <p class="bloque-pie">
              Las líneas son las que se guardaron el día que se calculó este mes: un mes cerrado
              enseña su desglose tal como se calculó entonces, no con los datos de hoy.
              <!-- QUÉ ES LA FECHA DE CORTE de las dos líneas de pendiente (D-CORTE) · aquí, que es
                   donde esa fecha se ve, y no en un aviso del principio: dirección preguntó por el
                   «2026-08-31» leyendo precisamente estas líneas. La escribe el servidor con la
                   fecha ya calculada, y solo cuando este mes tiene alguna línea que la lleve. -->
              <span v-if="mes.notaCorte">{{ mes.notaCorte }}</span>
            </p>
          </div>

        </template>
      </template>

    </f7-page-content>

    <!--
      ENVIAR REPORTE (#547) · el diálogo de envío: a quién, con qué asunto, y qué se manda.

      Los destinatarios NO van fijos y no se resuelven aquí: se eligen por grupo, por persona o
      escribiendo el correo a mano, y la API los resuelve en el momento del envío. Lo que se manda es
      el código del grupo y el `username` de la persona, no su buzón: así un alta, una baja o un
      correo corregido después de abrir este diálogo ya cuentan.
    -->
    <f7-popup :opened="envio.abierto" @popup:closed="cerrarEnvio"
      style="--f7-popup-tablet-width:620px">
      <f7-page>
        <f7-navbar title="Enviar el informe por correo">
          <f7-nav-right><f7-link popup-close icon-f7="xmark"></f7-link></f7-nav-right>
        </f7-navbar>
        <f7-page-content>
          <!-- De qué mes es el correo, lo primero: desde aquí no se ve la pantalla de detrás, y lo
               que se manda es EL MES QUE ESTÁ ABIERTO. Sin decirlo, quien lo envíe no tiene forma de
               comprobar que no se equivocó de mes al pulsar.

               Lo que NO dice es «tal como está en la pantalla»: eso no se puede prometer, porque el
               informe lo compone el servidor al mandarlo con lo que esté guardado entonces. El
               porqué y lo que sí se dice, en `avisoCifrasAlEnviar` (P2 Codex ronda 5). -->
          <f7-block strong class="envio-mes">
            Se manda el control diario de <strong>{{ etiquetaMes }}</strong>, con el informe completo
            en el cuerpo del correo. Mandarlo no cambia ninguna cifra ni recalcula ningún objetivo:
            los objetivos van con la fecha y la hora en que se calcularon.
            <span class="envio-mes-vigencia">{{ avisoCifrasAlEnviar }}</span>
          </f7-block>

          <f7-block-title>Destinatarios</f7-block-title>
          <f7-block strong>
            <f7-block v-if="envio.cargandoDestinatarios" class="text-align-center">
              <f7-preloader></f7-preloader>
            </f7-block>
            <div class="aviso-error" v-if="envio.errorDestinatarios">
              {{ envio.errorDestinatarios }}
              <!-- Con la lista caída se puede seguir enviando a correos escritos a mano: lo que
                   falta son los grupos, no el envío. Decirlo es lo que evita cerrar el diálogo
                   pensando que no se puede mandar nada. -->
              <div class="envio-ayuda">Puedes mandarlo escribiendo los correos a mano, abajo.</div>
            </div>
            <!-- Lo que se ha quitado de la elección, dicho donde se elige: un grupo que estaba
                 marcado y ya no está en la lista tiene que decirlo aquí, no dejar el botón apagado
                 sin motivo ni —peor— seguir encendido mandando a quien no se ve. -->
            <p class="envio-ayuda envio-ayuda-aviso" v-if="envio.descartados">
              {{ envio.descartados }}
            </p>

            <!-- `gruposVisibles` y no `envio.grupos`: mientras la lista se relee, la de la apertura
                 anterior NO se dibuja. Lo que se ve y lo que se puede mandar son lo mismo porque
                 salen de la misma propiedad, no porque dos sitios se mantengan de acuerdo. -->
            <!-- Mientras el correo sale, a quién va NO se puede cambiar, y se dice aquí, junto a
                 los chips (§9): el cuerpo y los destinatarios ya viajaron, así que un chip que se
                 apagara enseñaría una elección que no es la del correo que se está entregando. Lo
                 mismo que ya hacen los tres campos de texto de abajo con su `:disabled`. -->
            <p class="envio-ayuda envio-ayuda-aviso" v-if="eleccionRetenida">
              Mientras el correo sale no se puede cambiar a quién va: ya ha salido con los
              destinatarios que tenías elegidos. Se desbloquea solo al terminar.
            </p>

            <template v-if="gruposVisibles.length">
              <p class="envio-rotulo">Por grupo</p>
              <div class="envio-chips">
                <span v-for="grupo in gruposVisibles" :key="'g-' + grupo.codigo"
                  class="envio-chip" :class="{ 'envio-chip-on': grupoElegido(grupo.codigo),
                    'envio-chip-retenido': eleccionRetenida }"
                  @click="alternarGrupo(grupo.codigo)">
                  {{ grupo.etiqueta }}
                  <span class="envio-chip-cuantos">{{ cuantosEnElGrupo(grupo) }}</span>
                </span>
              </div>
              <!-- Un grupo sin nadie se puede elegir igual y no manda nada: decirlo aquí evita que
                   se cuente como enviado a alguien. -->
              <p class="envio-ayuda" v-if="envio.notaGrupos">{{ envio.notaGrupos }}</p>

              <p class="envio-rotulo">O por persona</p>
              <div class="envio-chips">
                <span v-for="persona in personasElegibles" :key="'p-' + persona.username"
                  class="envio-chip" :class="{ 'envio-chip-on': personaElegida(persona.username),
                    'envio-chip-retenido': eleccionRetenida }"
                  :title="persona.email" @click="alternarPersona(persona.username)">
                  {{ persona.nombre || persona.username }}
                </span>
              </div>
              <p class="envio-ayuda" v-if="!personasElegibles.length">
                Ninguno de los grupos tiene personas con correo en su ficha.
              </p>
            </template>

            <!-- Un correo escrito aquí es elegir destinatario igual que marcar un grupo, y cuenta
                 como tal: ver `eleccionVacia`, que es lo que decide si el diálogo está como nuevo.
                 -->
            <p class="envio-rotulo">Y correos sueltos</p>
            <input v-model="envio.emails" type="text" class="envio-campo"
              :disabled="eleccionRetenida" placeholder="correo@empresa.com, otro@empresa.com">
            <p class="envio-ayuda">Separados por comas. Para quien no sea empleado o no esté en
              ningún grupo.</p>

            <p class="envio-rotulo">En copia (opcional)</p>
            <input v-model="envio.emailsCc" type="text" class="envio-campo"
              :disabled="eleccionRetenida" placeholder="copia@empresa.com">
          </f7-block>

          <f7-block-title>Asunto</f7-block-title>
          <f7-block strong>
            <input v-model="envio.asunto" type="text" class="envio-campo"
              :disabled="eleccionRetenida"
              :placeholder="'Control diario facturación — SIMECAL — ' + etiquetaMes">
            <!-- En blanco lo pone la API, que es quien sabe cómo se llama este informe y quién le
                 añade la marca de pruebas. Escribiéndolo aquí serían dos asuntos distintos el día
                 que uno de los dos cambie. -->
            <p class="envio-ayuda">Si lo dejas en blanco va el asunto de siempre con el mes.</p>
          </f7-block>

          <f7-block v-if="envio.error" class="aviso-error">{{ envio.error }}</f7-block>

          <f7-block>
            <!-- El botón y el método miran LA MISMA condición (`motivoNoSePuedeEnviarYa`), que es lo
                 que impide que quede un camino por el que se mande con el botón diciendo que no se
                 puede: pasó con la lista a medio releer (P1 Codex ronda 3). -->
            <f7-button fill color="green" :disabled="!!motivoNoSePuedeEnviarYa || enviandoReporte"
              @click="enviarReporte">
              {{ enviandoReporte ? 'Enviando…' : 'Enviar' }}
            </f7-button>
            <!-- §9 · un control que no se puede usar dice por qué y qué lo desbloquea, y lo dice
                 JUNTO al control: quien pulsa no relaciona un aviso del principio de la pantalla con
                 el botón de abajo. Y el texto es el mismo que apaga el botón, así que no pueden
                 decir cosas distintas. -->
            <p class="envio-ayuda envio-ayuda-aviso" v-if="motivoNoSePuedeEnviarYa">
              {{ motivoNoSePuedeEnviarYa }}
            </p>
          </f7-block>
        </f7-page-content>
      </f7-page>
    </f7-popup>

    <!--
      PROGRAMAR REPORTE (#549) · los envíos periódicos de ESTE informe.

      Lo que se programa es el mismo informe que manda «Enviar reporte», por la misma consulta: si
      el informe cambia, cambia aquí sin tocar nada. Lo que decide esta pantalla es cada cuánto sale
      y a quién, y nada más. **De qué mes es el correo no se elige**: el envío programado manda
      siempre el mes en curso, y eso se dice arriba del todo porque es lo único en lo que no se
      parece al botón de enviar a mano.
    -->
    <f7-popup :opened="prog.abierto" @popup:closed="cerrarProgramaciones"
      style="--f7-popup-tablet-width:680px">
      <f7-page>
        <f7-navbar title="Programar el envío del informe">
          <f7-nav-right><f7-link popup-close icon-f7="xmark"></f7-link></f7-nav-right>
        </f7-navbar>
        <f7-page-content>
          <f7-block strong class="envio-mes">
            Cada envío programado manda el control diario <strong>del mes en curso</strong>, con el
            informe completo en el cuerpo del correo. No cambia ninguna cifra ni recalcula ningún
            objetivo: los objetivos van con la fecha y la hora en que se calcularon, y uno sin
            calcular sale «sin calcular».
            <span class="envio-mes-vigencia">El botón «Enviar reporte» es otra cosa: ese manda el
              mes que tengas abierto, en el momento.</span>
          </f7-block>

          <f7-block v-if="prog.cargando" class="text-align-center">
            <f7-preloader></f7-preloader>
          </f7-block>
          <!-- §9 · con la lista sin leer, el aviso lleva lo que la desbloquea al lado: el botón de
               recargar. Y mientras tanto «Guardar» está apagado y dice por qué
               (`motivoNoSePuedeDecidirUnAlta`): no se puede dar de alta una regla sin poder ver si
               ya hay otra igual. -->
          <f7-block v-if="prog.error" class="aviso-error">
            {{ prog.error }}
            <div class="prog-botonera">
              <f7-button outline :disabled="prog.cargando" @click="cargarProgramaciones">
                Recargar
              </f7-button>
            </div>
          </f7-block>
          <f7-block v-if="prog.aviso" class="aviso-guardado-ok">{{ prog.aviso }}</f7-block>

          <template v-if="!prog.cargando">
            <f7-block-title>Envíos programados</f7-block-title>
            <!-- «No hay ninguno» solo cuando se ha podido mirar: con la lectura caída, lo que hay
                 que decir es que no se sabe, y eso lo dice el aviso de arriba. -->
            <f7-block strong v-if="!prog.lista.length && !prog.error">
              <p class="envio-ayuda">Todavía no hay ninguno. El formulario de abajo da de alta el
                primero.</p>
            </f7-block>
            <div v-for="regla in prog.lista" :key="regla.id" class="prog-regla"
              :class="{ 'prog-regla-pausada': !regla.activa }">
              <div class="prog-regla-cabecera">
                <span class="prog-punto" :class="regla.activa ? 'prog-punto-activa' : ''"></span>
                <strong>{{ regla.nombre || 'Sin nombre' }}</strong>
                <span class="prog-chip-neutro">{{ textoFrecuencia(regla) }}</span>
              </div>
              <p class="prog-detalle">{{ textoDestinatarios(regla) }}</p>
              <p class="prog-detalle" v-if="regla.ultimoEnvio">
                Último envío: {{ formatInstante(regla.ultimoEnvio) }}
                ({{ regla.ultimoEstado || '—' }})
              </p>
              <!-- Los tres botones preguntan a `bloqueoDeRegla` y a nada más. Un solo sitio a
                   propósito: cada vez que el cerrojo se reparte por botón, el siguiente se queda
                   fuera. El `:disabled` y el método del botón miran el mismo. -->
              <div class="prog-botones">
                <!-- «Editar» tiene un cerrojo más que los otros dos: con un alta de resultado
                     incierto pendiente, traerse otra regla al formulario soltaría una clave de la
                     que no se sabe cómo acabó. Un borrador normal NO lo bloquea: pregunta antes de
                     descartarlo (ver `editarRegla`). -->
                <button type="button" class="btn-pequeno"
                  :disabled="!!bloqueoDeRegla(regla) || !!motivoNoSePuedeCambiarDeRegla"
                  @click="editarRegla(regla)">Editar</button>
                <button type="button" class="btn-pequeno" :disabled="!!bloqueoDeRegla(regla)"
                  @click="alternarRegla(regla)">
                  {{ regla.activa ? 'Pausar' : 'Activar' }}
                </button>
                <button type="button" class="btn-pequeno btn-pequeno-rojo"
                  :disabled="!!bloqueoDeRegla(regla)" @click="borrarRegla(regla)">
                  Eliminar
                </button>
              </div>
              <!-- §9 · un control que no se puede usar dice por qué, quién lo desbloquea y dónde, y
                   lo dice junto al control. El motivo es el del propio cerrojo, así que no hay dos
                   textos que puedan contar cosas distintas. -->
              <p class="envio-ayuda envio-ayuda-aviso" v-if="bloqueoDeRegla(regla)">
                {{ bloqueoDeRegla(regla) }}
              </p>
              <!-- §9 · y el del alta incierta, que solo apaga «Editar», también junto al control y
                   con el mismo texto que lo apaga. -->
              <p class="envio-ayuda envio-ayuda-aviso"
                v-else-if="motivoNoSePuedeCambiarDeRegla">
                {{ motivoNoSePuedeCambiarDeRegla }}
              </p>
            </div>

            <f7-block-title>
              {{ prog.form.id ? 'Editar el envío programado' : 'Nuevo envío programado' }}
            </f7-block-title>
            <!-- El formulario entero se bloquea mientras se guarda, no solo sus botones: el cuerpo
                 de la petición se copia al pulsar «Guardar», así que lo que se escriba después no
                 se manda y al volver la respuesta el formulario se limpia y se lo lleva sin decir
                 nada. -->
            <f7-block strong :class="{ 'prog-form-bloqueado': progFormRetenido }">
              <p class="envio-rotulo">Nombre</p>
              <input v-model="prog.form.nombre" type="text" class="envio-campo"
                :disabled="progFormRetenido" placeholder="Control diario a Dirección">

              <p class="envio-rotulo">Cada cuánto</p>
              <div class="prog-opciones">
                <button type="button" v-for="f in FRECUENCIAS" :key="f" class="prog-opcion"
                  :class="{ 'prog-opcion-elegida': prog.form.frecuencia === f }"
                  :disabled="progFormRetenido"
                  @click="prog.form.frecuencia = f">{{ f }}</button>
              </div>

              <div class="prog-fila">
                <label class="prog-campo-inline">
                  <span class="envio-rotulo">Hora</span>
                  <!-- `step` de cinco minutos: el envío automático solo pasa cada cinco, así que
                       las flechas del campo ofrecen justo las horas que se respetan. Escribir otra
                       a mano sigue siendo posible, y eso lo dice `avisoHora`. -->
                  <input v-model="prog.form.hora" type="time" step="300"
                    class="envio-campo prog-campo-corto" :disabled="progFormRetenido">
                </label>
                <label class="prog-campo-inline" v-if="prog.form.frecuencia === 'SEMANAL'">
                  <span class="envio-rotulo">Día de la semana</span>
                  <select v-model="prog.form.diaSemana" class="envio-campo prog-campo-corto"
                    :disabled="progFormRetenido">
                    <option v-for="d in DIAS_SEMANA" :key="d" :value="d">{{ d }}</option>
                  </select>
                </label>
                <label class="prog-campo-inline" v-if="prog.form.frecuencia === 'MENSUAL'">
                  <span class="envio-rotulo">Día del mes</span>
                  <!-- `step="1"` para que las flechas vayan de uno en uno. No es la garantía: el
                       botón llama al método, no manda el formulario, así que la validez nativa no
                       se aplica y quien escriba «1,5» a mano lo para `avisoDiaDelMes`. -->
                  <input v-model.number="prog.form.diaMes" type="number" min="1" step="1"
                    :max="DIA_MES_MAXIMO" class="envio-campo prog-campo-corto"
                    :disabled="progFormRetenido">
                </label>
              </div>
              <!-- §9 · pegado a la fila de «Hora», que es el campo que lo arregla. -->
              <p class="envio-ayuda envio-ayuda-aviso" v-if="avisoHora">{{ avisoHora }}</p>
              <!-- §9 · y lo mismo con el día del mes, junto a su campo y no solo bajo «Guardar». -->
              <p class="envio-ayuda envio-ayuda-aviso" v-if="avisoDiaDelMes">
                {{ avisoDiaDelMes }}
              </p>

              <label class="prog-check">
                <input type="checkbox" v-model="prog.form.soloLaborables"
                  :disabled="progFormRetenido">
                Solo días laborables
              </label>
              <!-- §9 · el motivo junto al control que lo desbloquea, no solo junto a «Guardar»:
                   quien elige el sábado está mirando aquí. -->
              <p class="envio-ayuda envio-ayuda-aviso" v-if="avisoFinDeSemana">
                {{ avisoFinDeSemana }}
              </p>

              <p class="envio-rotulo">Destinatarios</p>
              <!-- Los MISMOS grupos, las mismas personas y el mismo campo de correos que el diálogo
                   de «Enviar reporte», leídos del mismo sitio (`/mes/destinatarios`): no hay una
                   lista nueva que mantener. Lo que se guarda es el código del grupo y el
                   `username`, nunca el buzón, así que un alta o una baja cuentan en el envío
                   siguiente sin tocar la programación. -->
              <f7-block v-if="prog.cargandoDestinatarios" class="text-align-center">
                <f7-preloader></f7-preloader>
              </f7-block>
              <!-- §9 · con la lista sin leer, el aviso lleva al lado lo que lo desbloquea —el
                   botón de recargar— y no manda a nadie a buscarlo arriba. Y lo que la regla tenga
                   elegido se conserva y se dice aquí mismo: los chips no se pueden pintar sin la
                   lista, así que sin esta línea el apartado afirmaría que no hay destinatarios. -->
              <div class="aviso-error" v-if="prog.errorDestinatarios">
                {{ prog.errorDestinatarios }}
                <div class="envio-ayuda">Puedes programarlo a correos escritos a mano, abajo.</div>
                <div class="prog-botonera">
                  <f7-button outline :disabled="prog.cargandoDestinatarios"
                    @click="cargarDestinatariosProgramacion">
                    Recargar
                  </f7-button>
                </div>
              </div>
              <p class="envio-ayuda envio-ayuda-aviso" v-if="progElegidosQueNoSeDibujan">
                {{ progElegidosQueNoSeDibujan }}
              </p>
              <!-- Lo que se ha quitado de la elección, dicho donde se elige: un grupo que estaba
                   guardado en la regla y ya no está en la lista tiene que decirlo aquí, no dejar
                   «Guardar» apagado sin motivo ni —peor— guardar a quien no se ve. -->
              <p class="envio-ayuda envio-ayuda-aviso" v-if="prog.descartados">
                {{ prog.descartados }}
              </p>
              <template v-if="progGruposVisibles.length">
                <p class="envio-rotulo">Por grupo</p>
                <div class="envio-chips">
                  <span v-for="grupo in progGruposVisibles" :key="'pg-' + grupo.codigo"
                    class="envio-chip"
                    :class="{ 'envio-chip-on': progGrupoElegido(grupo.codigo),
                      'envio-chip-retenido': progFormRetenido }"
                    @click="alternarGrupoProg(grupo.codigo)">
                    {{ grupo.etiqueta }}
                    <span class="envio-chip-cuantos">{{ cuantosEnElGrupo(grupo) }}</span>
                  </span>
                </div>
                <p class="envio-ayuda" v-if="prog.notaGrupos">{{ prog.notaGrupos }}</p>

                <p class="envio-rotulo">O por persona</p>
                <div class="envio-chips">
                  <span v-for="persona in progPersonasElegibles" :key="'pp-' + persona.username"
                    class="envio-chip"
                    :class="{ 'envio-chip-on': progPersonaElegida(persona.username),
                      'envio-chip-retenido': progFormRetenido }"
                    :title="persona.email" @click="alternarPersonaProg(persona.username)">
                    {{ persona.nombre || persona.username }}
                  </span>
                </div>
                <p class="envio-ayuda" v-if="!progPersonasElegibles.length">
                  Ninguno de los grupos tiene personas con correo en su ficha.
                </p>
              </template>

              <p class="envio-rotulo">Y correos sueltos</p>
              <input v-model="prog.form.emails" type="text" class="envio-campo"
                :disabled="progFormRetenido" placeholder="correo@empresa.com, otro@empresa.com">
              <p class="envio-ayuda">Separados por comas. Para quien no sea empleado o no esté en
                ningún grupo.</p>

              <label class="prog-check">
                <input type="checkbox" v-model="prog.form.activa" :disabled="progFormRetenido">
                Activado
              </label>
              <p class="envio-ayuda">
                Pausado se guarda igual y no se manda nada hasta que se active.
              </p>
            </f7-block>

            <f7-block>
              <!-- UN ALTA DE LA QUE NO SE SABE SI QUEDÓ CREADA tiene sus propios dos botones, y
                   mientras está puesto el formulario de arriba está retenido tal como se mandó: su
                   clave ya puede estar gastada en el servidor, así que lo único que se puede hacer
                   con ella es repetir la misma petición —que el servidor no duplica— o descartarla
                   a sabiendas. §9 · el aviso y las dos salidas van JUNTOS y junto al control, no en
                   un aviso al principio del diálogo. -->
              <template v-if="prog.altaIncierta">
                <div class="aviso-envio-incierto">{{ prog.altaIncierta }}</div>
                <div class="prog-botonera">
                  <!-- «Reintentar» pasa por la MISMA puerta que acabará mirando el método que
                       manda: si ahí no se puede mandar, aquí no se puede pulsar. Con el botón
                       encendido y la puerta cerrada —las dos listas se leen en paralelo al abrir el
                       diálogo— la pulsación soltaba la retención y no mandaba nada, y el alta se
                       quedaba sin aviso y sin estos dos botones. -->
                  <f7-button fill color="green" style="flex:1"
                    :disabled="prog.guardando || !!motivoNoSePuedeReintentarElAlta"
                    @click="reintentarAltaIncierta">
                    {{ prog.guardando ? 'Reintentando…' : 'Reintentar' }}
                  </f7-button>
                  <!-- Y «Descartar este alta» NO se apaga nunca por la puerta: es la salida que
                       siempre tiene que estar, porque lo que la cierra puede no arreglarse. -->
                  <f7-button outline style="flex:1" :disabled="prog.guardando"
                    @click="descartarAltaIncierta">
                    Descartar este alta
                  </f7-button>
                </div>
                <!-- §9 · por qué no se puede reintentar, quién lo desbloquea y dónde, junto al
                     control. Y es el MISMO texto que apaga el botón. -->
                <p class="envio-ayuda envio-ayuda-aviso"
                  v-if="motivoNoSePuedeReintentarElAlta">
                  {{ motivoNoSePuedeReintentarElAlta }}
                </p>
                <!-- Su clave no está guardada en este navegador, así que una recarga se la llevaría
                     y con ella la única manera de preguntar cómo acabó. No se puede arreglar desde
                     aquí —la petición ya salió—, pero sí se puede decir antes de que alguien
                     recargue. Mismo aviso que en el informe de abonos. -->
                <p class="envio-ayuda envio-ayuda-aviso" v-if="prog.altaSinApunte">
                  Y resuélvelo <strong>sin recargar la pantalla</strong>: el identificador de esta
                  operación no se ha podido guardar en este navegador —el almacenamiento está lleno
                  o bloqueado, y en modo privado no se puede—, así que si recargas se pierde y ya no
                  se podrá preguntar cómo acabó.
                </p>
              </template>
              <template v-else>
                <div class="prog-botonera">
                  <f7-button fill color="green" style="flex:1"
                    :disabled="!!motivoNoSePuedeGuardarLaProgramacion || prog.guardando"
                    @click="guardarRegla">
                    {{ prog.guardando ? 'Guardando…' : 'Guardar' }}
                  </f7-button>
                  <f7-button outline style="flex:1" :disabled="prog.guardando"
                    @click="limpiarFormProgramacion">
                    {{ prog.form.id ? 'Cancelar la edición' : 'Limpiar' }}
                  </f7-button>
                </div>
                <!-- §9 · por qué está apagado, quién lo desbloquea y dónde, junto al control. Y es
                     el MISMO texto que apaga el botón, así que no pueden decir cosas distintas. -->
                <p class="envio-ayuda envio-ayuda-aviso"
                  v-if="motivoNoSePuedeGuardarLaProgramacion">
                  {{ motivoNoSePuedeGuardarLaProgramacion }}
                </p>
              </template>
              <!-- Con un alta incierta el motivo ya está arriba, en su propio aviso: repetirlo aquí
                   serían dos textos de lo mismo en la misma pantalla. -->
              <f7-block v-if="prog.errorForm && !prog.altaIncierta" class="aviso-error">
                {{ prog.errorForm }}
              </f7-block>
            </f7-block>

            <!-- El historial. Está aquí y no en otra pantalla porque contesta la única pregunta que
                 deja una programación: ¿salió? Cuando un envío falla —incluido el mes sin ficha,
                 que no manda correo— este apunte es lo único que lo cuenta. -->
            <f7-block-title>Últimos envíos</f7-block-title>
            <f7-block v-if="prog.envios.error" class="aviso-error">{{ prog.envios.error }}</f7-block>
            <f7-block strong v-else-if="!prog.envios.lista.length">
              <p class="envio-ayuda">Todavía no ha salido ninguno con una programación.</p>
            </f7-block>
            <table class="prog-tabla" v-else>
              <thead>
                <tr><th>Cuándo</th><th>Cómo acabó</th><th>Buzones</th></tr>
              </thead>
              <tbody>
                <tr v-for="envio in prog.envios.lista" :key="envio.id">
                  <td>
                    {{ formatInstante(envio.fechaEnvio) }}
                    <!-- De qué programación salió: con varias reglas, la fecha sola no lo dice. -->
                    <p class="envio-ayuda">{{ envio.asunto || 'Sin nombre' }}</p>
                  </td>
                  <td>
                    {{ textoEstadoEnvio(envio) }}
                    <!-- D-COB-8 · el motivo del fallo se enseña: un «ERROR» sin motivo obliga a
                         preguntar por dentro, y es justo lo que este historial venía a evitar. -->
                    <p class="envio-ayuda envio-ayuda-aviso" v-if="envio.errorDetalle">
                      {{ envio.errorDetalle }}
                    </p>
                  </td>
                  <td>{{ textoBuzonesEnvio(envio) }}</td>
                </tr>
              </tbody>
            </table>
            <f7-block>
              <!-- D-COB-8 · se dice qué NO está en esta lista. Si no, un envío hecho con «Enviar
                   reporte» que no aparece aquí se leería como un envío que no salió. -->
              <p class="envio-ayuda">
                Aquí salen los envíos de las programaciones, a su hora. Lo que se manda con «Enviar
                reporte» no se registra: ese botón manda el informe en el momento y a quien se
                escriba, y no es un envío programado. Así que esta lista <strong>no sirve para saber
                si un «Enviar reporte» salió</strong>: eso lo dice el aviso de la propia pantalla.
              </p>
            </f7-block>
          </template>
        </f7-page-content>
      </f7-page>
    </f7-popup>
  </f7-page>
</template>
<style scoped>
.sello-datos-bar { padding: 6px 16px 0; }

/* ---------------------------------------------------------------------- 1 · CABECERA */
.cabecera {
  display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between;
  padding: 10px 16px 0;
}
.selector { display: flex; align-items: center; }
.selector-mes {
  font-size: 16px; font-weight: 700; color: #1a2744;
  min-width: 160px; text-align: center;
}
.flecha {
  border: 1px solid #c8d3e5; background: white; color: #1a2744;
  border-radius: 8px; width: 32px; height: 32px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.flecha:disabled { opacity: .35; cursor: default; }
/* El recargar de la barra, apagado mientras se guarda: misma pista visual que las flechas. */
.enlace-apagado { opacity: .35; pointer-events: none; }
.marca-estado {
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px;
  border-radius: 10px; padding: 3px 9px; margin-left: 12px;
}
.marca-en-curso { background: #E6F1FB; color: #185FA5; }
.marca-cerrado { background: #eef0f4; color: #65718a; }

.cabecera-derecha { display: flex; align-items: center; flex-wrap: wrap; }
.recalculado { font-size: 11px; color: #7c8db0; margin-right: 12px; }
/* LA FILA DE LOS TRES BOTONES (dirección, 18/10/2026) · `nowrap` es el acuerdo: los tres van juntos
   en una línea y «Guardar» el último. Si la ventana es estrecha lo que cede es la fila entera, que
   baja debajo de «Recalculado» —`cabecera-derecha` sigue envolviendo—, y nunca un botón suelto:
   repartidos de uno en uno es como se apilaban en vertical y bajaban la tabla.

   El hueco entre botones va en `margin-left` del segundo y el tercero, y NO en `gap`: el `gap` de
   flexbox pide Chrome 84 y el `browserslist` de este repo baja a Chrome 49, así que en los
   navegadores del suelo no se aplicaría y los tres saldrían pegados. Es el mismo motivo que ya está
   escrito en `.mm-*` de «Facturación por ejercicios». */
.cabecera-acciones {
  display: flex; align-items: center; flex-wrap: nowrap; flex: 0 0 auto;
}
.cabecera-acciones .btn-pdf, .cabecera-acciones .btn-guardar { margin-left: 6px; }
/* LOS TRES BOTONES · compactos, como pidió dirección el 18/10/2026, y los tres con la MISMA caja:
   son la misma fila y una altura distinta la descuadra.

   `width: auto` no es decoración y es la causa de que se apilaran: Framework7 declara
   `button { width: 100% }` por selector de tipo (`framework7.bundle.css`), así que cada uno de los
   tres pedía el ancho completo de la cabecera y se iba a su propia línea —tres líneas de pantalla
   comidas, y la tabla «Día a día» tres líneas más abajo—. Es el mismo roce que ya está documentado
   en `.fuera-de-mes-detalle` aquí abajo y en `.navbar-btn` de «Abonos abiertos».

   `white-space: nowrap` por lo mismo, un paso más allá: sin él un rótulo de dos palabras se parte en
   dos líneas al estrecharse y el botón crece de alto, que es lo que dirección no quiere. */
.btn-enviar, .btn-pdf, .btn-guardar {
  width: auto; min-width: 0; flex: 0 0 auto; white-space: nowrap;
  border-radius: 8px; font-size: 12px; font-weight: 600; padding: 6px 11px; cursor: pointer;
  display: flex; align-items: center;
}
/* Guardar: el borde es del mismo color que el fondo —no se ve— y está para que los tres botones
   midan igual de alto y de ancho por dentro. Sin él, el único sin borde queda 2 px más bajo que sus
   dos vecinos y la fila se descuadra. */
.btn-guardar { border: 1px solid #0F6E56; background: #0F6E56; color: white; }
.btn-guardar:disabled { border-color: #c8d3e5; background: #c8d3e5; color: #7c8db0; cursor: default; }
.btn-guardar-icono { font-size: 15px; margin-right: 4px; }
/* Exportar a PDF: secundario frente a Guardar, que es la acción que escribe. */
.btn-pdf { border: 1px solid #185FA5; background: white; color: #185FA5; }
.btn-pdf:disabled { border-color: #c8d3e5; color: #a3b0c6; cursor: default; }
.btn-pdf-icono { font-size: 15px; margin-right: 4px; }
/* Enviar reporte: la misma caja y el mismo peso que «Exportar a PDF» —son las dos salidas del mismo
   mes y ninguna manda sobre la otra—, y los dos a la izquierda de «Guardar», que es la que escribe. */
.btn-enviar { border: 1px solid #185FA5; background: white; color: #185FA5; }
.btn-enviar:disabled { border-color: #c8d3e5; color: #a3b0c6; cursor: default; }
.btn-enviar-icono { font-size: 15px; margin-right: 4px; }

/*
 * LA MISMA FILA EN UN MÓVIL ESTRECHO · la app se distribuye también por Cordova, y con tres botones
 * `nowrap` la fila mide más que la pantalla: lo que sobra lo arrastra la PÁGINA entera, que es el
 * scroll horizontal que la #184 prohíbe, y «Guardar», que va el último, se queda fuera del borde.
 *
 * La cuenta, aproximada, a 12 px y con icono: «Enviar reporte» y «Exportar a PDF» unos 119 px cada
 * uno —76 de rótulo, 19 de icono con su hueco, 22 de relleno y 2 de borde— y «Guardar (12)» unos
 * 111; con los dos huecos de 6, la fila pide ~360 px, y sumando los 32 de relleno de `.cabecera`
 * empieza a desbordar por debajo de unos 390 px de ventana. El corte va a 480 para dejar fuera
 * también los móviles grandes (430 px) y el contador de «Guardar (N)» con dos cifras.
 *
 * Lo que cede son los botones, NO la fila: dejarlos envolver es volver a apilarlos, que es
 * exactamente lo que dirección rechazó el 18/10/2026 —la tabla «Día a día» tiene que empezar a la
 * misma altura—. Sin icono, a 11 px y con menos relleno la fila baja a ~258 px y entra en los 288
 * que deja una pantalla de 320. El icono es lo primero que se va porque es lo único que no lleva
 * información: el rótulo ya dice lo que hace el botón.
 *
 * `flex: 0 1 auto` es el último seguro, no el plan: si algún rótulo creciera, el botón se estrecha
 * dentro de la fila en vez de empujar la página hacia fuera.
 */
@media (max-width: 480px) {
  .cabecera-acciones .btn-pdf, .cabecera-acciones .btn-guardar { margin-left: 4px; }
  .btn-enviar, .btn-pdf, .btn-guardar {
    font-size: 11px; padding: 5px 7px; flex: 0 1 auto; overflow: hidden;
  }
  .btn-enviar-icono, .btn-pdf-icono, .btn-guardar-icono { display: none; }
}

.nota-exportar {
  font-size: 12px; color: #65718a; line-height: 1.4;
  margin: 8px 16px 0; max-width: 760px;
}

/* La espera de «Guardar» ocupa su propia línea de la cabecera: compartiendo fila con el botón le
   quitaría el sitio, y lo que no puede pasar es que el aviso estruje al control del que habla. */
.guardar-espera {
  flex: 0 0 100%; max-width: min(100%, 420px); margin: 6px 0 0 auto;
  font-size: 11px; color: #65718a; line-height: 1.4; text-align: right;
}
/* El mismo tono que `guardar-espera`, que es el otro control que espera a lo mismo, pero alineado
   con el título de la tabla: es de las casillas que tiene debajo, no de la cabecera. */
.casillas-espera {
  margin: 0 0 6px; font-size: 11px; color: #65718a; line-height: 1.4;
}

/* ---------------------------------------------------------------------- avisos */
.aviso-error {
  background: #FAECE7; border-left: 4px solid #D85A30; border-radius: 6px;
  padding: 10px 12px; font-size: 13px; color: #7a2d12; margin: 12px 16px;
}
.aviso-cerrada {
  background: #FFF6E5; border-left: 4px solid #C8862A; border-radius: 6px;
  padding: 14px 16px; font-size: 14px; color: #5c3d05; margin: 16px;
  line-height: 1.5; max-width: 760px;
}
/* El guardado que no entró (#164). Se distingue del error de carga: aquí hay un dato escrito que
   sigue en pantalla y que hay que volver a intentar, no una pantalla que no ha cargado. */
.aviso-guardado-error {
  background: #FAECE7; border-left: 4px solid #D85A30; border-radius: 6px;
  padding: 12px 14px; margin: 12px 16px; max-width: 760px; color: #7a2d12;
}
.aviso-guardado-titulo { display: flex; align-items: center; margin: 0; font-size: 14px; font-weight: 700; }
.aviso-guardado-lista { margin: 8px 0 0; padding-left: 18px; font-size: 13px; line-height: 1.5; }
.aviso-guardado-nota { margin: 8px 0 0; font-size: 12px; line-height: 1.4; }
.aviso-guardado-ok {
  background: #E8F5F0; border-left: 4px solid #0F6E56; border-radius: 6px;
  padding: 10px 12px; font-size: 13px; color: #0b4e3d; margin: 12px 16px; max-width: 760px;
}
/* El correo del que NO SE SABE si salió (#547, ronda 3). En ámbar y no en rojo, con los mismos
   colores que el resto de los avisos de esta pantalla que piden mirar algo: en rojo se leería como
   «no ha salido», que es justo lo que no se puede afirmar y lo que invita a repetirlo. */
.aviso-envio-incierto {
  background: #FFF6E5; border-left: 4px solid #C8862A; border-radius: 6px;
  padding: 12px 14px; margin: 12px 16px; max-width: 760px; color: #5c3d05;
  font-size: 13px; line-height: 1.5;
}
.aviso-envio-salida { margin-top: 10px; display: flex; align-items: center; flex-wrap: wrap; }
/* `width: auto` por lo mismo que los tres de la cabecera: con el `button { width: 100% }` de
   Framework7 este botón se comía la línea entera y empujaba su nota al renglón de abajo, que es
   justo la nota que explica lo que va a pasar al pulsarlo. */
.btn-envio-otra-vez {
  width: auto; min-width: 0; flex: 0 0 auto; white-space: nowrap;
  background: #fff; border: 1px solid #C8862A; border-radius: 6px; color: #5c3d05;
  padding: 6px 12px; font-size: 13px; font-weight: 600; cursor: pointer; margin-right: 10px;
}
.aviso-envio-salida-nota { font-size: 12px; color: #7a5a14; }

/* La casilla que otra persona ha movido mientras se escribía. En azul y no en naranja: no es un
   fallo —no hay nada que reintentar— y en naranja se leería como el guardado que no entró. */
.aviso-base-movida {
  background: #E9F0FA; border-left: 4px solid #185FA5; border-radius: 6px;
  padding: 12px 14px; margin: 12px 16px; max-width: 760px; color: #123c66;
}
.aviso-base-movida .aviso-revisar-icono { color: #185FA5; }

/* Al mes en curso le falta la hora de recálculo. En ámbar como el resto de los avisos que piden
   mirar algo: no es un error de la pantalla, es que las cifras pueden no estar al día. */
.aviso-recalculo {
  display: flex; align-items: flex-start;
  background: #FFF6E5; border-left: 4px solid #C8862A; border-radius: 6px;
  padding: 12px 14px; margin: 12px 16px; max-width: 760px; color: #5c3d05;
  font-size: 13px; line-height: 1.5;
}
.aviso-recalculo-icono { color: #C8862A; font-size: 18px; margin-right: 8px; flex: 0 0 auto; }

/* El mes que no se ha podido releer después de guardar (#215). En ámbar, con el resto de los avisos
   que dicen «las cifras pueden no estar al día», y no en naranja: el naranja de esta pantalla es el
   del dato que no ha entrado, y lo que no ha entrado ya lo dice su propio aviso justo debajo. */
.aviso-relectura {
  display: flex; align-items: flex-start;
  background: #FFF6E5; border-left: 4px solid #C8862A; border-radius: 6px;
  padding: 12px 14px; margin: 12px 16px; max-width: 760px; color: #5c3d05;
  font-size: 13px; line-height: 1.5;
}
.aviso-relectura-icono { color: #C8862A; font-size: 18px; margin-right: 8px; flex: 0 0 auto; }

.aviso-revisar {
  background: #FFF6E5; border-left: 4px solid #C8862A; border-radius: 6px;
  padding: 12px 14px; margin: 12px 16px; max-width: 760px; color: #5c3d05;
}
.aviso-revisar-titulo { display: flex; align-items: center; margin: 0; font-size: 14px; font-weight: 700; }
.aviso-revisar-icono { color: #C8862A; font-size: 18px; margin-right: 6px; }
.aviso-guardado-error .aviso-revisar-icono { color: #D85A30; }
.aviso-revisar-lista { margin: 8px 0 0; padding-left: 18px; font-size: 13px; line-height: 1.5; }
.aviso-revisar-abono {
  background: #C8862A; color: white; border-radius: 4px;
  font-size: 10px; font-weight: 700; padding: 1px 5px; margin-left: 4px; text-transform: uppercase;
}
.aviso-revisar-nota { margin: 8px 0 0; font-size: 12px; line-height: 1.4; }

/* EL AVISO DE DESCUADRE AL PIE DE LA TABLA (#295) · en el mismo ámbar que los demás avisos de esta
   pantalla y no en rojo: la tabla y el TOTAL se siguen leyendo, así que no es un error, es un «mira
   esto». Pegado a la tabla —`margin-top` pequeño— para que se lea como parte de su pie y no como un
   bloque nuevo. */
.aviso-descuadre {
  background: #FFF6E5; border-left: 4px solid #C8862A; border-radius: 6px;
  padding: 10px 12px; margin: 8px 0 0; max-width: 860px; color: #5c3d05;
}
.aviso-descuadre-titulo {
  display: flex; align-items: center; margin: 0; font-size: 13px; font-weight: 700;
}
.aviso-descuadre-lista {
  margin: 6px 0 0; padding-left: 18px; font-size: 12px; line-height: 1.5;
}
.aviso-descuadre-nota { margin: 6px 0 0; font-size: 11px; line-height: 1.4; }

.aviso-sin-detalle {
  display: flex; align-items: flex-start;
  background: white; border: 1px solid #e0e0e0; border-left: 4px solid #7c8db0; border-radius: 10px;
  padding: 14px 16px; font-size: 13px; color: #44516b; line-height: 1.5; max-width: 760px;
}
.aviso-sin-detalle-icono { color: #7c8db0; font-size: 20px; margin-right: 10px; flex: 0 0 auto; }

/* ---------------------------------------------------------------- 2 · LOS RESÚMENES */
.resumenes {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 12px; padding: 12px 16px;
}
.resumen {
  background: white; border: 1px solid #e0e0e0; border-radius: 10px;
  border-left: 4px solid #185FA5; padding: 12px 14px;
}
/* LOS RÓTULOS DE LA TARJETA, DOS PUNTOS MÁS GRANDES (#618, dirección 18/10/2026) · las cuatro
   etiquetas —OBJETIVO DEL MES, CONSEGUIDO, FALTA, CUMPLIMIENTO— son lo que dice qué es cada cifra, y
   a 10 px en mayúsculas se leían peor que la cifra que explican. El título de la tarjeta sube con
   ellas: dejarlo en 11 px lo pondría por debajo de las etiquetas que encabeza, que es la jerarquía
   del revés. Las cifras grandes (`resumen-val`) no se tocan: ya se leen bien y subirlas obligaría a
   recolocar la tarjeta. */
.resumen-rotulo {
  font-size: 12px; color: #7c8db0; margin: 0 0 8px;
  text-transform: uppercase; letter-spacing: .4px; font-weight: 700;
}
.resumen-cifras { display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 8px; }
.resumen-celda { display: flex; flex-direction: column; }
.resumen-eti { font-size: 12px; color: #7c8db0; text-transform: uppercase; letter-spacing: .3px; }
.resumen-val { font-size: 18px; font-weight: 700; color: #1a2744; }
.resumen-val-nc { font-size: 13px; font-weight: 600; color: #B06A00; }
.resumen-pista {
  display: block; background: #f2f5fa; border-radius: 3px; height: 8px; margin-top: 10px;
  overflow: hidden;
}
.resumen-barra { display: block; height: 8px; border-radius: 3px; }
.resumen-nc { font-size: 11px; color: #B06A00; margin: 8px 0 0; line-height: 1.4; }

/* El sello y el botón del objetivo, en la misma línea y pegados a la cifra: la fecha de cuándo se
   calculó solo informa si se lee al lado del número que explica. */
.resumen-objetivo {
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap;
  gap: 8px; margin-top: 10px;
}
/* El sello «Calculado el … por …» también sube dos puntos (#618): dirección lo lee a diario para
   saber de cuándo son las cifras, y es la línea que la ficha nombra junto a las etiquetas. Sigue
   compartiendo fila con «Recalcular» —`resumen-objetivo` envuelve— así que si en una tarjeta
   estrecha ya no caben los dos, el botón baja a su línea como hacía antes. */
.resumen-sello { font-size: 12px; color: #7c8db0; line-height: 1.3; }
.resumen-sello-sin { color: #B06A00; font-weight: 600; }
/* `flex: 0 0 auto` ya estaba, pero no basta: ese `auto` es el `width` del botón, y el `width: 100%`
   que Framework7 pone a todo `<button>` hacía que el ancho pedido fuera el del bloque entero, así
   que «Recalcular» bajaba a su propia línea estirado de lado a lado en vez de quedarse al lado de su
   sello «Calculado el …». Es el mismo olvido que había en los tres de la cabecera. */
.btn-recalcular {
  width: auto; min-width: 0; white-space: nowrap;
  border: 1px solid #c8d3e5; border-radius: 8px; background: white; color: #185FA5;
  font-size: 12px; font-weight: 600; padding: 5px 10px; cursor: pointer;
  display: flex; align-items: center; flex: 0 0 auto;
}
.btn-recalcular:disabled { background: #f2f5fa; color: #7c8db0; cursor: default; }
.btn-recalcular-icono { font-size: 14px; margin-right: 5px; }
.resumen-espera { font-size: 11px; color: #65718a; margin: 6px 0 0; line-height: 1.4; }
.resumen-fallo { font-size: 11px; color: #7a2d12; margin: 6px 0 0; line-height: 1.4; }
/* EL AVISO DE QUE LA CIFRA NO RECOGE TODO (#295) · dentro del bloque y debajo de las cuatro cifras,
   en el ámbar de los avisos de esta pantalla. Con fondo propio para que no se lea como una nota más
   del objetivo: tiene que competir con una cifra de 20 px que está justo encima. Y en `flex` con el
   icono fijo, como los demás avisos, para que la frase no se meta debajo de él al partirse. */
.resumen-descuadre {
  display: flex; align-items: flex-start; margin: 8px 0 0;
  background: #FFF6E5; border-left: 3px solid #C8862A; border-radius: 5px;
  padding: 7px 9px; font-size: 11px; line-height: 1.45; color: #5c3d05;
}
.resumen-descuadre-icono { color: #C8862A; font-size: 16px; margin-right: 6px; flex: 0 0 auto; }

.nota-objetivos {
  display: flex; align-items: flex-start;
  background: #EEF3FA; border-left: 4px solid #185FA5; border-radius: 6px;
  padding: 10px 12px; margin: 0 16px 12px; font-size: 12px; color: #1a2744; line-height: 1.4;
}
.nota-objetivos-icono { color: #185FA5; font-size: 18px; flex: 0 0 auto; margin-right: 8px; }

.nota-cobro {
  display: flex; align-items: flex-start;
  background: #FAEEDA; border-left: 4px solid #BA7517; border-radius: 6px;
  padding: 10px 12px; margin: 0 16px 12px; font-size: 12px; color: #6b4a0c; line-height: 1.4;
}
.nota-cobro-icono { color: #BA7517; font-size: 18px; flex: 0 0 auto; margin-right: 8px; }

/* ------------------------------------------------------------------------ bloques */
.bloque { margin: 0 16px 18px; }
.bloque-titulo {
  font-size: 12px; font-weight: 700; color: #1a2744; margin: 0 0 6px;
  text-transform: uppercase; letter-spacing: .4px;
}
.bloque-pie { font-size: 11px; color: #7c8db0; margin: 6px 0 0; line-height: 1.4; }
.var-positivo { color: #0F6E56; }
.var-negativo { color: #C0392B; }

/* ----------------------------------------------------------------------- 3 · TABLA */
/* La tabla se desplaza DENTRO de su caja, en los dos sentidos, y de ahí el alto máximo.
   El vertical no es un adorno: la cabecera y el pie van `position: sticky`, y un `sticky` se pega
   al ancestro que se desplaza. Con solo `overflow-x`, ese ancestro es esta caja y la caja no se
   desplaza en vertical: al bajar por la página se iba entera y la cabecera con ella, así que en un
   mes largo se leían doce columnas de cifras sin saber cuál era cada una. Con el alto acotado, la
   que se desplaza es la caja y la cabecera se queda. */
.tabla-scroll {
  overflow-x: auto; overflow-y: auto; max-height: 68vh;
  background: white; border: 1px solid #e0e0e0; border-radius: 10px;
}
/* No baja de 980 px: con doce columnas, por debajo de eso los importes se parten y la tabla deja de
   leerse, así que en una pantalla estrecha se desplaza en horizontal en vez de comprimirse. Mismo
   criterio que el gráfico de la Tarjeta B. */
/* EL CUERPO DE LA TABLA, DOS PUNTOS MÁS GRANDE (#618, dirección 18/10/2026) · es la cifra que se
   lee a diario, y a 11 px dirección la leía con esfuerzo. Los dos puntos van en el `font-size` de la
   TABLA y no celda a celda: así suben de una vez los días, el pie de totales y la línea de
   registrado fuera de mes —que heredan de aquí— y ninguna de las tres se queda atrás por olvido.
   No se toca el ancho mínimo ni el relleno de las celdas: la cuenta de columnas y el alto de la
   cabecera de la pantalla son los mismos que antes. */
.tabla-cd { border-collapse: collapse; width: 100%; min-width: 980px; font-size: 13px; }
.tabla-cd th, .tabla-cd td {
  padding: 4px 7px; text-align: right; white-space: nowrap; border-bottom: 1px solid #edf1f7;
}
/* Los rótulos de columna suben los mismos dos puntos que las cifras: son el par que se lee junto
   —«¿esta cifra de qué columna es?»— y subir solo uno de los dos vuelve ilegible al otro. */
.tabla-cd thead th {
  background: #f7f9fc; color: #7c8db0; font-size: 12px;
  text-transform: uppercase; letter-spacing: .3px; position: sticky; top: 0; z-index: 2;
}
/* El rótulo de D-FACT-IVA, debajo del nombre de su columna: se lee como una aclaración de FACTURADO
   y no como un segundo rótulo. `white-space: normal` y el ancho tope son para que quepa en dos
   líneas en vez de estirar la columna: el `nowrap` lo heredaría del `th` y la tabla entera se iría
   medio centenar de píxeles a la derecha, que es reorganizar una pantalla que ya funcionaba. */
.tabla-cd thead th .th-nota {
  display: block; white-space: normal; max-width: 96px; margin-left: auto;
  font-size: 9px; font-weight: 400; text-transform: none; letter-spacing: 0;
  color: #9aa7bd; line-height: 1.25;
}
/* El pie se queda pegado abajo por lo mismo que la cabecera arriba: el TOTAL es lo que dirección
   coteja con las filas de un vistazo, y en un mes largo quedaba fuera de la caja. */
.tabla-cd tfoot td {
  background: #f7f9fc; border-top: 2px solid #c8d3e5; border-bottom: 0;
  position: sticky; bottom: 0; z-index: 2;
}
.col-sticky { position: sticky; left: 0; background: white; text-align: left; z-index: 1; }
.tabla-cd thead .col-sticky { background: #f7f9fc; z-index: 3; }
.tabla-cd tfoot .col-sticky { background: #f7f9fc; z-index: 3; }
.col-dia { min-width: 92px; }
/* Las dos etiquetitas de la columna DÍA y la marca del TOTAL suben un punto, no dos: son
   acompañantes de un rótulo que ahora es de 13 px y a 9 px se leerían como una errata, pero
   igualarlas al rótulo las convertiría en un segundo rótulo. Es la proporción que la ficha de la
   #618 deja mantener, no un tamaño nuevo. */
.dia-habil {
  display: inline-block; background: #eef0f4; color: #65718a; border-radius: 8px;
  font-size: 10px; padding: 1px 5px; margin-left: 5px;
}
.dia-no-habil {
  display: inline-block; background: #FAEEDA; color: #8a6414; border-radius: 8px;
  font-size: 10px; padding: 1px 5px; margin-left: 5px;
}
/* La marca del TOTAL cuando hay descuadre (#295). Misma forma que las dos de arriba —son la misma
   clase de etiqueta pegada a la primera columna— y en el ámbar de los avisos. */
.total-incompleto {
  display: inline-block; background: #C8862A; color: white; border-radius: 8px;
  font-size: 10px; font-weight: 700; padding: 1px 5px; margin-left: 5px; text-transform: uppercase;
}
/* El corte visual entre el bloque de facturación y el de cobro, que en la maqueta es una doble
   línea: son dos mitades de la tabla y se leen por separado. */
.col-separador { border-left: 2px solid #c8d3e5; }

/* Lo manual se distingue a la vista (D-MANUAL): fondo distinto y casilla de entrada. */
.col-manual, .col-manual-th { background: #FFFBEA; }
.tabla-cd thead .col-manual-th { background: #FDF3D0; color: #8a6414; }
.col-manual { padding: 2px 4px; }
/*
 * EL EJE DE LAS CUATRO COLUMNAS MANUALES (#618, dirección 18/10/2026) · en estas cuatro —POEDD,
 * COBRO REAL, DEPÓSITO y TOTAL DEPÓSITO— la cantidad del día no está en la celda: está DENTRO de una
 * casilla, con el borde y el relleno de la casilla por medio. La cifra del día cae, contando desde
 * el borde derecho de su celda, a 4 px de relleno de la celda + 1 de borde de la casilla + 5 de
 * relleno de la casilla = 10 px —y 15 más si el navegador le pone flechitas, ver abajo—; el TOTAL del
 * pie y el rótulo de la cabecera son celdas normales y caen a los 7 px del relleno general. Esa
 * diferencia es el desajuste que dirección fotografió el 18/10/2026: el total no se leía en la
 * vertical de las cantidades, y de las seis columnas que nombró son estas cuatro las que lo tenían.
 *
 * Se arregla por los dos lados y en un solo sitio, porque las cuatro comparten clase:
 *  - la casilla se pega al borde derecho de su celda —`margin-left: auto`, el mismo recurso que
 *    `.th-nota`—, para que su cifra no dependa de cómo el navegador coloque un `<input>` cuando a la
 *    columna le sobra ancho (la tabla es `width: 100%`, así que a 1920 px sobra);
 *  - la cabecera y el pie de esas columnas se separan los mismos 10 px del borde, no 7.
 * Resultado: rótulo, cantidades de los días y total, los tres en la misma vertical.
 *
 * Las otras dos columnas de cobro que nombró la ficha —COBRO + DEP. y DIFERENCIA— y las cuatro de
 * facturación no necesitan nada: son celdas normales arriba y abajo, con el mismo relleno y la misma
 * alineación, así que ya estaban en un solo eje. Lo que se veía torcido en ellas era el vecino.
 */
.tabla-cd thead .col-manual-th, .tabla-cd tfoot .col-manual-th { padding-right: 10px; }
.entrada-manual {
  display: block; margin-left: auto; -moz-appearance: textfield;
  width: 88px; border: 1px solid #E3D08E; border-radius: 4px; background: white;
  font-size: 13px; text-align: right; padding: 3px 5px; color: #1a2744;
}
/* Y las flechitas del `number` fuera —`-moz-appearance` arriba, el par de pseudoelementos aquí—, que
   es la mitad gorda del desajuste: Chrome las dibuja dentro de la casilla y a su derecha, así que se
   comen unos 15 px y empujan la cifra del día hacia la izquierda, mientras el TOTAL del pie, que no
   es una casilla, se queda pegado al borde. `appearance: none` en el propio `<input>` no las quita
   —hay que ir al pseudoelemento— y por eso llevaban ahí desde el principio.
   Lo que se teclea no cambia, y las flechas del teclado siguen subiendo y bajando el importe: lo que
   desaparece es el par de flechas de ratón, que para escribir un importe con céntimos no se usa y
   movía el eje de la columna. */
.entrada-manual::-webkit-outer-spin-button,
.entrada-manual::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.entrada-manual:focus { outline: none; border-color: #BA7517; box-shadow: 0 0 0 2px #FAEEDA; }
.entrada-manual:disabled { background: #f6f6f4; color: #9aa7bd; border-color: #e3e3dd; }
/* Los cuatro estados de una casilla, y el orden en el fichero es su prioridad: el último que case
   gana, porque es lo que hay que hacer con esa casilla lo que decide cuál importa.
   El guardado va primero: en cuanto se vuelve a teclear encima deja de aplicar y lo tapa «tocada». */
.col-manual-guardada { background: #E8F5F0; }
.col-manual-guardada .entrada-manual { border-color: #0F6E56; }
/* Lo tocado y sin guardar, y lo que se intentó guardar y no entró: dos estados distintos, porque
   lo que hay que hacer con cada uno es distinto. */
.col-manual-tocada { background: #FDF3D0; }
.col-manual-tocada .entrada-manual { border-color: #BA7517; font-weight: 700; }
.col-manual-fallida { background: #FAECE7; }
.col-manual-fallida .entrada-manual { border-color: #D85A30; font-weight: 700; }
/* Y la que otra persona ha movido por debajo: tercer estado, porque lo que hay que decidir es otra
   cosa —guardar la tuya encima o quedarse con la suya—. Va después de `fallida` en el fichero a
   propósito: si una casilla es las dos cosas, lo que importa es que la cifra de debajo cambió. */
.col-manual-base-movida { background: #E9F0FA; }
.col-manual-base-movida .entrada-manual { border-color: #185FA5; font-weight: 700; }
/* Y la que tiene texto a medias —un `12.`, que todavía no es una cifra—: es la que hay que terminar
   para poder guardar o recalcular, y el aviso de los dos botones la nombra. Va última porque mientras
   se está escribiendo es lo único que importa de esa casilla. */
.col-manual-a-medias { background: #FDF3D0; }
.col-manual-a-medias .entrada-manual { border-color: #BA7517; border-style: dashed; font-weight: 700; }

.fila-hoy { background: #E6F1FB; }
.fila-hoy .col-sticky { background: #E6F1FB; }
.fila-no-habil td { background: #fbfbf8; }
.fila-no-habil .col-manual { background: #FFFBEA; }

/* LA LÍNEA DE REGISTRADO FUERA DE MES (#289) · es una fila de la tabla, no un aviso: se pinta en
   ámbar —que en esta pantalla es «mira esto»— para que no se lea como un día más, pero su importe
   está en la columna FACTURADO, alineado con los días, porque el TOTAL lo suma con ellos. */
.fila-fuera-de-mes td {
  background: #FFFBEA; border-top: 1px solid #E3D08E; border-bottom: 1px solid #E3D08E;
  color: #6b4a0c;
}
/* La celda de la explicación. `white-space: normal` porque la tabla lo pone a `nowrap` para los
   importes y aquí hay una frase: sin esto la celda ensancharía la tabla hasta sacarla de la caja.
   Y los 13 px son los de la tabla (#618): esta línea es una fila más de la columna FACTURADO y
   dirección la nombró con las cifras, así que no puede quedarse dos puntos por debajo de ellas. */
.celda-fuera-de-mes {
  text-align: left; white-space: normal; font-size: 13px; line-height: 1.4;
}
/* El que abre el detalle. Es un `<button>` porque dispara una acción, pero se lee y se pulsa como
   un enlace más de la frase, así que tiene que declarar su ancho y su alineación:
   Framework7 pone `button { width: 100% }` a TODOS los botones de la aplicación
   (`framework7.bundle.css`), y el navegador les da `text-align: center` por su cuenta. Sin estas
   tres líneas la caja se comía la celda entera —1.246 px medidos en la preview— y las palabras se
   dibujaban centradas a unos 600 px del final de la frase, o directamente fuera de la fila en una
   ventana estrecha: el control existía y funcionaba, pero no se encontraba. */
/* Y su tamaño es el de la frase que lo rodea, no uno propio: es una palabra más de esa línea, y a
   13 px la línea no puede llevar un «ver el detalle» dos puntos más pequeño por dentro (#618). */
.fuera-de-mes-detalle {
  display: inline; width: auto; min-width: 0; text-align: left; vertical-align: baseline;
  margin: 0 0 0 4px; padding: 0; border: 0; background: none; font-size: 13px;
  font-family: inherit; color: #8a6414; text-decoration: underline; text-decoration-style: dotted;
  cursor: pointer;
}
.fuera-de-mes-detalle:hover, .fuera-de-mes-detalle:focus { color: #BA7517; }
/* El foco de teclado se ve. El cambio de color del texto —#8a6414 a #BA7517, sobre el mismo fondo
   ámbar de la fila— no basta para saber qué control está enfocado al llegar con Tab, y es lo único
   que quedaba porque este selector retiraba el recuadro nativo con `outline: none`. Mismo esquema
   que en Novedades: el `:focus` a secas funciona en todas partes y el par siguiente retira el
   recuadro al pulsar con el ratón donde el navegador sabe distinguirlo; un navegador que no
   entienda `:focus-visible` descarta las dos reglas y se queda con el recuadro siempre, que es el
   fallo del lado seguro. */
.fuera-de-mes-detalle:focus { outline: 2px solid #BA7517; outline-offset: 2px; }
.fuera-de-mes-detalle:focus:not(:focus-visible) { outline: none; }
.fuera-de-mes-detalle:focus-visible { outline: 2px solid #BA7517; outline-offset: 2px; }
/* La fila del detalle, debajo de la línea que lo abre. */
.fila-fuera-de-mes-detalle td {
  background: #FFFBEA; border-bottom: 1px solid #E3D08E; text-align: left;
  white-space: normal; padding: 8px 10px;
}
.fuera-de-mes-lista {
  margin: 0; padding-left: 18px; font-size: 12px; line-height: 1.5; color: #4a3b1a;
}
.fuera-de-mes-nota { margin: 6px 0 0; font-size: 11px; color: #6b4a0c; line-height: 1.4; max-width: 860px; }

.leyenda {
  display: flex; align-items: flex-start; margin-top: 8px;
  font-size: 11px; color: #6b4a0c; line-height: 1.4; max-width: 860px;
}
.leyenda-muestra {
  width: 14px; height: 14px; border-radius: 3px; flex: 0 0 auto; margin-right: 8px; margin-top: 1px;
}
.leyenda-muestra-manual { background: #FFFBEA; border: 1px solid #E3D08E; }

/* ------------------------------------------------------------------ 4 · DESGLOSE */
.desgloses {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 12px;
}
.desglose { background: white; border: 1px solid #e0e0e0; border-radius: 10px; padding: 12px 14px; }
.desglose-titulo {
  font-size: 11px; font-weight: 700; color: #1a2744; margin: 0 0 8px;
  text-transform: uppercase; letter-spacing: .3px;
}
.desglose-linea {
  display: flex; align-items: flex-start; padding: 6px 0; border-bottom: 1px solid #f2f5fa;
  font-size: 12px;
}
.desglose-concepto { flex: 1 1 auto; display: flex; flex-direction: column; padding-right: 8px; }
.desglose-eti { color: #1a2744; font-weight: 600; }
.desglose-origen { font-size: 10px; color: #7c8db0; line-height: 1.3; margin-top: 2px; }
.desglose-base { width: 86px; flex: 0 0 auto; text-align: right; color: #44516b; }
.desglose-factor { width: 48px; flex: 0 0 auto; text-align: right; color: #7c8db0; }
.desglose-importe { width: 86px; flex: 0 0 auto; text-align: right; color: #1a2744; font-weight: 600; }
.desglose-total {
  display: flex; justify-content: space-between; align-items: baseline;
  padding-top: 8px; margin-top: 4px; border-top: 2px solid #c8d3e5; font-size: 12px; color: #1a2744;
}
.desglose-diario { border-top: 1px solid #edf1f7; }
/* Lo que una regla ha dejado fuera de la línea de arriba (#257). Sangrado y en gris para que se
   lea como una nota de SU línea y no como un sumando más del desglose. */
.desglose-excluidas { margin: 2px 0 8px; padding: 6px 8px 6px 10px; background: #f7f9fc;
  border-left: 2px solid #d6dfec; border-radius: 0 6px 6px 0; }
.desglose-excluidas-tit { font-size: 10px; color: #5f7193; margin: 0; line-height: 1.35;
  font-weight: 600; }
.desglose-excluida { font-size: 10px; color: #7c8db0; margin: 2px 0 0; line-height: 1.35; }
.desglose-vacio { font-size: 12px; color: #B06A00; margin: 4px 0 0; line-height: 1.4; }
.desglose-nc { font-size: 11px; color: #B06A00; margin: 8px 0 0; line-height: 1.4; }
.desglose-sello { font-size: 10px; color: #7c8db0; margin: 8px 0 0; }

/* ------------------------------------------------------- enviar reporte (#547) */
.envio-mes { font-size: 13px; color: #1a2744; line-height: 1.5; }
/* De cuándo son las cifras que se van a mandar: va dentro del mismo bloque que dice de qué mes es
   —es lo mismo contado entero— y en el tono de los avisos de esta pantalla, no en rojo: no es un
   fallo, es la vigencia de lo que se está viendo. */
.envio-mes-vigencia {
  display: block; margin-top: 8px; font-size: 11px; color: #B06A00; line-height: 1.4;
}
.envio-rotulo {
  font-size: 11px; font-weight: 600; color: #64748b; margin: 10px 0 4px;
  text-transform: uppercase; letter-spacing: 0.4px;
}
.envio-campo {
  width: 100%; padding: 7px 9px; border: 1px solid #ddd; border-radius: 4px;
  font-size: 13px; box-sizing: border-box;
}
.envio-ayuda { font-size: 11px; color: #65718a; line-height: 1.4; margin: 4px 0 0; }
/* El aviso de por qué no se puede enviar: el mismo tono de los demás avisos de espera de esta
   pantalla, para que se lea como una indicación y no como un error. */
.envio-ayuda-aviso { color: #B06A00; margin-top: 8px; }
/* El hueco entre chips va en el margen de cada uno y no en `gap`, por lo mismo que la fila de
   botones de la cabecera: `gap` pide Chrome 84 y el `browserslist` de este repo baja a Chrome 49,
   donde los chips saldrían pegados y no se sabría dónde acaba uno y empieza el siguiente. */
.envio-chips { display: flex; flex-wrap: wrap; margin-bottom: -6px; }
.envio-chip {
  padding: 5px 11px; border-radius: 12px; background: #f0f4f8; color: #64748b;
  font-size: 12px; font-weight: 500; cursor: pointer; margin: 0 6px 6px 0;
}
.envio-chip-on { background: #185FA5; color: white; }
/* El chip retenido mientras el correo sale: apagado y sin cursor de mano, como un control
   deshabilitado, porque es lo que es. El porqué va escrito encima, no en el propio chip. */
.envio-chip-retenido { opacity: 0.55; cursor: default; }
.envio-chip-cuantos { font-size: 10px; opacity: 0.85; margin-left: 4px; }

/* ------------------------------------------------- PROGRAMAR REPORTE (#549)
 * El diálogo reutiliza los estilos del de «Enviar reporte» para todo lo que es lo mismo —los chips
 * de destinatarios, los rótulos, los campos y los avisos—, y añade solo lo suyo: la ficha de cada
 * regla, los botones pequeños y la tabla del historial. Son los mismos nombres y los mismos colores
 * que en «Abonos abiertos», que es la otra pantalla con programación. */

/* El enlace de la barra. `white-space: nowrap` porque son dos palabras y la barra es estrecha: sin
   él se parte en dos líneas y la barra crece de alto. */
.navbar-programar {
  font-size: 13px; font-weight: 600; color: #c8d3e5; white-space: nowrap; padding: 0 8px;
}

.prog-regla {
  border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 12px; margin: 0 16px 8px;
  background: white;
}
/* Una regla pausada se ve apagada, y además lo dice su botón («Activar») y su punto gris: el color
   solo no distingue nada para quien no lo vea. */
.prog-regla-pausada { background: #f8fafc; }
.prog-regla-cabecera {
  display: flex; align-items: center; flex-wrap: wrap; font-size: 13px; color: #1a2744;
}
.prog-punto {
  width: 8px; height: 8px; border-radius: 50%; background: #c8d3e5; margin-right: 8px;
  flex: 0 0 auto;
}
.prog-punto-activa { background: #0F6E56; }
.prog-chip-neutro {
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px;
  background: #eef0f4; color: #65718a; border-radius: 10px; padding: 2px 8px; margin-left: 8px;
}
.prog-detalle { font-size: 11px; color: #65718a; line-height: 1.4; margin: 6px 0 0; }
.prog-botones { display: flex; flex-wrap: wrap; margin: 8px 0 0 -6px; }
/* `width: auto` no es decoración: Framework7 declara `button { width: 100% }` por selector de tipo,
   así que sin esto cada botón pediría la línea entera y los tres saldrían apilados. Es el mismo
   roce que ya está escrito en la fila de botones de la cabecera. */
.btn-pequeno {
  width: auto; min-width: 0; flex: 0 0 auto; white-space: nowrap;
  border: 1px solid #c8d3e5; background: white; color: #1a2744;
  border-radius: 8px; font-size: 11px; font-weight: 600; padding: 5px 10px; cursor: pointer;
  margin: 0 0 6px 6px;
}
.btn-pequeno:disabled { border-color: #e2e8f0; color: #a3b0c6; cursor: default; }
.btn-pequeno-rojo { border-color: #C0392B; color: #C0392B; }
.btn-pequeno-rojo:disabled { border-color: #e2e8f0; color: #a3b0c6; }

.prog-opciones { display: flex; flex-wrap: wrap; margin: 0 0 -6px -6px; }
.prog-opcion {
  width: auto; min-width: 0; flex: 0 0 auto;
  border: 1px solid #c8d3e5; background: white; color: #64748b;
  border-radius: 12px; font-size: 12px; font-weight: 500; padding: 5px 11px; cursor: pointer;
  margin: 0 0 6px 6px;
}
.prog-opcion-elegida { background: #185FA5; border-color: #185FA5; color: white; }
.prog-opcion:disabled { opacity: .55; cursor: default; }

.prog-fila { display: flex; flex-wrap: wrap; margin-left: -12px; }
.prog-campo-inline { display: block; margin: 8px 0 0 12px; }
.prog-campo-corto { max-width: 160px; }
.prog-check {
  display: flex; align-items: center; font-size: 12px; color: #1a2744; margin-top: 12px;
}
.prog-check input { margin-right: 8px; }
/* El formulario entero apagado mientras se guarda: los campos ya llevan su `:disabled`, y esto es
   la pista visual de que la caja completa está retenida. */
.prog-form-bloqueado { opacity: .6; }
.prog-botonera { display: flex; }
.prog-botonera .button { margin-left: 6px; }
.prog-botonera .button:first-child { margin-left: 0; }

.prog-tabla { width: 100%; border-collapse: collapse; font-size: 12px; margin: 0 0 8px; }
.prog-tabla th, .prog-tabla td {
  text-align: left; padding: 6px 16px; border-bottom: 1px solid #eef0f4; vertical-align: top;
}
.prog-tabla th { font-size: 10px; text-transform: uppercase; color: #7c8db0; letter-spacing: .4px; }
</style>
<script>
import Vue from 'vue';
import { unwrapDto } from '../../js/db/responseUtils.js';
import FactCobro from '../../js/db/FactCobro.js';
import Utils from '../../js/Utils.js';
import { registroDe } from '../../js/utils/operacionesAbonosCobros.js';
import PermisosFacturacion from './permisos-facturacion.js';
import SelloDatos from '../../components/SelloDatos.vue';
import { EventBus } from '../../js/EventBus.js';
import cerrojoDeLaSesion from '../../js/utils/cerrojoDeLaSesion.js';

/**
 * Los nombres de los cuatro datos manuales **salen del adaptador** y no se escriben aquí: son parte
 * del contrato con la API (`FactCobroDatoManualEnum`), y una cadena mal puesta en la pantalla se
 * vería como un 400 al guardar y no antes.
 */
const DATOS = FactCobro.DATOS_MANUALES;

/** Cómo se rotula cada dato manual en los avisos y en las etiquetas de las casillas. */
const ROTULOS = {};
ROTULOS[DATOS.POEDD] = 'POEDD';
ROTULOS[DATOS.COBRO_REAL] = 'COBRO REAL';
ROTULOS[DATOS.DEPOSITO] = 'DEPÓSITO';
ROTULOS[DATOS.TOTAL_DEPOSITO] = 'TOTAL DEPÓSITO';

/** De qué campo del DTO del día sale el valor guardado de cada dato manual. */
const CAMPOS = {};
CAMPOS[DATOS.POEDD] = 'poedd';
CAMPOS[DATOS.COBRO_REAL] = 'cobroReal';
CAMPOS[DATOS.DEPOSITO] = 'deposito';
CAMPOS[DATOS.TOTAL_DEPOSITO] = 'totalDeposito';

/** El primer año de la pestaña, el primero que trajo la carga del histórico (#168). */
const PRIMER_ANIO = 2020;

/**
 * Dos importes son el mismo si valen lo mismo, aunque no se escriban igual: `1200` y `1200.00` son
 * la misma cifra. Es la misma comparación que hace el servidor, y es lo que evita que pulsar
 * «Guardar» sin haber cambiado nada mande peticiones.
 */
function mismoImporte(uno, otro) {
  if (uno === null || uno === undefined || uno === '') return otro === null || otro === undefined || otro === '';
  if (otro === null || otro === undefined || otro === '') return false;
  return Number(uno) === Number(otro);
}

/**
 * Cómo se nombra un mes. Es el mismo par año-mes con el que el adaptador arma el nombre del fichero
 * (`control-diario-2026-09.pdf`), que es lo que de verdad choca cuando dos exportaciones del mismo
 * mes vuelan a la vez.
 */
function claveDeMes(anio, mes) {
  return anio + '-' + (mes < 10 ? '0' + mes : String(mes));
}

/**
 * **La identidad de una exportación**: ni la pantalla que la pidió, ni el instante. Y son DOS
 * nombres, no uno, porque las dos cosas que se apuntan de ella no se reparten igual:
 *
 * - `fichero`: **el mes**, que es de lo que está hecho el nombre del fichero
 *   (`control-diario-2026-10.pdf`). Lo que no puede haber dos veces a la vez es una escritura de ese
 *   fichero, y el fichero es uno por dispositivo: va sin la sesión **a propósito**, para que cerrar
 *   sesión y entrar otra persona no deje dos escrituras del mismo fichero pisándose.
 * - `aviso`: **la sesión y el mes**. Un fallo es información para una persona, así que lleva la
 *   sesión dentro —y no al lado, para filtrar después—: el aviso de otra sesión no se nombra igual,
 *   así que no se puede leer por descuido ni hay que acordarse de comprobarlo.
 *
 * O sea: quien entre después puede ver el botón apagado de un mes que se está escribiendo, porque eso
 * es verdad y dura lo que dura; lo que no verá nunca es el resultado de la exportación de otro.
 *
 * El sello de sesión lo da el adaptador (`FactCobro.sesionActual`), que es el mismo que decide si una
 * respuesta sigue siendo de quien la pidió: con dos lecturas distintas de la sesión —una aquí y otra
 * allí— habría dos vigilantes que no se hablan, y eso ya costó cuatro rondas en el PR #1759.
 */
function laExportacionDe(sesion, anio, mes) {
  const delMes = claveDeMes(anio, mes);
  return { fichero: delMes, aviso: sesion + '|' + delMes };
}

/**
 * **EL REGISTRO DE EXPORTACIONES, FUERA DE LA PANTALLA** (#173, decisión técnica del 07/10/2026).
 *
 * Vive en el módulo y no en `data()`, y eso es el arreglo: la ruta de esta pantalla no lleva
 * `keepAlive`, así que salir al menú **destruye el componente** y volver a entrar crea otro. Con el
 * registro dentro de la instancia, la instancia nueva nacía con el mapa vacío y dejaba pedir otra vez
 * el PDF de un mes que seguía en vuelo: las dos respuestas llegan a `FileUtils.openFile` con el mismo
 * nombre de fichero y en la aplicación móvil se escriben sobre la misma entrada temporal.
 *
 * El bloqueo y el fallo pertenecen a **la operación**, que dura lo que dura —petición, escritura y
 * apertura del fichero—, y no a quien la mira. De ahí las dos cosas que hace este registro:
 *
 * - `enVuelo`: qué ficheros se están escribiendo. Solo los suelta `terminaLaExportacion`, y solo
 *   cuando la promesa del adaptador se resuelve o se rompe, que es **después** de escribir y abrir el
 *   fichero. Ni el cambio de mes, ni recargar, ni destruir la pantalla terminan nada.
 * - `fallos`: por qué no salió el PDF, por cuenta y mes, para que la pantalla que esté viva en ese
 *   momento —la que lo pidió o la que se abra después— lo enseñe al volver a ese mes.
 *
 * Es `Vue.observable` para que las instancias que se recrean no solo lo lean, sino que **reaccionen**:
 * el botón se apaga y el aviso aparece sin que la pantalla nueva tenga que preguntar por su cuenta.
 *
 * Lo que NO guarda: nada de lo que se teclea. Los datos manuales son de la instancia y de su
 * borrador, y sobrevivir a la pantalla los haría reaparecer donde nadie los escribió.
 */
const exportaciones = Vue.observable({ enVuelo: {}, fallos: {} });

/** Apunta la exportación como en vuelo y se lleva el aviso del intento anterior de ESE mes. */
function empiezaLaExportacion(operacion) {
  Vue.set(exportaciones.enVuelo, operacion.fichero, true);
  Vue.delete(exportaciones.fallos, operacion.aviso);
}

/**
 * **El único sitio que suelta el bloqueo**, con el motivo si no salió. Uno solo para que no haya un
 * camino —el error, el reintento, la sesión caducada— que lo libere sin apuntar el por qué o que se
 * olvide de liberarlo: lo llaman las dos ramas de la misma promesa y nadie más.
 */
function terminaLaExportacion(operacion, motivo) {
  Vue.delete(exportaciones.enVuelo, operacion.fichero);
  if (motivo) Vue.set(exportaciones.fallos, operacion.aviso, motivo);
}

/**
 * **LA IDENTIDAD DE UN ENVÍO DE CORREO** (#547, P2 Codex ronda 2), con la misma forma que la de la
 * exportación y por el mismo motivo: la ruta de esta pantalla no lleva `keepAlive`, así que salir al
 * menú la destruye y volver a entrar monta otra con su `data()` nuevo. Con el bloqueo dentro de la
 * instancia, navegar fuera y volver mientras el `POST` seguía en vuelo dejaba pulsar «Enviar
 * reporte» otra vez: **dos correos de verdad a los mismos buzones**, que con una conexión lenta no
 * cuesta ningún esfuerzo provocar.
 *
 * Son dos nombres porque las dos cosas que se apuntan no se reparten igual, y ninguno de los dos se
 * reparte como el de la exportación —que va por fichero, sin la sesión, porque el fichero del
 * dispositivo es uno—:
 *
 * - `cerrojo`: **la sesión**, sin el mes. Lo que no puede haber es dos correos saliendo a la vez de
 *   la misma persona, sea del mes que sea: cada petición manda un correo de verdad, y el aviso de
 *   arriba habla de un envío. Es el mismo alcance que tenía el indicador de la instancia, que
 *   apagaba el botón también al cambiar de mes; lo que cambia es que ahora sobrevive a la pantalla.
 *   Y va por sesión y no por dispositivo justo al revés que el fichero: que otra cuenta mande su
 *   informe del mismo mes a quien ella elija es legítimo, y bloquearla no protegería nada.
 * - `aviso`: **la sesión y el mes**, para que el resultado del envío de octubre no aparezca encima
 *   de septiembre ni se lo lea quien entre después.
 */
function elEnvioDe(sesion, anio, mes) {
  return { cerrojo: sesion, aviso: sesion + '|' + claveDeMes(anio, mes) };
}

/**
 * **EL REGISTRO DE ENVÍOS, FUERA DE LA PANTALLA** (#547, P2 Codex ronda 2).
 *
 * Mismo criterio que `exportaciones`, porque el envío tiene el mismo ciclo: una petición que dura lo
 * que dura y una pantalla que puede no estar viva cuando conteste. Son tres piezas y la tercera es
 * de la ronda 3.
 *
 * - `enVuelo`: si esa sesión tiene un correo saliendo. Solo lo suelta `terminaElEnvio`, y solo
 *   cuando la promesa del adaptador se resuelve o se rompe. Ni el cambio de mes, ni recargar, ni
 *   destruir la pantalla terminan nada.
 * - `avisos`: cómo acabó el último envío de esa sesión y ese mes. Está aquí y no en `data()` por la
 *   otra mitad del mismo fallo: con el aviso en la instancia, quien salía al menú y volvía no veía
 *   ni que el correo había salido ni que no, así que volvía a pulsar —y ese sí es el segundo correo
 *   que llega al buzón—.
 * - `inciertos`: los envíos de los que **no se sabe si el correo salió**. Ver
 *   `comoAcaboElEnvio`: es lo que impide que un resultado que no acredita nada se presente como un
 *   fallo y se vuelva a mandar a ciegas.
 *
 * Lo que NO guarda: a quién se le manda ni lo que se teclea en el diálogo. Eso es de la instancia,
 * y sobrevivir a la pantalla lo haría reaparecer donde nadie lo eligió.
 */
const envios = Vue.observable({ enVuelo: {}, avisos: {}, inciertos: {} });

/** Apunta el envío como en vuelo y se lleva el aviso del intento anterior de ESE mes. */
function empiezaElEnvio(operacion) {
  Vue.set(envios.enVuelo, operacion.cerrojo, true);
  Vue.delete(envios.avisos, operacion.aviso);
}

/**
 * **El único sitio que suelta el cerrojo del envío**, y el único que apunta cómo acabó. Uno solo,
 * por lo mismo que en la exportación: que no haya un camino —el fallo, el reintento— que lo libere
 * sin decir qué pasó o que se olvide de liberarlo.
 *
 * Y es también **el único que retiene un mes del que no se sabe si el correo salió**: la retención
 * se deduce del aviso que ya se está apuntando (`aviso.incierto`), no la decide quien llama. Un
 * camino nuevo que apunte un resultado incierto retiene el mes aunque se olvide de retenerlo.
 */
function terminaElEnvio(operacion, aviso) {
  Vue.delete(envios.enVuelo, operacion.cerrojo);
  if (aviso) Vue.set(envios.avisos, operacion.aviso, aviso);
  if (aviso && aviso.incierto) Vue.set(envios.inciertos, operacion.aviso, true);
}

/** Si esa sesión tiene un correo saliendo ahora mismo, del mes que sea. */
function hayEnvioEnVuelo(sesion) {
  return !!envios.enVuelo[sesion];
}

/** Si de ese mes hay un envío del que no se sabe si salió, y por eso no se repite solo. */
function hayEnvioIncierto(aviso) {
  return !!envios.inciertos[aviso];
}

/**
 * **El único sitio que levanta la retención de un mes incierto**, y solo cuando una persona lo pide
 * a sabiendas: el botón «Mandarlo otra vez de todas formas», que dice en su aviso que puede llegar
 * una segunda copia. No la levanta el paso del tiempo, ni cambiar de mes, ni recargar la pantalla.
 *
 * El aviso se queda: lo que se ha decidido es volver a mandar, no que el primer correo no saliera.
 */
function asumirElRiesgoDeRepetir(operacion) {
  Vue.delete(envios.inciertos, operacion.aviso);
}

/**
 * **Al cerrar sesión se tiran los avisos de la que se va** (ronda 2 del 07/10/2026).
 *
 * El sello de sesión ya no los deja leer a quien entre después —lleva dentro la generación del
 * cierre, ver `FactCobro.sesionActual`—, pero no leerse no es lo mismo que no estar: un registro
 * que vive en el módulo se queda con el aviso de cada sesión que pasó por el dispositivo. Esto es
 * la mitad que el sello no hace, y es la misma limpieza que hace la pantalla de los boletines de
 * festivos al cambiar de sesión.
 *
 * Se tiran **solo los avisos**, no `enVuelo`: cerrar sesión no termina ninguna petición, y el
 * fichero del mes que se está escribiendo en el dispositivo sigue escribiéndose. Soltar ahí el
 * bloqueo dejaría pedir otra vez ese mes con el primero en vuelo, que es exactamente lo que se
 * cerró en la ronda 2 anterior. Lo suelta `terminaLaExportacion` y nadie más.
 *
 * Los avisos del correo (#547) se van por lo mismo y en el mismo sitio, y su cerrojo se queda por
 * lo mismo: cerrar sesión no para el `POST` que ya salió, y soltarlo dejaría mandar el informe otra
 * vez con el primero en vuelo.
 *
 * Y con los avisos se va **la retención de los meses inciertos**, que va por la misma clave que
 * ellos —sesión y mes—: la sesión que entre después no la ve de ninguna manera, así que dejarla
 * puesta no retiene nada y solo deja basura del que se fue. No es el caso del cerrojo, que va por
 * sesión sin el mes y sí aguanta algo: el `POST` que sigue saliendo.
 */
EventBus.$on('app:session:close', function() {
  Object.keys(exportaciones.fallos).forEach(function(aviso) {
    Vue.delete(exportaciones.fallos, aviso);
  });
  Object.keys(envios.avisos).forEach(function(aviso) {
    Vue.delete(envios.avisos, aviso);
  });
  Object.keys(envios.inciertos).forEach(function(aviso) {
    Vue.delete(envios.inciertos, aviso);
  });
});

/**
 * El texto del fallo de una exportación (#173).
 *
 * Es del módulo y no un método porque se calcula cuando la petición contesta, y entonces la pantalla
 * que la pidió puede estar destruida: el motivo pertenece a la operación y no depende de ninguna
 * instancia viva.
 */
/**
 * Los correos de un campo escrito a mano: separados por comas, sin blancos y sin los vacíos que deja
 * una coma de más. Mismo criterio que el informe de abonos abiertos.
 */
function correosDe(texto) {
  if (!texto) return [];
  return String(texto).split(',')
    .map(function(correo) { return correo.trim(); })
    .filter(function(correo) { return !!correo; });
}

/**
 * **El registro de las operaciones de esta pantalla que todavía no se sabe cómo acabaron**, guardado
 * en el navegador.
 *
 * Es el mecanismo del informe de abonos abiertos (`operacionesAbonosCobros.js`), con **su propio
 * prefijo de almacén**: los apuntes de las dos pantallas no se ven entre ellos, igual que en el
 * servidor sus claves no se cruzan porque el módulo va dentro.
 *
 * POR QUÉ SE COMPARTE EN VEZ DE COPIARSE. Porque la copia es lo que falló. La clave del alta vivía
 * **solo en `prog.form`**, así que una recarga, una pestaña cerrada o un «ir atrás» a mitad del
 * guardado se llevaban por delante lo único que permitía preguntar después qué había pasado: al
 * volver, el formulario se recreaba con `claveAlta: null`, generaba otra y el servidor ya no podía
 * reconocer el reintento —dos programaciones iguales mandando el informe dos veces—. Ese es el
 * tercer borde de la misma familia que la revisión automática sacó en tres rondas seguidas, y aquí
 * estaba resuelto con su decisión de dirección detrás (#193, 15/10/2026).
 *
 * El segundo parámetro es `null` a propósito: esta pantalla nunca guardó operaciones en el navegador,
 * así que no hay ninguna versión anterior que convertir.
 */
const operacionesPendientes = registroDe('factcobro-control-diario-operaciones:', null);

/**
 * Cuánto tiene que llevar fuera un intento para que un «no me consta» del servidor sea **el final** y
 * no «todavía no me consta».
 *
 * Los mismos 20 segundos que el informe de abonos abiertos, y por lo mismo: un `POST` puede estar
 * todavía de camino cuando se pregunta, y ahí «no consta» no acredita nada. Tratarlo como final
 * dejaría repetir el alta mientras la primera va llegando.
 */
const MARGEN_HASTA_QUE_PUDO_LLEGAR = 20000;

/** Las dos situaciones en las que el servidor acredita que **no se ha escrito nada**. */
const SITUACIONES_SIN_EFECTO = ['NO_CONSTA', 'RETOMABLE'];

/** Quién está delante. La clave de una operación es suya: ver `operacionesPendientes`. */
function usuarioDeAhora() {
  const usuario = Utils.getUser();
  return (usuario && usuario.username) || null;
}

/**
 * **Si ese fallo acredita que el servidor no escribió nada.**
 *
 * Solo lo acredita un `4xx` que no sea de los que se cierran sin respuesta: eso es «te he rechazado
 * y no he aplicado nada». Un `5xx`, o un fallo sin estado, no dice nada de lo que pasó dentro —la
 * escritura puede haber entrado y haberse caído el servidor al contestar—, y tratarlo como un
 * rechazo soltaría la clave con la que después hay que preguntar.
 *
 * Es la misma regla que el registro del servidor aplica para soltar una clave, escrita aquí una sola
 * vez: con la decisión repartida por cada `catch`, cada camino acaba afirmando por su cuenta que no
 * se hizo nada, que es exactamente la avería que costó dos rondas en el informe de abonos abiertos.
 */
function seAcreditaQueNoSeEscribio(err) {
  const estado = err && err.status;
  if (!estado || TIMEOUTS_CON_ENVIO_QUIZA_HECHO.indexOf(estado) > -1) return false;
  return estado >= 400 && estado < 500;
}

/** El texto de §9 cuando el navegador no puede guardar la clave del alta: qué, por qué y dónde. */
const NO_SE_PUDO_APUNTAR_EL_ALTA = 'No se ha dado de alta, y es a propósito: este navegador no ha '
  + 'podido guardar el identificador de la operación —el almacenamiento está lleno, bloqueado o en '
  + 'modo privado—. Sin él, si la respuesta se perdiera no se podría saber después si la '
  + 'programación quedó creada, y volver a intentarlo crearía dos iguales. Libera espacio del '
  + 'navegador o sal del modo privado, y vuelve a pulsar «Guardar».';

/**
 * **Por qué esa hora no vale**, o `null`.
 *
 * Del módulo y no un método porque la miran dos sitios —el aviso que va junto al campo (§9) y la
 * puerta de «Guardar»— y tienen que decir lo mismo.
 */
function motivoDeLaHora(hora) {
  const limpia = (hora || '').trim();
  if (!limpia) {
    // El campo vacío también es un aviso, y no «ningún problema»: un `type="time"` se puede dejar
    // vacío borrando su contenido, y con esto a nulo el botón «Guardar» quedaba habilitado y mandaba
    // `hora: ''` para que la API lo rechazara después. Un estado incompleto del formulario se dice
    // en el formulario (§9), no tras una petición fallida.
    return 'Pon la hora a la que tiene que salir, en formato HH:MM —por ejemplo 08:00—.';
  }
  if (!/^\d{1,2}:\d{2}$/.test(limpia)) {
    return 'La hora tiene que ir en formato HH:MM, por ejemplo 08:00.';
  }
  const minutos = Number(limpia.split(':')[1]);
  if (minutos % FactCobro.PASO_MINUTOS === 0) return null;
  return 'La hora tiene que ir de ' + FactCobro.PASO_MINUTOS + ' en ' + FactCobro.PASO_MINUTOS
    + ' minutos —08:00, 08:05, 08:10…—: el envío automático solo pasa cada '
    + FactCobro.PASO_MINUTOS + ' minutos, así que una programación a las 08:03 saldría a las '
    + '08:00, antes de la hora que pone.';
}

/**
 * **Por qué ese día del mes no vale**, o `null`.
 *
 * Y lo primero que se mira es que sea un **entero**, que es lo que faltaba: el campo es
 * `type="number"` con `v-model.number`, así que escribir `1,5` o `1.5` deja un número con decimales
 * en el formulario. La validez nativa por `step` no salva nada —el botón llama al método, no manda
 * un formulario—, así que «Guardar» quedaba habilitado, se mandaba `diaMes: 1.5` y lo rechazaba la
 * API. Un formulario que la propia pantalla sabe que no vale se dice en el formulario (§9).
 */
function motivoDelDiaDelMes(valor) {
  const dia = Number(valor);
  if (!Number.isFinite(dia) || !Number.isInteger(dia)) {
    return 'El día del mes tiene que ser un número entero, sin decimales: el día 1, el 15… no hay '
      + 'medio día.';
  }
  if (dia < 1 || dia > FactCobro.DIA_MES_MAXIMO) {
    return 'El día del mes tiene que estar entre 1 y ' + FactCobro.DIA_MES_MAXIMO + ', para que '
      + 'exista también en febrero.';
  }
  return null;
}

/** **Por qué ese día de la semana no vale con «solo días laborables»**, o `null`. */
function motivoDelFinDeSemana(form) {
  const finDeSemana = ['SABADO', 'DOMINGO'].indexOf(form.diaSemana) > -1;
  if (!finDeSemana || !form.soloLaborables) return null;
  return 'Una programación en sábado o en domingo no puede ser de «solo días laborables»: no '
    + 'saldría ningún día. Quita la marca o elige otro día.';
}

/**
 * **LOS CAMPOS DE UNA PROGRAMACIÓN, EN UN SOLO SITIO**: cómo se llama cada uno para la API, cuándo
 * aplica, qué valor viaja y por qué no valdría.
 *
 * ESTO ES LA PUERTA DE «GUARDAR», Y POR ESO ES UNA TABLA. La revisión automática sacó la misma
 * avería en dos rondas, cada vez en un campo distinto: la **hora vacía** (ronda 1) y el **día del mes
 * con decimales** (ronda 3), las dos veces con «Guardar» habilitado mandando algo que la API rechaza.
 * Eran un `if` por campo escrito a mano, así que el campo siguiente volvía a quedarse fuera. Con la
 * tabla, de aquí salen **las dos cosas** —el motivo que apaga el botón y el cuerpo que se manda
 * (`cuerpoDeLaProgramacion`)—, de modo que un campo que viaje sin regla no puede existir: tendría que
 * añadirse a esta tabla para llegar a la petición.
 *
 * `aplica` es lo que hace que la frecuencia no necesite ramas: el día de la semana solo cuenta en una
 * SEMANAL y el día del mes solo en una MENSUAL, y lo que no aplica viaja a `null` —que es lo que la
 * API espera— y no se valida.
 */
const CAMPOS_DE_LA_PROGRAMACION = [
  {
    campo: 'nombre',
    valor: function(form) { return (form.nombre || '').trim(); },
    motivo: function(valor) {
      return valor ? null : 'Ponle un nombre: es con lo que se reconoce en la lista de arriba.';
    }
  },
  {
    campo: 'frecuencia',
    valor: function(form) { return form.frecuencia; },
    motivo: function(valor) {
      return FactCobro.FRECUENCIAS.indexOf(valor) > -1 ? null
        : 'Elige cada cuánto tiene que salir: diaria, semanal o mensual.';
    }
  },
  {
    campo: 'hora',
    valor: function(form) { return (form.hora || '').trim(); },
    motivo: motivoDeLaHora
  },
  {
    campo: 'diaSemana',
    aplica: function(form) { return form.frecuencia === 'SEMANAL'; },
    valor: function(form) { return form.diaSemana; },
    motivo: function(valor, form) {
      if (FactCobro.DIAS_SEMANA.indexOf(valor) < 0) return 'Elige el día de la semana.';
      return motivoDelFinDeSemana(form);
    }
  },
  {
    campo: 'diaMes',
    aplica: function(form) { return form.frecuencia === 'MENSUAL'; },
    valor: function(form) { return Number(form.diaMes); },
    motivo: motivoDelDiaDelMes
  },
  {
    campo: 'soloLaborables',
    valor: function(form) { return !!form.soloLaborables; },
    motivo: function() { return null; }
  },
  {
    campo: 'activa',
    valor: function(form) { return !!form.activa; },
    motivo: function() { return null; }
  }
];

/** **Por qué no se puede guardar lo que hay escrito**, o `null`. El primer campo que no cuadra. */
function motivoDeLosCampos(form) {
  for (let i = 0; i < CAMPOS_DE_LA_PROGRAMACION.length; i++) {
    const campo = CAMPOS_DE_LA_PROGRAMACION[i];
    if (campo.aplica && !campo.aplica(form)) continue;
    const motivo = campo.motivo(campo.valor(form), form);
    if (motivo) return motivo;
  }
  return null;
}

/**
 * **El cuerpo que se manda**, compuesto con la misma tabla que decide si se puede mandar.
 *
 * Lo que no aplica viaja a `null` y no al valor que haya quedado en el formulario: una regla DIARIA
 * no lleva día de la semana, y mandarlo haría que un cambio de frecuencia dejara dentro el día de la
 * anterior.
 *
 * Los destinatarios entran aparte porque no salen del formulario sino de cruzarlo con la lista que el
 * diálogo está enseñando (`progDestinatarios`): es lo que impide mandar a alguien que no está
 * delante.
 */
function cuerpoDeLaProgramacion(form, destinatarios) {
  const cuerpo = { id: form.id, visto: form.visto };
  CAMPOS_DE_LA_PROGRAMACION.forEach(function(campo) {
    cuerpo[campo.campo] = !campo.aplica || campo.aplica(form) ? campo.valor(form) : null;
  });
  cuerpo.grupos = destinatarios.grupos;
  cuerpo.personas = destinatarios.personas;
  cuerpo.emails = destinatarios.emails;
  return cuerpo;
}

/**
 * **La clave de un alta de programación** (#549), la que viaja en el parámetro `operacion` de la URL.
 *
 * Es lo que permite reintentar un alta sin duplicarla: con ella el servidor reconoce el reintento y
 * contesta la regla que ya creó, en vez de crear otra igual que mandaría el informe por duplicado en
 * cada ejecución. Se genera una vez por alta, se **guarda en el navegador antes de lanzar el
 * `POST`** (`operacionesPendientes`) y se suelta cuando el alta se resuelve.
 *
 * Lo que hace falta no es que sea criptográfica: es que **no se repita** entre dos altas distintas.
 * Aun así no se usa el pseudoaleatorio del lenguaje —Sonar lo marca como punto de seguridad
 * (javascript:S2245) y aquí no hace falta pasar por ahí—: `crypto.randomUUID` cuando está, que es
 * el caso de todos los navegadores que soporta la ERP sobre HTTPS; `crypto.getRandomValues` cuando
 * no; y como último recurso, el reloj más un contador de esta pestaña, que no vuelve atrás.
 *
 * Es de este fichero y no se importa la de «Abonos abiertos», que es local de su pantalla: lo que
 * se comparte de verdad —la cabecera y qué garantiza— está en `FactCobro.js`, que es por donde pasan
 * las dos.
 */
let altasPedidas = 0;

function nuevaClaveDeAlta() {
  const cripto = typeof crypto !== 'undefined' ? crypto : null;
  if (cripto && typeof cripto.randomUUID === 'function') {
    return cripto.randomUUID();
  }
  if (cripto && typeof cripto.getRandomValues === 'function') {
    const octetos = cripto.getRandomValues(new Uint8Array(16));
    let hex = '';
    for (let i = 0; i < octetos.length; i++) {
      hex += ('0' + octetos[i].toString(16)).slice(-2);
    }
    return 'alta-' + hex;
  }
  altasPedidas += 1;
  // El `performance.now()` entra para que dos pestañas abiertas en el mismo milisegundo no saquen
  // la misma: su origen de tiempos es el de cada pestaña, no el del reloj.
  const dePestana = typeof performance !== 'undefined' && performance.now
    ? Math.trunc(performance.now() * 1000).toString(36)
    : '0';
  return 'alta-' + Date.now().toString(36) + '-' + dePestana + '-' + altasPedidas;
}

/**
 * El texto del fallo de un envío (#547).
 *
 * Los rechazos de la propia petición —no hay destinatarios, un grupo que no existe, una persona sin
 * correo en su ficha— los escribe la API con el motivo dentro, y se enseñan tal cual: son los que
 * tienen arreglo en el propio diálogo. El 403 se traduce aquí porque el servidor no puede saber qué
 * botón se pulsó.
 */
function motivoDelFalloDelEnvio(err) {
  if (err && err.code === FactCobro.CODIGO_PESTANA_CERRADA && err.message) {
    return err.message;
  }
  if (err && err.status === 403) {
    return 'Mandar este informe por correo es solo de ADMIN y DIRECTOR.';
  }
  return err && err.message ? err.message
    : 'El informe no se ha podido enviar. Vuelve a intentarlo.';
}

/**
 * El texto de un fallo del diálogo de **Programar reporte** (#549).
 *
 * Los rechazos de la API se enseñan tal cual y son casi todos: dicen qué hora no vale, qué persona
 * no se puede resolver, o que la regla la ha cambiado alguien mientras la tenías abierta. Esos son
 * los que tienen arreglo en el propio formulario, así que resumirlos los convertiría en un «ha
 * habido un problema» que obliga a adivinar. El 403 se traduce aquí porque el servidor no puede
 * saber desde qué diálogo se pulsó.
 *
 * Aquí no hay «no se sabe si se ha aplicado» como en el envío, y es por el tipo de operación, no
 * por descuido: esto escribe en la base de datos, y ninguna de las cinco escrituras puede hacer el
 * trabajo dos veces. La edición, el interruptor y el borrado van **condicionados a lo que la
 * pantalla tenía delante**, así que un reintento sobre una regla que ya cambió no pisa nada y lo
 * dice; y el alta, que no tiene nada delante que repetir, lleva su clave (`nuevaClaveDeAlta`), de la
 * que sale el `_id` de la regla. Un correo, en cambio, no se puede «des-enviar», y por eso ese
 * camino sí distingue.
 */
function motivoDeLaProgramacion(err, porOmision) {
    if (err && err.code === FactCobro.CODIGO_PESTANA_CERRADA && err.message) {
        return err.message;
    }
    if (err && err.status === 403) {
        return 'Programar el envío de este informe es solo de ADMIN y DIRECTOR.';
    }
    return err && err.message ? err.message : porOmision;
}

/**
 * El formulario de programación como recién abierto.
 *
 * Está aquí, en una sola función, porque lo usan tres sitios —el estado inicial, «Limpiar» y la
 * huella con la que se compara si se ha tocado— y con tres copias la siguiente pareja de campos que
 * se añada se quedaría fuera de una de ellas.
 */
function formProgramacionNuevo() {
  return {
    id: null,
    /** El `modificadaEn` que se tenía delante al abrir la edición, en milisegundos. */
    visto: null,
    /**
     * La clave con la que viaja un alta, que **se conserva entre reintentos**: es lo que impide que
     * un «Guardar» cuya respuesta se pierde acabe creando dos reglas iguales. Se saca al pedir el
     * alta y se suelta cuando el alta sale bien; una edición no la usa.
     */
    claveAlta: null,
    nombre: '',
    frecuencia: 'DIARIA',
    hora: '08:00',
    diaSemana: 'LUNES',
    diaMes: 1,
    soloLaborables: true,
    elegidosGrupos: [],
    elegidosPersonas: [],
    emails: '',
    activa: true
  };
}

/**
 * **Lo que el formulario de programación dice ahora mismo**, en una cadena que se puede comparar
 * (P2 Codex ronda 2).
 *
 * Es lo que permite saber si hay un borrador a medio escribir antes de sustituirlo al pulsar
 * «Editar» en otra ficha. Se compara una cadena del formulario entero y no una bandera por campo:
 * una bandera hay que acordarse de ponerla en cada `v-model` nuevo, y lo que falló fue justamente un
 * olvido.
 *
 * El `id` y el `visto` entran —editar otra regla es un cambio— pero la `claveAlta` **no**: es la
 * clave de la petición, no algo que nadie haya escrito, y con ella dentro un alta a medias cambiaría
 * de huella al pulsar «Guardar» sin que el borrador haya cambiado.
 */
function huellaDelFormProgramacion(form) {
  if (!form) return '';
  return JSON.stringify([
    form.id || null, form.visto || null, (form.nombre || '').trim(), form.frecuencia, form.hora,
    form.diaSemana, Number(form.diaMes), !!form.soloLaborables,
    (form.elegidosGrupos || []).slice().sort(), (form.elegidosPersonas || []).slice().sort(),
    (form.emails || '').trim(), !!form.activa
  ]);
}

/**
 * Los estados con los que `DatabaseModel.sendRequest` avisa de que **la petición se cerró sin
 * respuesta y el servidor pudo haberla aplicado** (ver su `errorCallback`), incluido el **408**, que
 * es un `4xx` y aun así no es un rechazo.
 *
 * La lista es la de allí a propósito, y es la misma que mira el informe de abonos abiertos: si cada
 * pantalla decidiera por su cuenta, una acabaría llamando rechazo a un 408 y dejaría repetir un
 * correo que pudo haber salido.
 */
const TIMEOUTS_CON_ENVIO_QUIZA_HECHO = [0, 408, 504, 520, 522, 524, 598, 599];

/**
 * **CÓMO ACABÓ UN ENVÍO, DECIDIDO EN UN SOLO SITIO** (#547, P2 Codex ronda 3).
 *
 * Es la garantía de una familia que la revisión automática ha sacado tres rondas seguidas, cada vez
 * por un sitio distinto: **un resultado que no acredita que el correo no salió se presentaba como un
 * fallo, y un fallo invita a volver a pulsar**. La ronda 1 lo sacó en la API —un corte del SMTP
 * después de transmitir el mensaje contado como «no ha salido»—; la ronda 2, con el cerrojo que no
 * sobrevivía a la pantalla; y la ronda 3, con cualquier rechazo soltando el cerrojo, incluido el
 * `status === 0` que es justo el que no se puede interpretar.
 *
 * Parchear cada sitio no la cierra, porque la decisión estaba repartida: la tomaba el `catch` de
 * turno. Ahora la toma esta función y nadie más, y los dos inciertos que existen —el que la API
 * acredita con su código y el que no llega a tener respuesta— salen por aquí con la misma marca.
 *
 * Lo que la marca `incierto` provoca está en `terminaElEnvio`: el mes queda retenido y «Enviar
 * reporte» no vuelve a mandar nada hasta que una persona lo pida a sabiendas. **No hace seguro el
 * reintento** —para eso haría falta una clave de operación que el servidor no duplique, como la que
 * tiene «Abonos abiertos», y eso no es de esta tarea—: lo que hace es no invitar a repetirlo
 * creyendo que no salió.
 */
function comoAcaboElEnvio(err) {
  // La API lo acredita con su código: el mensaje de correo llegó a entregarse al SMTP y la conexión
  // se cortó después, así que Office365 pudo haberlo aceptado. Lo dice el servidor porque es el
  // único que lo sabe; la pantalla no lo deduce del texto.
  if (err && err.code === FactCobro.CODIGO_ENVIO_INCIERTO) {
    return { incierto: true, texto: motivoDelFalloDelEnvio(err) };
  }
  // Y aquí no hay respuesta que leer: la petición se cerró antes de traerla. El `POST` pudo llegar
  // entero y el correo pudo salir; lo que se ha perdido es lo que el servidor contestó.
  if (err && TIMEOUTS_CON_ENVIO_QUIZA_HECHO.indexOf(err.status) > -1) {
    return {
      incierto: true,
      texto: 'No se sabe si el informe ha salido: la petición se cerró sin respuesta y el servidor '
        + 'pudo haber mandado el correo. Comprueba en el buzón de los destinatarios si ha llegado '
        + 'ANTES de volver a mandarlo.'
    };
  }
  return { incierto: false, texto: motivoDelFalloDelEnvio(err) };
}

/**
 * **CÓMO ACABÓ UN «GUARDAR» DE LA PROGRAMACIÓN** (P1 Codex ronda 2), decidido en un solo sitio y
 * con la misma lista de estados que el envío.
 *
 * El caso que importa es el **alta**: si el `POST` se cierra sin respuesta —un 408, un 504, un
 * `status === 0`— el servidor pudo haber creado la regla, así que presentarlo como un fallo invita a
 * cambiar la hora o los destinatarios y volver a pulsar. Y eso, con la clave del alta ya gastada, no
 * puede acabar bien: el servidor devuelve la regla del primer intento si el contenido coincide y
 * rechaza por clave reutilizada si no, de modo que el formulario modificado o se anuncia como
 * guardado sin serlo o se queda sin salida. Lo que se hace está en `guardarRegla`: el formulario
 * queda retenido tal como se mandó, y las dos salidas son repetirlo igual o descartarlo a sabiendas.
 *
 * Una **edición** incierta no retiene nada: va condicionada al `visto`, así que si la primera entró,
 * el reintento no escribe nada y el servidor contesta que se recargue. Lo que cambia es el texto,
 * que no dice «no se ha guardado» de algo que pudo guardarse.
 *
 * La lista de estados es la misma que la del envío a propósito: con una lista por camino, uno
 * acabaría llamando rechazo a un 408.
 */
function comoAcaboLaProgramacion(err, eraEdicion) {
  if (err && TIMEOUTS_CON_ENVIO_QUIZA_HECHO.indexOf(err.status) > -1) {
    return {
      incierto: true,
      texto: eraEdicion
        ? 'No se sabe si el cambio se ha guardado: la petición se cerró sin respuesta y el '
          + 'servidor pudo haberlo aplicado. Cierra el diálogo y vuelve a abrirlo para ver cómo ha '
          + 'quedado la programación antes de volver a cambiarla.'
        : 'No se sabe si la programación se ha creado: la petición se cerró sin respuesta y el '
          + 'servidor pudo haberla guardado. Mira la lista de arriba: si ya está, pulsa «Descartar '
          + 'este alta». Si no está, pulsa «Reintentar» —se manda tal cual y el servidor NO la '
          + 'duplica—. Hasta entonces el formulario se queda como se mandó: cambiarlo ahora y '
          + 'reintentar no se podría guardar.'
    };
  }
  return { incierto: false, texto: motivoDeLaProgramacion(err, eraEdicion
    ? 'La programación no se ha podido guardar.'
    : 'La programación no se ha podido dar de alta.') };
}

function motivoDelFallo(err) {
  // La pestaña cerrada (#169) trae su propia explicación; se enseña tal cual, igual que en la
  // carga. Y la pantalla no se vacía: lo que hay en ella sigue siendo válido, lo único que no
  // ha salido es el documento.
  if (err && err.code === FactCobro.CODIGO_PESTANA_CERRADA && err.message) {
    return err.message;
  }
  if (err && err.status === 403) {
    return 'El PDF de esta pantalla es solo de ADMIN y DIRECTOR.';
  }
  return err && err.message ? err.message : 'El PDF no se ha podido generar. Vuelve a intentarlo.';
}

export default {
  components: { SelloDatos },
  data: function() {
    return {
      cargando: false,
      error: null,
      // El aviso de pestaña cerrada (#169), tal como lo manda el servidor. Separado de `error`
      // porque no es lo mismo: `error` invita a reintentar y esto no se arregla reintentando.
      pestanaCerrada: null,
      /**
       * **La relectura que va después de «Guardar» no ha podido traer el mes, y había cifras
       * tecleadas sin guardar** (#215). Separado de `error` porque es el caso contrario: `error`
       * acompaña a una pantalla que se queda sin cifras, y esto acompaña a una pantalla que sigue
       * entera a propósito.
       *
       * Antes ese fallo entraba por el camino de `error`, que vacía `mes`: desaparecía la tabla y
       * con ella las casillas donde estaban las cifras que no habían entrado, mientras el aviso
       * naranja seguía diciendo «lo que no ha entrado sigue escrito en su casilla». Y lo único que
       * se ofrecía era el botón de recargar, que **tira el borrador**, así que había que teclearlo
       * todo otra vez.
       */
      errorRelectura: null,
      anio: null,
      numeroMes: null,
      mes: null,
      /**
       * **Cuándo se leyó lo que la tabla está enseñando.** Lo pone `aplicarMes`, que es la única
       * puerta por la que entran cifras del servidor, así que vale igual para la carga, para la
       * relectura de un lote y para el recálculo de un objetivo.
       *
       * Existe por el diálogo de envío: el correo lo compone el servidor **al mandarlo**, leyendo lo
       * que consta guardado en ese momento, así que entre esta lectura y el envío otra persona puede
       * haber guardado una cifra o recalculado un objetivo. La pantalla no puede evitarlo —releer
       * antes de enviar no es una opción, porque el GET del mes recalcula el mes en curso
       * (D-GUARDADO) y enviar no escribe nada—, pero sí puede decir de cuándo es lo que se está
       * viendo y cómo asegurarse: ver `avisoCifrasAlEnviar` (P2 Codex ronda 5).
       */
      leidoEn: null,
      aniosDisponibles: [],
      /** Las tres columnas manuales del bloque de cobro, en el orden en que van en la tabla. */
      datosDeCobro: [DATOS.POEDD, DATOS.COBRO_REAL, DATOS.DEPOSITO],
      /** La cuarta, que va al final de la fila. */
      datoTotalDeposito: DATOS.TOTAL_DEPOSITO,
      /**
       * Si el detalle de la línea «Registrado fuera de mes» está desplegado (#289). Empieza plegado:
       * la línea se entiende sin abrirla —importe, cuántos documentos y de qué mes son— y el detalle
       * es para comprobarla documento a documento.
       */
      detalleFueraDeMesAbierto: false,
      /**
       * Cuántas columnas tiene la tabla, para el `colspan` de la línea de registrado fuera de mes y
       * de su detalle. Si se añade una columna hay que subirlo, y por eso está aquí y no escrito en
       * el `colspan`: en el atributo sería un número suelto en medio del marcado.
       */
      COLUMNAS: 12,
      /**
       * **El estado de cada casilla manual**, por `fecha|dato`. Es el único sitio donde vive lo
       * que esta pantalla sabe de una casilla: lo tecleado y sin guardar, y los avisos sobre ella.
       *
       * Cada entrada:
       *
       * - `clave`, `fecha`, `dato`: de qué casilla habla.
       * - `pendiente`: si hay algo tecleado sin guardar. Es un campo propio y no `valor != null`,
       *   porque `valor` puede ser `null` a propósito —dejar la casilla en blanco es un cambio—.
       * - `valor`, `valorVisto`: lo tecleado, y el importe que había en la casilla cuando se
       *   empezó a editar. El `valorVisto` es el guardado condicional del servidor y tiene que ser
       *   el que se vio, no el que haya llegado entre medias.
       * - `fallo`: el último guardado de esta casilla no entró, con el motivo que dio el servidor
       *   (#164).
       * - `conflicto`: lo que hay guardado ahora en esa casilla, cuando ha cambiado por debajo de
       *   lo que se vio al empezar a teclear.
       * - `exito`: el último guardado de esta casilla entró.
       *
       * **Por qué están juntos y no en tres listas aparte.** Lo estaban, y el aviso se quedaba
       * puesto cuando la casilla dejaba de tener nada pendiente: deshacer una cifra apagaba el
       * botón de guardar y seguía diciendo que había un dato sin guardar, hasta recargar a mano
       * (#170, ronda 4, opción A de la nota de diseño). Colgando el aviso de la casilla, los
       * avisos de la cabecera son **derivados** —ver los `computed` de abajo— y una casilla que
       * se queda sin nada pendiente se lleva su aviso sin que haya que acordarse en cada camino.
       *
       * **Y solo lo escribe `fijarCasilla`**, que es donde viven las dos reglas de qué sobrevive.
       */
      casillas: {},
      guardando: false,
      /**
       * **La sesión con la que se abrió esta pantalla**, y con la que se nombran sus exportaciones.
       * El sello lo da el adaptador, que es quien decide si una respuesta sigue siendo de quien la
       * pidió; aquí solo se guarda.
       *
       * Se resuelve una vez, al crear la instancia, y no en cada `computed`: una pantalla pertenece a
       * la sesión con la que se abrió, y leer `localStorage` dentro de un `computed` no se recalcula
       * cuando cambia —no es reactivo— así que daría la ilusión de seguir la sesión sin seguirla.
       * Quien entra después con otra cuenta abre una pantalla nueva, y esa nace con la suya.
       */
      sesion: FactCobro.sesionActual(),
      /**
       * Cuál de los dos objetivos se está recalculando ahora mismo, o `null` (#249). Es **uno a la
       * vez** a propósito, aunque los dos botones sean independientes: cada recálculo contesta con
       * el mes entero releído, así que con dos en vuelo la respuesta que llegara última podría
       * haberse leído antes de que el otro escribiera, y la pantalla se quedaría enseñando la cifra
       * y la fecha de antes del que sí terminó. Mientras uno corre, el otro botón sale apagado y
       * dice por qué.
       */
      objetivoEnCurso: null,
      /**
       * Por magnitud, el motivo del último recálculo que **no** llegó a guardarse. El servidor
       * manda el motivo y aquí solo se enseña: pulsar un botón y que no pase nada es el fallo
       * callado que #164 no admite.
       */
      fallosObjetivo: {},
      /**
       * El aviso del último «Guardar» que **no habla de ninguna casilla**: el caso en que todo lo
       * que había se deshizo antes de salir, así que no se guardó nada. Lo demás no se guarda aquí:
       * se deriva del estado de las casillas.
       */
      avisoDelLote: null,
      /**
       * **El diálogo de «Enviar reporte»** (#547): a quién, con qué asunto y qué se ha podido leer de
       * la lista de destinatarios.
       *
       * `elegidosGrupos` y `elegidosPersonas` llevan **códigos de grupo y usernames**, no correos: el
       * buzón lo resuelve la API en el momento del envío, así que un alta, una baja o un correo
       * corregido después de abrir el diálogo ya cuentan. Es la misma razón por la que la pantalla no
       * suma ni formatea cifras.
       */
      envio: {
        abierto: false,
        cargandoDestinatarios: false,
        errorDestinatarios: null,
        /**
         * Por qué el diálogo ha quitado algo de lo que estaba elegido, o `null`. Lo pone
         * `descartarLoQueNoSeVe` y se enseña junto a los destinatarios.
         */
        descartados: null,
        grupos: [],
        notaGrupos: null,
        elegidosGrupos: [],
        elegidosPersonas: [],
        emails: '',
        emailsCc: '',
        asunto: '',
        error: null,
        /**
         * **Nº de lectura de la lista de destinatarios**, para descartar la que llega tarde (P2
         * Codex ronda 2). Mismo mecanismo que `peticionEnCurso` con el mes, y hace falta por lo
         * mismo: la lista se relee cada vez que se abre el diálogo, así que cerrarlo y volver a
         * abrirlo antes de que la primera conteste deja dos lecturas en vuelo escribiendo el mismo
         * estado. Sin numerarlas, un fallo tardío de la primera vaciaba la lista que la segunda
         * acababa de pintar —y se llevaba la elección con ella—, y en el orden contrario dejaba una
         * lista antigua al lado del error de la lectura vigente.
         */
        lecturaEnCurso: 0
      },
      /**
       * **El diálogo de «Programar reporte»** (#549): las reglas que hay, el formulario de la que
       * se está dando de alta o editando, y el historial de lo que ha salido.
       *
       * Está aparte de `envio` y no dentro, aunque las dos cosas elijan destinatarios con los
       * mismos chips, porque son dos operaciones distintas con dos ciclos de vida: una manda un
       * correo ahora y la otra escribe una regla. Con el estado compartido, cerrar un diálogo
       * dejaría a medias el otro —y la elección de uno se vería dentro del otro—.
       *
       * Lo que sí es lo mismo es **de dónde sale la lista de grupos y personas**: la misma lectura
       * (`FactCobro.destinatariosInforme`), por `leerDestinatarios`. No hay dos listas.
       *
       * `form.emails` es texto separado por comas, como en el diálogo de envío; `elegidosGrupos` y
       * `elegidosPersonas` llevan códigos de grupo y `username`s, nunca correos: el buzón lo
       * resuelve la API en cada envío.
       */
      prog: {
        abierto: false,
        cargando: false,
        /** El fallo de la lectura de la lista de reglas, que deja el diálogo sin nada que enseñar. */
        error: null,
        /** Lo último que salió bien: «guardada», «borrada», «pausada». Se va al volver a tocar. */
        aviso: null,
        lista: [],
        guardando: false,
        /** El fallo del último «Guardar», «Pausar» o «Eliminar», con el motivo de la API. */
        errorForm: null,
        /**
         * **Un alta de la que no se sabe si quedó creada**, con su explicación, o `null` (P1 Codex
         * ronda 2).
         *
         * Mientras esto esté puesto, el formulario está retenido tal como se mandó: ver
         * `progFormRetenido` y `altaInciertaPendiente`. La clave del alta ya puede estar gastada en
         * el servidor, así que lo único que se puede hacer con ella es **repetir exactamente la
         * misma petición** —y entonces el servidor contesta la regla que creó, sin duplicarla— o
         * descartarla a sabiendas. Modificar la hora o los destinatarios y reintentar con esa clave
         * no es ninguna de las dos cosas: el servidor rechazaría el cambio por clave reutilizada y
         * el formulario se quedaría sin salida.
         */
        altaIncierta: null,
        cargandoDestinatarios: false,
        errorDestinatarios: null,
        descartados: null,
        grupos: [],
        notaGrupos: null,
        /** Nº de lectura de la lista de destinatarios, para descartar la que llega tarde. */
        lecturaEnCurso: 0,
        /**
         * Nº de lectura de la **lista de reglas**, para descartar la que llega tarde (P2 Codex
         * ronda 2).
         *
         * Mismo mecanismo que `lecturaEnCurso` y hace falta por lo mismo: la lista se relee al
         * abrir el diálogo y después de cada escritura, así que cerrarlo y volver a abrirlo —o
         * guardar mientras la primera lectura viene por el camino— deja dos GET en vuelo
         * escribiendo el mismo estado. Sin numerarlas, una respuesta antigua que llegue al final
         * reemplaza la lista recién cargada, o borra su error, y entonces el diálogo enseña reglas
         * que ya se han editado o borrado.
         */
        lecturaLista: 0,
        /** Lo mismo para el historial de envíos, por el mismo motivo. */
        lecturaEnvios: 0,
        /**
         * **El formulario tal como se cargó**, en texto, para saber si se ha tocado (P2 Codex
         * ronda 2).
         *
         * Lo escribe `recordarFormProgramacion` cada vez que el formulario se carga o se limpia, y
         * lo compara `progFormModificado`. Se guarda una **copia del formulario entero** y no una
         * lista de banderas por campo porque así un campo nuevo entra solo: con banderas, el
         * siguiente que se añada se quedaría fuera y «Editar» volvería a tirar un borrador en
         * silencio.
         *
         * Y es una copia del objeto, no su huella en texto, porque hay dos cambios que los hace **la
         * propia pantalla** y no quien está delante —la propuesta de los grupos por omisión y la
         * poda de lo que ya no está en la lista—: esos se aplican también aquí, para que no cuenten
         * como borrador. Con la huella ya calculada no se podrían aplicar, y entonces abrir el
         * diálogo dejaría el formulario «modificado» de entrada y la pregunta de «Editar» saldría
         * siempre, que es la manera de que deje de significar nada.
         */
        formOriginal: formProgramacionNuevo(),
        /**
         * **Nº de sesión del formulario**: el estado único que gobierna abrir, recuperar y guardar
         * (decisión de @Donchurru del 19/10/2026, punto 2).
         *
         * Sube **una sola vez y en un solo sitio** —`recordarFormProgramacion`, por el que pasan los
         * tres caminos que cargan el formulario: traer una regla a editar, limpiarlo y traer un
         * borrador recuperado—. Quien lanza algo que después va a escribir en el formulario se queda
         * con el número de antes y, al volver, solo escribe si sigue siendo el mismo.
         *
         * Hace falta porque las respuestas no llegan en orden ni vienen de una sola ventana: la
         * consulta del alta recuperada tarda, y en ese rato quien está delante puede haber empezado
         * otra, haberse puesto a editar una regla o haber resuelto el alta desde otra pestaña. Sin
         * esto, la respuesta tardía sustituía el formulario entero por el borrador antiguo —y con él
         * su clave—, así que lo que se guardaba después no era lo que había en pantalla.
         */
        sesionForm: 0,
        /**
         * **Si queda una recuperación sin resolver**: desde que se abre el diálogo hasta que la
         * consulta del alta pendiente contesta (o se sabe que no hay ninguna).
         *
         * Mientras esté puesto no se puede dar de alta nada (`motivoNoSePuedeDecidirUnAlta`). Es lo
         * que cierra la carrera de verdad: la consulta corre en paralelo con las dos listas, y si
         * estas contestaban antes el botón «Guardar» se encendía y se podía crear un alta nueva
         * mientras la anterior estaba todavía sin saberse —dos reglas iguales, el informe dos veces—.
         */
        recuperando: false,
        /**
         * **El cuerpo con el que se comprometió el alta**, tal cual se mandó, atado a su clave
         * (decisión de @Donchurru del 19/10/2026, punto 3).
         *
         * Un reintento tiene que mandar **esto** y no recomponerlo del catálogo de ahora: el catálogo
         * cambia —alguien deja la empresa, un grupo se retira— y con un destinatario de menos el
         * reintento ya no es la misma petición, así que el servidor lo recibe como la misma clave con
         * otro contenido y contesta 409. Lo que se pidió se conserva aunque ya no se pueda elegir hoy.
         *
         * Se guarda también en el apunte del navegador, que es lo que lo trae de vuelta tras un F5.
         */
        cuerpoComprometido: null,
        /**
         * **El apunte del navegador de ese alta**, tal cual se escribió: la entrada entera
         * (`ambito`, `usuario`, `accion`, `clave`, `borrador`, `cuerpo`, `intentadaEn`).
         *
         * Se guarda porque **el apunte se puede perder sin que la operación se haya resuelto**, y
         * entonces hay que poder reponerlo: dos pestañas comparten el almacén y comparten la clave
         * de un alta recuperada, así que si una termina bien y la olvida mientras la otra sigue en
         * duda, el apunte que la segunda necesita ya no está —y un `clear()` del navegador hace lo
         * mismo—. Sin la entrada no se puede reponer: `reapuntar` necesita el cuerpo y el borrador
         * originales, y recomponerlos del catálogo de ahora es justo lo que no se puede hacer
         * (decisión de @Donchurru, punto 3). Lo repone `retenerAltaIncierta`.
         */
        apunteDelAlta: null,
        /**
         * **Si la clave de ese alta no está guardada en el navegador** y no se ha podido reponer
         * (almacenamiento lleno, bloqueado o modo privado).
         *
         * No se puede arreglar desde la pantalla —la petición ya salió— pero sí se puede decir
         * antes de que alguien recargue, que es cuando se perdería: §9, junto a los dos botones que
         * la resuelven. Mismo aviso que el de `sinApuntar` en el informe de abonos.
         */
        altaSinApunte: false,
        /**
         * **EL ALTA QUE SIGUE APUNTADA EN EL NAVEGADOR**, o `null`: el espejo del almacén del que
         * cuelga la puerta de «Guardar».
         *
         * ESTO ES LO QUE CIERRA LA FAMILIA, Y NO UN AVISO MÁS. Hasta aquí, lo que impedía crear un
         * alta mientras otra estaba sin resolver era `altaIncierta`, **puesto a mano por cada
         * camino**. Y la revisión automática ha ido sacando, ronda tras ronda, el camino que se
         * olvidaba de ponerlo: «Reintentar» lo soltaba antes de mandar, la rama incierta lo dejaba
         * solo en memoria, y la respuesta de la recuperación que no se puede aplicar —porque
         * entretanto alguien escribió en el formulario— se iba sin ponerlo. Siempre el mismo
         * defecto: **un olvido ABRE la puerta**, porque la protección era una afirmación y no una
         * consecuencia.
         *
         * Aquí la puerta cuelga del apunte, que es el dato que ningún camino puede dejar a medias:
         * se escribe ANTES de lanzar la petición y solo se borra con la prueba de que la operación
         * acabó. Así un olvido ya no abre nada —deja el apunte donde estaba y la puerta cerrada—, y
         * el camino que se añada mañana hereda la protección sin acordarse de ella.
         *
         * Es un espejo y no una lectura directa porque de esto cuelga un `computed`: en Vue 2 el
         * almacén del navegador no es reactivo. Lo refrescan las TRES únicas puertas por las que
         * esta pantalla escribe en el almacén —`apuntarElAlta`, `olvidarElAlta` y
         * `reponerElApunteDelAlta`— más la apertura del diálogo y **el aviso del navegador cuando
         * escribe otra pestaña** (`escucharElRegistroDeOtrasPestanas`). Esto último hace falta
         * porque las cuatro primeras son cosas que pasan aquí dentro, y el almacén es del navegador:
         * con el diálogo ya abierto, un alta apuntada en otra pestaña no movía este espejo y la
         * puerta se quedaba abierta (P1 Codex 19/10/2026).
         */
        altaPendienteEnElNavegador: null,
        envios: { lista: [], error: null },
        form: formProgramacionNuevo()
      },
      // Nº de petición: descarta las respuestas que llegan tarde, para que dos cambios de mes
      // seguidos no dejen en pantalla el primero por encima del segundo.
      peticionEnCurso: 0
    };
  },
  mounted: function() {
    // Lo que otra pestaña apunte o suelte en el almacén entra por aquí: ver
    // `escucharElRegistroDeOtrasPestanas`.
    this.escucharElRegistroDeOtrasPestanas();
  },
  beforeDestroy: function() {
    this.dejarDeEscucharElRegistroDeOtrasPestanas();
  },
  computed: {
    /**
     * Si la pantalla está enseñando cifras ahora mismo. Gobierna el sello de datos, y es una sola
     * condición y no una lista de estados a los que esconderlo, para que el estado nuevo que se
     * añada mañana no se olvide de apagarlo (mismo criterio que la Tarjeta B).
     */
    hayCifras: function() {
      return !!this.mes && this.mes.existe === true && !this.cargando;
    },
    /**
     * Qué dice el sello de los datos que hay en pantalla.
     *
     * «En vivo» mientras la pantalla enseñe lo que acaba de servir el servidor, que es lo normal:
     * esta pantalla no pasa por caché. Y «no se sabe de cuándo son» cuando la relectura de un
     * «Guardar» no ha podido traer el mes (#215): la tabla se queda a propósito —es donde están
     * escritas las cifras que no entraron— pero lo que enseña es de antes del lote, así que un
     * «Datos en vivo» justo encima del aviso que dice que no se ha podido releer sería la pantalla
     * contradiciéndose a sí misma. No se esconde el sello, se degrada: una pantalla que se queda
     * callada sobre la frescura de sus cifras es lo que #16 no admite.
     */
    estadoDelSello: function() {
      return this.errorRelectura ? 'desconocido' : 'vivo';
    },
    etiquetaMes: function() {
      return (this.mes && this.mes.etiquetaMes) || '—';
    },
    /**
     * Si hay una escritura de esta pantalla en vuelo: un lote de datos manuales o el recálculo de un
     * objetivo (#249). Es lo que apaga el recargar y las flechas de mes.
     *
     * Es una sola condición y no una lista repetida en cada sitio, por lo mismo que `hayCifras`:
     * así la escritura que se añada mañana no se olvida de apagarlos. Y apagarlos importa porque las
     * dos respuestas traen el mes entero: cambiando de mes a mitad, lo que llega habla del mes que
     * ya no está en pantalla.
     */
    ocupado: function() {
      return this.guardando || !!this.objetivoEnCurso;
    },
    /**
     * Si hay que avisar de que al mes en curso le falta la hora de recálculo. Se exige que el
     * servidor mande el texto: sin él no hay nada que enseñar, y escribirlo aquí sería inventarse
     * un motivo que esta pantalla no conoce.
     */
    recalculoSinSello: function() {
      return !!this.mes && this.mes.enCurso === true && !this.mes.recalculadoEn
        && !!this.mes.notaRecalculo && !this.cargando;
    },
    /**
     * El texto del aviso de junto a «Recalculado», o `null` si no hay que avisar de nada. Manda
     * `notaRefresco` —el último refresco de este mes no ha escrito (#1205)—, que el servidor solo
     * envía cuando eso pasa y vale también con una hora antigua en la cabecera; si no, la nota del
     * guion del mes en curso, como hasta ahora. Cuando no hay hora los dos traen el mismo texto, así
     * que nunca se enseñan dos avisos del mismo hecho.
     */
    avisoRecalculo: function() {
      if (!this.mes || this.cargando) {
        return null;
      }
      if (this.mes.notaRefresco) {
        return this.mes.notaRefresco;
      }
      return this.recalculoSinSello ? this.mes.notaRecalculo : null;
    },
    facturasARevisar: function() {
      return (this.mes && this.mes.facturasARevisar) || [];
    },
    /**
     * El aviso de que lo que se enseña no recoge todo lo que la consulta ha contado para el mes
     * (#295), o `null` cuando no hay ningún descuadre, que es el caso normal.
     *
     * Lo decide el servidor y aquí no se deduce de ninguna resta: la cifra del mes **es** la suma
     * de los días más la línea de registrado fuera de mes, así que restarlas en la pantalla daría
     * siempre cero y el descuadre de verdad —una factura que cuenta para el mes y no cae en ningún
     * día— no se ve en ninguna de las dos cifras.
     *
     * Es una sola condición y no la misma comprobación repetida en los dos sitios que lo pintan,
     * por lo mismo que `ocupado`: así los dos se encienden y se apagan juntos y no puede quedar el
     * TOTAL marcado como incompleto sin el aviso que lo explica.
     */
    descuadre: function() {
      return (this.mes && this.mes.descuadre) || null;
    },
    /**
     * Si la pantalla está enseñando la tabla del día a día, que es lo único que cambia de los
     * textos del aviso: sin tabla no hay TOTAL del que hablar, y una nota que lo nombrara mandaría
     * a comparar con un pie que no se dibuja.
     *
     * Lo decide el servidor con `sinDetalleDiario` y nada más —la misma condición del `v-if`, no
     * una deducida a partir de los días—, y vive aquí para que el aviso de arriba, el de abajo y su
     * nota no puedan responder cada uno una cosa.
     */
    hayTabla: function() {
      return !!this.mes && !this.mes.sinDetalleDiario;
    },
    notaFacturasARevisar: function() {
      return this.mes && this.mes.notaFacturasARevisar;
    },
    /** Los dos bloques de resumen, con el ancho de su barra ya resuelto. */
    resumenes: function() {
      const self = this;
      if (!this.mes) return [];
      // Las claves son solo el `:key` del `v-for`, y van en minúscula a propósito: en mayúscula se
      // confundirían con los nombres del contrato (`FactCobro.DATOS_MANUALES` y las magnitudes de
      // la API), que no se escriben a mano en esta pantalla.
      return [
        { clave: 'facturacion', dto: this.mes.resumenFacturacion, color: '#185FA5' },
        { clave: 'cobro', dto: this.mes.resumenCobro, color: '#BA7517' }
      ].filter(function(b) { return !!b.dto; }).map(function(b) {
        return {
          clave: b.clave,
          color: b.color,
          etiqueta: b.dto.etiqueta,
          objetivo: b.dto.objetivo,
          conseguido: b.dto.conseguido,
          falta: b.dto.falta,
          porcentaje: b.dto.porcentaje,
          motivoNoCalculable: b.dto.motivoNoCalculable,
          anchoBarra: self.anchoBarra(b.dto.porcentaje),
          // Lo del objetivo a botón (#249). `magnitud` es lo que se manda para pedir el recálculo y
          // viene del servidor: en la pantalla no se escribe ningún nombre del contrato. Y
          // `recalculable` tampoco se deduce aquí de `migrado` o de `existe`: lo decide el servicio
          // que escribe, y repetirlo aquí acabaría dibujando un botón que al pulsarlo da un error.
          magnitud: b.dto.magnitud,
          sinCalcular: b.dto.sinCalcular === true,
          notaSinCalcular: b.dto.notaSinCalcular,
          calculadoEn: b.dto.calculadoEn,
          calculadoPor: b.dto.calculadoPor,
          recalculable: b.dto.recalculable === true,
          motivoNoRecalculable: b.dto.motivoNoRecalculable,
          recalculando: self.objetivoEnCurso === b.dto.magnitud,
          fallo: self.fallosObjetivo[b.dto.magnitud] || null,
          // El aviso de descuadre es de la cifra de FACTURACIÓN y de ninguna otra (#295): el cobro
          // real no sale de ese reparto por días. Va en el bloque y no fuera para que se pinte
          // pegado a la cifra que está corta.
          descuadre: b.clave === 'facturacion' ? self.descuadre : null
        };
      });
    },
    desgloses: function() {
      if (!this.mes) return [];
      return [this.mes.desgloseFacturacion, this.mes.desgloseCobro]
        .filter(function(d) { return !!d; })
        .map(function(d) {
          return {
            etiqueta: d.etiqueta,
            lineas: d.lineas || [],
            total: d.total,
            motivoNoCalculable: d.motivoNoCalculable,
            diasLaborables: d.diasLaborables,
            objetivoDiario: d.objetivoDiario,
            calculadoEn: d.calculadoEn,
            calculadoPor: d.calculadoPor
          };
        });
    },
    /**
     * Las casillas que tienen algo tecleado sin guardar, en el orden en que entraron. Es lo que
     * manda el botón «Guardar», y sale del estado de las casillas: una casilla que ya no tiene
     * nada pendiente no está aquí, sin que nadie tenga que quitarla de una lista.
     */
    pendientes: function() {
      const self = this;
      return Object.keys(this.casillas)
        .map(function(clave) { return self.casillas[clave]; })
        .filter(function(casilla) { return casilla.pendiente; });
    },
    cuantosSinGuardar: function() {
      return this.pendientes.length;
    },
    /**
     * Los datos del último «Guardar» que no entraron (#164), **derivados** del estado de cada
     * casilla. Al ser derivados, deshacer una cifra retira su aviso: ya no hay ningún dato sin
     * guardar del que avisar.
     */
    erroresGuardado: function() {
      const self = this;
      return Object.keys(this.casillas)
        .map(function(clave) { return self.casillas[clave]; })
        .filter(function(casilla) { return !!casilla.fallo; })
        .map(function(casilla) {
          return {
            clave: casilla.clave,
            fecha: casilla.fecha,
            columna: self.rotulo(casilla.dato),
            motivo: casilla.fallo.motivo
          };
        });
    },
    /**
     * Las casillas que tenían algo tecleado y cuyo valor guardado ha cambiado por debajo. No es un
     * fallo, es un aviso: la cifra tecleada sigue escrita, pero lo que había cuando se empezó a
     * teclear ya no es lo que hay. Decirlo es lo mismo que exige #164 para un guardado que no entra.
     */
    basesMovidas: function() {
      const self = this;
      return Object.keys(this.casillas)
        .map(function(clave) { return self.casillas[clave]; })
        .filter(function(casilla) { return !!casilla.conflicto; })
        .map(function(casilla) {
          return {
            clave: casilla.clave,
            fecha: casilla.fecha,
            columna: self.rotulo(casilla.dato),
            guardado: casilla.conflicto.guardado
          };
        });
    },
    /**
     * El aviso verde de lo que se ha guardado, contado sobre **las casillas que siguen con su
     * guardado en pie**: volver a teclear en una de ellas la saca de aquí, que es lo que pidió
     * dirección —«una edición nueva retira el éxito anterior»— y lo que evita decir que algo está
     * guardado encima de una casilla que ya no lo está.
     *
     * Con algún fallo no se dice nada: el aviso naranja es el que hay que leer, y un «2 datos
     * guardados» al lado se lee como que todo fue bien.
     */
    avisoGuardado: function() {
      const self = this;
      if (this.erroresGuardado.length) return null;
      const entraron = Object.keys(this.casillas)
        .filter(function(clave) { return self.casillas[clave].exito; }).length;
      if (entraron === 1) return 'Dato guardado.';
      if (entraron > 1) return entraron + ' datos guardados.';
      return this.avisoDelLote;
    },
    /**
     * Las casillas que tienen **texto a medias**: lo que se ve escrito y no está en el borrador
     * porque el navegador no lo devuelve (`badInput`, el `12.` de `editar`).
     *
     * Es la única cosa de esta pantalla que vive solo en el DOM, así que es la única que un
     * repintado puede perder. Derivado del estado de cada casilla, como los demás avisos: la tecla
     * que completa la cifra lo retira sin que ningún camino tenga que acordarse.
     */
    casillasAMedias: function() {
      const self = this;
      return Object.keys(this.casillas)
        .map(function(clave) { return self.casillas[clave]; })
        .filter(function(casilla) { return !!casilla.aMedias; })
        .map(function(casilla) {
          return {
            clave: casilla.clave,
            fecha: casilla.fecha,
            columna: self.rotulo(casilla.dato)
          };
        });
    },
    /**
     * Si hay algo escribiéndose que una escritura se llevaría por delante.
     *
     * **Es lo que impide empezar una escritura**, y está en un solo sitio por lo mismo que
     * `ocupado`: los dos controles que escriben —«Guardar» y los dos «Recalcular»— lo miran de
     * aquí, así que el que se añada mañana no se olvidará. Y no se trata de una validación de la
     * cifra: lo que protege es que las dos escrituras acaban **repintando la tabla** —el guardado
     * apaga las casillas y relee el mes, el recálculo contesta con el mes entero—, y ese repintado
     * sustituye el texto a medias por el último valor apuntado. Antes se perdía en silencio, que
     * era el fallo por los dos caminos (P2 Codex #1777, rondas 2 y 3).
     */
    hayAlgoAMedias: function() {
      return this.casillasAMedias.length > 0;
    },
    /**
     * Qué hay que terminar de escribir y por qué eso apaga los botones, dicho **junto al control**
     * que no responde y con la casilla concreta: quien pulsa no relaciona un aviso del principio de
     * la pantalla con el botón que tiene debajo del dedo (dirección, 05/10/2026). Y lo desbloquea
     * quien está escribiendo, en esa misma casilla: no es un permiso de nadie.
     */
    /**
     * **Qué casillas están a medio escribir**, nombradas por su columna y su día. En un solo sitio
     * porque lo dicen dos avisos —el de «Guardar» y el de los dos botones que sacan el mes— y los
     * dos tienen que nombrar las mismas: con la lista compuesta en cada uno, el día que cambie el
     * rótulo de una columna solo cambiaría en uno.
     */
    cualesAMedias: function() {
      const self = this;
      return this.casillasAMedias.map(function(casilla) {
        return casilla.columna + ' del ' + self.formatFecha(casilla.fecha);
      }).join(', ');
    },
    porQueHayAlgoAMedias: function() {
      if (!this.hayAlgoAMedias) return null;
      const cuales = this.cualesAMedias;
      return 'Termina la cifra que estás escribiendo en ' + cuales + ': todavía no es un número '
        + '—le falta algún dígito detrás de la coma—. Se desbloquea en cuanto lo sea, o si borras '
        + 'la casilla; lo desbloqueas tú ahí mismo, no hace falta nadie más. Está apagado para no '
        + 'perder lo que estás escribiendo: tanto guardar como recalcular vuelven a pintar la '
        + 'tabla, y lo que todavía no es una cifra no se puede guardar.';
    },
    /**
     * Si «Guardar» se puede pulsar ahora mismo.
     *
     * Mira `ocupado` y no `guardando`: la exclusión con el recálculo de un objetivo (#249) tiene
     * que ser **simétrica**, porque las dos escrituras contestan el mes entero. Con solo
     * `guardando`, el botón de un objetivo se apagaba al guardar pero no al revés, así que guardar a
     * mitad de un recálculo dejaba las dos respuestas en vuelo: el GET final del guardado mueve
     * `peticionEnCurso`, lo que llegue después del recálculo se descarta por `esDeOtroMes`, y la
     * pantalla se queda enseñando el objetivo de antes aunque el servidor ya tenga guardado el
     * nuevo (P2 Codex #1777, ronda 1).
     *
     * Y tampoco con una casilla a medias: el lote apaga las casillas y relee el mes, así que el
     * texto que todavía no es una cifra se iría con el repintado (ver `hayAlgoAMedias`).
     */
    sePuedeGuardar: function() {
      return !this.ocupado && !this.hayAlgoAMedias && !this.cargando && this.cuantosSinGuardar > 0
        && !!this.mes && this.mes.editable === true;
    },
    /**
     * **La exportación del mes que está en pantalla, vista desde esta sesión**: sus dos nombres en el
     * registro del módulo. Es lo único que esta pantalla necesita para preguntar por ella.
     */
    operacionDelMes: function() {
      return laExportacionDe(this.sesion, this.anio, this.numeroMes);
    },
    /**
     * Si el PDF **del mes que está en pantalla** se está generando ahora mismo (#173): apaga el
     * botón y cambia su rótulo a «Generando…».
     *
     * Es por mes y no un «hay alguna exportación en vuelo» porque lo que no se puede repetir es el
     * mes: dos peticiones del mismo mes se pisan el fichero, y dos de meses distintos no —cada una
     * guarda el suyo—. Así, con el PDF de octubre en vuelo se puede ir a septiembre y exportarlo, y
     * al volver a octubre el botón sigue apagado y dice la verdad: aquello no ha terminado.
     *
     * Lo pregunta al registro del módulo y no a un dato propio: si lo tuviera propio, una pantalla
     * recreada —salir al menú y volver a entrar— nacería diciendo que no hay nada en vuelo.
     */
    exportando: function() {
      return !!exportaciones.enVuelo[this.operacionDelMes.fichero];
    },
    /**
     * El aviso de la exportación **del mes que está en pantalla**, o nada (#173).
     *
     * Derivado y no un dato que se escribe: así la respuesta de una exportación no tiene que
     * acertar si su mes se está viendo o no —no lo sabe, y cuando lo adivinaba con un contador se
     * equivocaba—. Apunta su fallo en el mes que exportó y es la pantalla viva la que enseña el del
     * mes que se está mirando, aunque no sea la misma pantalla que lo pidió.
     */
    errorExportar: function() {
      return exportaciones.fallos[this.operacionDelMes.aviso] || null;
    },
    /**
     * **El envío de correo del mes que está en pantalla, visto desde esta sesión** (#547): sus dos
     * nombres en el registro del módulo. Ver `elEnvioDe`.
     */
    envioDelMes: function() {
      return elEnvioDe(this.sesion, this.anio, this.numeroMes);
    },
    /**
     * Si hay un «Enviar reporte» **de esta sesión** en vuelo (#547): apaga los dos botones que
     * mandan —el de la cabecera y el del diálogo— y cambia su rótulo a «Enviando…».
     *
     * Lo pregunta al registro del módulo y no a un dato propio, que es lo que arregla el hallazgo de
     * la ronda 2: una pantalla recreada —salir al menú y volver a entrar— nacía diciendo que no
     * había nada en vuelo y dejaba mandar el mismo informe otra vez con el primer `POST` todavía
     * saliendo.
     *
     * Y es por sesión y no por mes, al contrario que `exportando`: lo que no puede haber es dos
     * correos de la misma persona saliendo a la vez. Ver `elEnvioDe`.
     */
    enviandoReporte: function() {
      return hayEnvioEnVuelo(this.envioDelMes.cerrojo);
    },
    /**
     * Cómo acabó el último envío **del mes que está en pantalla** y de esta sesión, o nada (#547).
     *
     * Derivado del registro del módulo por lo mismo que `errorExportar`: la respuesta del envío no
     * tiene por qué saber si su mes se está mirando —ni si queda alguna pantalla—, así que apunta en
     * su mes y lo enseña la pantalla que esté viva. Es lo que hace que salir al menú y volver no
     * borre la única señal de que el correo salió.
     */
    avisoEnvio: function() {
      return envios.avisos[this.envioDelMes.aviso] || null;
    },
    /**
     * Si de este mes hay un envío **del que no se sabe si el correo salió** (P2 Codex ronda 3).
     *
     * Mientras lo haya, «Enviar reporte» no manda: no porque haya nada en vuelo —ya terminó—, sino
     * porque repetirlo puede entregar una segunda copia del informe y aquí no hay nada que lo
     * impida. Lo levanta una persona a sabiendas: ver `repetirAunqueNoSeSepa`.
     *
     * Va por mes y no por sesión entera, al contrario que el cerrojo: lo que puede estar ya en un
     * buzón es el informe de ESE mes, y el de otro no tiene por qué esperar.
     */
    envioIncierto: function() {
      return hayEnvioIncierto(this.envioDelMes.aviso);
    },
    /**
     * Si se puede exportar a PDF ahora mismo (#173).
     *
     * **No depende de `editable`**: un mes migrado del Excel no admite datos tecleados y sí se
     * exporta —es justo para lo que dirección pidió el PDF, archivar meses cerrados—. Lo que sí hace
     * falta es que haya ficha de ese mes (`existe`) y que no quede nada tecleado sin guardar: ver
     * `motivoNoExportable`.
     *
     * Y va por `ocupado` y no por `guardando` suelto: lo que no puede haber es una escritura de
     * esta pantalla en vuelo, **cualquiera** de las dos. Con el recálculo de un objetivo a medias
     * (#249) el servidor puede tener ya la cifra nueva mientras la pantalla sigue enseñando la
     * vieja, y entonces el papel no sería lo que se está viendo —que es el criterio de la
     * incidencia—. Preguntando por `ocupado`, que es donde vive esa lista, la escritura que se
     * añada mañana tampoco se olvida de apagar el botón.
     *
     * Y tampoco con el mes sin releer (#215, `errorRelectura`), por ese mismo criterio: ahí la
     * tabla enseña lo de antes del último lote mientras el servidor ya tiene lo que sí entró, así
     * que el papel saldría **más fresco** que la pantalla. Es el mismo caso que el recálculo a
     * medias, con las dos cifras cambiadas de sitio.
     */
    sePuedeExportar: function() {
      return this.loQueSeVeEsLoGuardado && !this.exportando;
    },
    /**
     * **Si lo que hay en pantalla es exactamente lo que consta guardado**, que es la condición de las
     * dos salidas del mes: el PDF (#173) y el correo (#547).
     *
     * Está en un solo sitio porque es **una sola regla**, y los dos documentos los compone el
     * servidor leyendo lo guardado: cualquiera de los dos sacado con la pantalla descuadrada sería un
     * documento que no es lo que se está viendo. Repetida en cada botón, el día que se añada un
     * estado nuevo a la lista —ya ha pasado dos veces, con el recálculo a botón de la #249 y con el
     * mes sin releer de la #215— uno de los dos se queda sin él.
     *
     * Lo que NO entra aquí es «ese documento concreto está saliendo ahora mismo»: eso es de cada
     * botón, y son cosas distintas —se puede mandar el correo del mes mientras se genera el PDF de
     * otro—.
     *
     * **Y lo tecleado no es solo `cuantosSinGuardar`.** Una cifra a medio escribir —el `-` o el
     * separador decimal que `editar` no apunta porque todavía no es un número— se ve en la casilla y
     * no está en el borrador, así que el contador sigue a cero y el documento saldría sin ella: la
     * tercera vez que esta lista se queda corta, después del recálculo a botón (#249) y del mes sin
     * releer (#215), y la única de las tres en la que la pantalla enseña algo que el servidor no
     * tiene (P2 Codex ronda 5). Es el mismo estado que ya frena las dos escrituras —«Guardar» y los
     * dos «Recalcular»— y por eso se pregunta a `hayAlgoAMedias`, que es donde vive.
     */
    loQueSeVeEsLoGuardado: function() {
      return !this.cargando && !this.ocupado && !!this.mes && this.mes.existe === true
        && this.cuantosSinGuardar === 0 && !this.hayAlgoAMedias && !this.errorRelectura;
    },
    /**
     * Si se puede mandar el informe por correo ahora mismo (#547): la misma condición que el PDF,
     * porque el correo lo compone el servidor con la misma lectura, más que no haya otro envío en
     * vuelo —cada petición manda un correo de verdad—.
     *
     * **No depende de `editable`**, igual que el PDF: un mes migrado del Excel no admite datos
     * tecleados y sí se manda, que es justo para lo que dirección pide estos informes.
     *
     * Y tampoco se manda con un envío incierto de este mes sin resolver (`envioIncierto`): ahí no
     * hay nada en vuelo, lo que hay es un correo que pudo salir. Entra en esta condición y no solo
     * en el botón para que lo mire también `abrirEnvio`, que es el otro camino.
     */
    sePuedeEnviar: function() {
      return this.loQueSeVeEsLoGuardado && !this.enviandoReporte && !this.envioIncierto;
    },
    /**
     * Por qué no se puede sacar el mes —ni en PDF (#173) ni por correo (#547)—, cuando el motivo no
     * es «espera un momento». Los estados que se explican solos —cargando, guardando, y el propio
     * botón diciendo «Generando…» o «Enviando…»— no devuelven texto: el preloader y el rótulo ya lo
     * dicen, y un aviso que aparece y desaparece en medio segundo solo hace parpadear la cabecera.
     *
     * La espera del recálculo sí lleva texto, por lo mismo que en «Guardar» y en las casillas: es
     * la única que deja el botón apagado sin que nada en la pantalla diga por qué, y un control
     * apagado y mudo es el defecto que dirección registró el 05/10/2026.
     *
     * **Es uno para los dos botones** y habla de los dos documentos, porque la causa es la misma y
     * están uno al lado del otro: con un texto por botón, la cabecera enseñaba dos párrafos largos
     * que decían lo mismo cambiando el sustantivo, y eso se lee como dos problemas distintos.
     */
    motivoNoSaleElDocumento: function() {
      if (!this.mes || this.cargando || this.guardando) return null;
      if (this.objetivoEnCurso) {
        return 'Espera a que termine de calcularse ' + this.cualSeEstaCalculando + ', más abajo. El '
          + 'PDF y el correo los genera el servidor con lo que consta guardado, así que a mitad del '
          + 'cálculo traerían una cifra distinta de la que estás viendo. Se desbloquean solos al '
          + 'acabar.';
      }
      if (this.mes.existe !== true) {
        return 'De este mes no hay ficha en la pestaña FACTURACIÓN, así que no hay nada que '
          + 'exportar ni que enviar.';
      }
      // La cifra a medio escribir va ANTES de los datos sin guardar, y no al final: con una casilla
      // a medias «Guardar» también está apagado (ver `sePuedeGuardar`), así que decir «pulsa Guardar
      // antes» mandaría a un botón que tampoco responde. Lo primero es terminar la cifra.
      if (this.hayAlgoAMedias) {
        return 'Termina la cifra que estás escribiendo en ' + this.cualesAMedias + ': el PDF y el '
          + 'correo los genera el servidor con lo que consta guardado, y lo que todavía no es un '
          + 'número no se puede guardar, así que no saldría en ellos. Lo desbloqueas tú ahí mismo, '
          + 'acabando la cifra o borrando la casilla; no hace falta nadie más.';
      }
      if (this.cuantosSinGuardar > 0) {
        return 'El PDF y el correo los genera el servidor con lo que consta guardado, así que '
          + (this.cuantosSinGuardar === 1 ? 'el dato que tienes sin guardar no saldría'
            : 'los ' + this.cuantosSinGuardar + ' datos que tienes sin guardar no saldrían')
          + ' en ellos. Pulsa «Guardar» antes.';
      }
      // Y con el mes sin releer, aunque no quede nada tecleado (#215): los dos documentos los genera
      // el servidor con lo que consta guardado, y lo que está en pantalla es de antes del último
      // lote, así que dirían cosas distintas de la tabla. Lo desbloquea el botón de recargar.
      if (this.errorRelectura) {
        return 'Primero recarga la pantalla con el botón de arriba: no se ha podido releer el mes '
          + 'después de guardar, y el PDF y el correo los genera el servidor con lo que consta '
          + 'guardado, así que traerían unas cifras distintas de las que estás viendo.';
      }
      return null;
    },
    /** El mismo motivo, para el tooltip de «Exportar a PDF»: mientras genera, el botón ya lo dice. */
    motivoNoExportable: function() {
      return this.exportando ? null : this.motivoNoSaleElDocumento;
    },
    /**
     * Y para el de «Enviar reporte»: mientras sale, el botón ya lo dice.
     *
     * El envío incierto va aquí y **no** en `motivoNoSaleElDocumento`, que es el de los dos
     * botones: de un mes con un correo del que no se sabe si salió, el PDF se exporta igual —no
     * manda nada a nadie—. Y el texto no se escribe aquí: es el del aviso que la pantalla ya está
     * enseñando junto al botón, con la salida dentro, para no decir dos cosas distintas del mismo
     * envío.
     */
    motivoNoEnviable: function() {
      if (this.enviandoReporte) return null;
      if (this.envioIncierto) {
        return this.avisoEnvio && this.avisoEnvio.texto
          ? this.avisoEnvio.texto
          : 'No se sabe si el último correo de este mes salió: compruébalo antes de volver a '
            + 'mandarlo.';
      }
      return this.motivoNoSaleElDocumento;
    },
    /**
     * **Con qué cifras sale el correo de verdad**, dicho en el diálogo (P2 Codex ronda 5).
     *
     * El diálogo decía que el informe se manda «tal como está en la pantalla», y eso no se puede
     * prometer: la petición lleva el mes y los destinatarios, y el cuerpo del correo lo compone el
     * servidor **al mandarlo**, con la misma lectura que sirve esta pantalla —que es lo que pide la
     * incidencia, «una cifra, un camino»—. Entre que la pantalla leyó el mes y se pulsa «Enviar»
     * pueden pasar horas, y otra persona puede haber guardado una cifra o recalculado un objetivo:
     * entonces el correo lleva la suya, no la de esta pantalla, y quien lo manda estaría firmando
     * unas cifras que no ha visto.
     *
     * Lo que se hace es decirlo, con la hora de la lectura y con la salida: recargar antes de
     * mandar. Lo que NO se hace es releer el mes aquí para comparar, aunque sea lo primero que
     * parece: el GET del mes **recalcula el mes en curso** (D-GUARDADO) y enviar no puede escribir
     * nada (criterio 9 de la incidencia), así que esa comprobación cambiaría cifras por el hecho de
     * mandar un correo. Un sello de versión que el servidor rechazara —la otra salida— es una
     * decisión que alcanza también al PDF de la #173, que sale igual de una lectura nueva: queda
     * propuesto como tarea aparte y dicho en el PR.
     *
     * Y habla **solo del correo**: desde que dirección quitó el adjunto (18/10/2026) el envío no
     * genera ningún fichero, así que nombrar aquí un PDF haría esperar un adjunto que no llega. El
     * papel sigue saliendo por «Exportar a PDF», que es otro botón y otra petición.
     */
    avisoCifrasAlEnviar: function() {
      return 'El correo lo compone el servidor al mandarlo, leyendo lo que consta '
        + 'guardado en ese momento. Lo que estás viendo se leyó el '
        + this.formatInstante(this.leidoEn) + ': si alguien ha guardado una cifra o ha recalculado '
        + 'un objetivo desde entonces, el correo llevará lo nuevo. Para mandar exactamente lo que '
        + 'tienes delante, cierra esto y recarga la pantalla con el botón de arriba antes de '
        + 'enviar.';
    },
    /**
     * Los roles que ven la pestaña, para el `v-roles` del botón de enviar. Sale del módulo de
     * permisos y no se escribe aquí: es lo que ese módulo explica en su cabecera.
     */
    rolesPestana: function() {
      return PermisosFacturacion.ROLES_PESTANA;
    },

    // ============================================== programar reporte (#549)

    /**
     * Lo que el planificador entiende, leído del adaptador y no escrito aquí: es parte del contrato
     * con la API, que lo valida en `FactCobroPeriodicidad`. Con la lista copiada en la pantalla,
     * una frecuencia mal puesta se vería como un 400 al guardar y no antes.
     */
    FRECUENCIAS: function() { return FactCobro.FRECUENCIAS; },
    DIAS_SEMANA: function() { return FactCobro.DIAS_SEMANA; },
    DIA_MES_MAXIMO: function() { return FactCobro.DIA_MES_MAXIMO; },
    /**
     * **SI NO HAY LISTA VIGENTE DE DESTINATARIOS**: mientras se está leyendo y cuando la lectura ha
     * fallado.
     *
     * Las dos cuentan igual, y por eso están en una sola propiedad: «no hay lista» no es lo mismo
     * que «la lista está vacía». Confundirlas es el hallazgo del 19/10/2026 —el `catch` dejaba
     * `prog.grupos = []` y desde ahí la pantalla se comportaba como si de verdad no quedara ningún
     * grupo ni ninguna persona, podando lo que la regla que se estaba editando tenía guardado—.
     *
     * Por aquí pasan los tres sitios que dependen de esa lista: lo que se dibuja
     * (`progGruposVisibles`), lo que se poda (`descartarLoQueNoSeVeEnLaProgramacion`) y lo que se
     * deja guardar (`motivoNoSePuedeGuardarLaProgramacion`). El que se añada mañana hereda el
     * criterio con solo mirar aquí.
     */
    progSinListaDeDestinatarios: function() {
      return this.prog.cargandoDestinatarios || !!this.prog.errorDestinatarios;
    },
    /**
     * **LA LISTA VIGENTE DE GRUPOS DEL FORMULARIO DE PROGRAMACIÓN**, con el mismo criterio que
     * `gruposVisibles` en el diálogo de envío: mientras no hay lista vigente **no se dibuja la de
     * antes**. De aquí salen los chips, las personas elegibles y lo que se guarda, los tres de la
     * misma propiedad, para que no se puedan contradecir.
     */
    progGruposVisibles: function() {
      return this.progSinListaDeDestinatarios ? [] : (this.prog.grupos || []);
    },
    /** Las personas de los grupos vigentes, sin repetir y sin las que no tienen correo. */
    progPersonasElegibles: function() {
      const vistos = {};
      const personas = [];
      this.progGruposVisibles.forEach(function(grupo) {
        (grupo.personas || []).forEach(function(persona) {
          if (persona && persona.username && !vistos[persona.username]) {
            vistos[persona.username] = true;
            personas.push(persona);
          }
        });
      });
      return personas;
    },
    /**
     * Las tres vías de destinatarios que se van a guardar, **cruzadas con la lista vigente**, o
     * `null` si no queda ninguna.
     *
     * Mismo criterio y mismo motivo que `destinatariosDelEnvio`: lo que se guarda sale de lo que la
     * pantalla está enseñando, no de lo que haya quedado en memoria de una apertura anterior. Una
     * regla guardada con un grupo que ya no existe se queda sin ese grupo en silencio, y eso es
     * exactamente lo que no puede pasar: lo avisa `descartarLoQueNoSeVeEnLaProgramacion`.
     */
    progDestinatarios: function() {
      const codigos = this.progGruposVisibles.map(function(grupo) { return grupo.codigo; });
      const usernames = this.progPersonasElegibles.map(function(p) { return p.username; });
      const grupos = this.prog.form.elegidosGrupos.filter(function(codigo) {
        return codigos.indexOf(codigo) > -1;
      });
      const personas = this.prog.form.elegidosPersonas.filter(function(username) {
        return usernames.indexOf(username) > -1;
      });
      const emails = correosDe(this.prog.form.emails);
      if (!grupos.length && !personas.length && !emails.length) return null;
      return { grupos: grupos, personas: personas, emails: emails };
    },
    /**
     * **Cuántos destinatarios del formulario cuelgan de la lista**: los grupos y las personas, que
     * son los que se guardan por código y hay que cruzar con ella. Los correos escritos a mano no
     * cuentan: esos están delante, en su campo, y no dependen de ninguna lectura.
     */
    progElegidosDeLaLista: function() {
      return this.prog.form.elegidosGrupos.length + this.prog.form.elegidosPersonas.length;
    },
    /**
     * **Lo que el formulario tiene elegido y la pantalla no puede dibujar** porque no hay lista
     * vigente, o `null`.
     *
     * Hace falta por §9 y por el corolario: con la lista caída los chips no se pintan, así que sin
     * esto el apartado «Destinatarios» de una regla que manda a Dirección se queda enseñando solo
     * los correos sueltos —o nada—, y eso es la pantalla afirmando que no hay destinatarios cuando
     * sí los hay. Se dice aquí, junto a los destinatarios, y junto a lo que lo desbloquea, que
     * es el botón «Recargar» de al lado.
     */
    progElegidosQueNoSeDibujan: function() {
      if (!this.progSinListaDeDestinatarios || !this.progElegidosDeLaLista) return null;
      const grupos = this.prog.form.elegidosGrupos.length;
      const personas = this.prog.form.elegidosPersonas.length;
      const trozos = [];
      if (grupos) trozos.push(grupos === 1 ? '1 grupo' : grupos + ' grupos');
      if (personas) trozos.push(personas === 1 ? '1 persona' : personas + ' personas');
      return 'Esta programación sigue teniendo elegidos ' + trozos.join(' y ') + ', y se '
        + 'conservan: no se ha quitado nada. Lo que no se puede es verlos ni cambiarlos hasta que '
        + 'la lista cargue.';
    },
    /**
     * **Si lo que se mandaría es el reintento de un alta ya comprometida**, que es lo que decide de
     * dónde sale el cuerpo —`cuerpoComprometido`, y no el catálogo de ahora (decisión de
     * @Donchurru, punto 3)— y, por eso mismo, qué comprobaciones tienen sentido antes de mandarlo.
     *
     * Está aquí y no suelta dentro de `guardarRegla` porque la puerta de «Guardar» también lo
     * necesita: un reintento no compone nada de la lista, así que lo que le pase a la lista no
     * puede bloquearlo —dejaría un alta incierta sin ninguna de sus dos salidas—.
     */
    progEsReintentoDeAlta: function() {
      return !this.prog.form.id && !!this.prog.cuerpoComprometido;
    },
    /**
     * **EL ALTA SIN RESOLVER QUE EL FORMULARIO NO TIENE DELANTE**, o `null`.
     *
     * Es la puerta que no depende de que ningún camino se acuerde de cerrarla, y por eso está escrita
     * como una consecuencia del apunte y no como un aviso que alguien pone. Lo que la cierra es «hay
     * una operación apuntada en el navegador y nadie la está atendiendo», y eso es verdad aunque el
     * camino que la dejó así se fuera sin decir nada —que es exactamente el hallazgo del 19/10/2026:
     * la respuesta de la recuperación que no se puede aplicar, porque entretanto alguien escribió en
     * el formulario, soltaba `recuperando` y se iba sin poner la retención; «Guardar» se encendía y
     * mandaba el borrador nuevo con una clave nueva mientras la anterior podía estar creada—.
     *
     * Las dos que NO cuentan, y por qué:
     *
     *  - **la que el formulario tiene delante** (misma clave): es la que resuelven «Reintentar» y
     *    «Descartar este alta», y contarla apagaría sus propias salidas;
     *  - **cualquiera, si ya hay una retención puesta**: el formulario solo puede atender una a la
     *    vez, así que `altaIncierta` ya frena el alta nueva y contar además la de al lado dejaría su
     *    «Reintentar» bloqueado para siempre, sin más salida que descartar.
     */
    progAltaApuntadaSinAtender: function() {
      if (this.prog.altaIncierta) return null;
      const apunte = this.prog.altaPendienteEnElNavegador;
      if (!apunte || this.prog.form.claveAlta === apunte.clave) return null;
      return apunte;
    },
    /**
     * **Por qué una hora que parece buena no saldría a su hora.** El planificador despierta cada
     * cinco minutos, así que una regla puesta a las 08:03 la ejecuta la pasada de las 08:00: tres
     * minutos ANTES de lo que pone la pantalla. La API lo rechaza en vez de redondearlo a
     * escondidas (D-COB-8), y esto lo dice antes de pulsar «Guardar», junto al campo que lo
     * arregla (§9).
     */
    avisoHora: function() {
      return motivoDeLaHora(this.prog.form.hora);
    },
    /**
     * El sábado o el domingo con «solo días laborables» no sale ningún día, y la API lo rechaza.
     * Se dice junto a la casilla que lo desbloquea, que es donde está mirando quien eligió el día.
     */
    avisoFinDeSemana: function() {
      if (this.prog.form.frecuencia !== 'SEMANAL') return null;
      return motivoDelFinDeSemana(this.prog.form);
    },
    /**
     * Por qué el día del mes no vale, **junto al campo** (§9): quien lo ha escrito está mirando ahí,
     * no el aviso de debajo del botón.
     */
    avisoDiaDelMes: function() {
      if (this.prog.form.frecuencia !== 'MENSUAL') return null;
      return motivoDelDiaDelMes(this.prog.form.diaMes);
    },
    /**
     * **POR QUÉ EL DIÁLOGO NO PUEDE DECIDIR UN ALTA AHORA MISMO**, o `null`: el único sitio donde se
     * mira si lo que enseña está completo.
     *
     * Son las dos lecturas de las que cuelga un alta, y las dos cuentan de las dos maneras —mientras
     * llegan y cuando han fallado—:
     *
     *  - **los destinatarios**, porque sin la lista no se puede saber a quién se le va a mandar;
     *  - **las programaciones que ya hay**, porque sin ellas no se puede ver si ya existe una igual.
     *    Esto es el hallazgo de la ronda 3: la lista fallaba, el `catch` la dejaba vacía con su
     *    aviso, el diálogo decía «Todavía no hay ninguno» y **«Guardar» seguía encendido**, así que
     *    se podía dar de alta una regla equivalente a otra que simplemente no se había podido leer.
     *    Dos reglas iguales son el informe saliendo dos veces, cada día.
     *
     * Y están **las dos en una propiedad** y no una condición por lectura: es la misma forma de fallo
     * que ya costó dos rondas en el diálogo de envío —cada control decidiendo por su cuenta—, así que
     * la lectura que se añada mañana se mira sola con solo entrar aquí.
     *
     * Solo frena el **alta**. Una edición no necesita la lista: lo que va a escribir es la regla que
     * ya tiene delante, condicionada a su `visto`, y bloquearla sería dejar sin arreglo una regla que
     * se está mirando por un fallo de otra lectura.
     */
    motivoNoSePuedeDecidirUnAlta: function() {
      // UNA RECUPERACIÓN SIN RESOLVER FRENA EL ALTA (P1 Codex ronda 4). Va primero porque es la
      // única de las tres que habla de una operación que puede estar ya escrita: dar de alta otra
      // mientras no se sabe qué pasó con la anterior es el camino directo a dos reglas iguales. La
      // consulta corre en paralelo con las dos listas de abajo, así que sin esto bastaba con que
      // ellas contestaran primero.
      if (this.prog.recuperando) {
        return 'Espera: quedó un alta de antes sin resolver y se está comprobando cómo acabó. '
          + 'Hasta saberlo no se puede crear otra, porque podrían quedar dos iguales mandando el '
          + 'informe dos veces.';
      }
      if (this.prog.cargandoDestinatarios) {
        return 'Espera a que termine de actualizarse la lista de destinatarios: hasta entonces no '
          + 'se puede saber a quién se le va a mandar.';
      }
      if (this.prog.cargando) {
        return 'Espera a que termine de cargarse la lista de envíos programados: hasta entonces no '
          + 'se puede saber si ya hay uno igual.';
      }
      if (this.prog.error) {
        return 'No se han podido cargar los envíos programados que ya hay, así que no se puede '
          + 'saber si el que vas a crear está repetido —y dos iguales mandarían el informe dos '
          + 'veces—. Pulsa «Recargar» arriba, en la lista, y vuelve a intentarlo.';
      }
      return null;
    },
    /**
     * **Por qué no se puede guardar la programación**, o `null`.
     *
     * Es una sola propiedad por la que pasan el `:disabled` del botón y el método que guarda, igual
     * que `motivoNoSePuedeEnviarYa` en el diálogo de envío: con dos condiciones separadas queda un
     * camino por el que se guarda con el botón diciendo que no se puede.
     *
     * Y lo que mira son **tres cosas y ninguna escrita dos veces**: lo que retiene el diálogo, lo que
     * falta por leer (`motivoNoSePuedeDecidirUnAlta`) y lo que dicen los campos
     * (`CAMPOS_DE_LA_PROGRAMACION`). Los campos no se repasan aquí a mano, que es lo que dejó fuera
     * la hora vacía en la ronda 1 y el día del mes con decimales en la ronda 3.
     *
     * Sale de `motivoParaNoMandarLaProgramacion`, que es la misma puerta que mira «Reintentar»: las
     * dos pulsaciones acaban en la misma petición, así que lo que la impide es lo mismo preguntado
     * de dos maneras.
     */
    motivoNoSePuedeGuardarLaProgramacion: function() {
      return this.motivoParaNoMandarLaProgramacion(false);
    },
    /**
     * **Por qué «Reintentar» no puede repetir el alta incierta ahora mismo**, o `null`.
     *
     * Es la MISMA puerta que «Guardar» —`motivoParaNoMandarLaProgramacion`— preguntada como lo que
     * el reintento es: la resolución de ese alta. Y existe por un fallo concreto (P1 Codex
     * 19/10/2026): «Reintentar» solo se apagaba mientras se guardaba, así que con las listas
     * todavía cargando —se leen en paralelo al abrir el diálogo— se podía pulsar, el método soltaba
     * la retención y acto seguido la puerta paraba el envío. Resultado: el formulario quedaba
     * suelto, sin aviso y sin los dos botones, con la clave ya gastada en el servidor.
     *
     * Con esto el botón se apaga **y dice por qué, junto al control** (§9), la retención no se
     * suelta, y «Descartar este alta» sigue siendo la salida que nunca se bloquea.
     */
    motivoNoSePuedeReintentarElAlta: function() {
      return this.motivoParaNoMandarLaProgramacion(true);
    },
    /**
     * **Si el formulario está retenido**: mientras se guarda, o mientras haya un alta de la que no
     * se sabe si quedó creada (P1 Codex ronda 2).
     *
     * Una sola propiedad por la que pasan todos los `:disabled` del formulario y
     * `alternarElegidoProg`, y no dos condiciones repetidas campo a campo: el campo siguiente que se
     * añada hereda la retención sin que nadie se acuerde, que es lo que no pasó con los chips la
     * primera vez.
     *
     * Con un alta incierta importa de verdad: la clave del alta ya puede estar gastada en el
     * servidor, así que lo único que se puede hacer con ese formulario es mandarlo **otra vez tal
     * cual**. Dejar que se cambie la hora o los destinatarios sería dejar escribir unos datos que no
     * se van a poder guardar con esa clave.
     */
    progFormRetenido: function() {
      return this.prog.guardando || !!this.prog.altaIncierta;
    },
    /**
     * **Si hay algo escrito en el formulario que no está guardado** (P2 Codex ronda 2).
     *
     * Es lo que hace que «Editar» en otra ficha pregunte antes de sustituirlo. Se compara contra la
     * foto que se tomó al cargarlo (`prog.formOriginal`), así que vale igual para un alta a medio
     * escribir y para una edición con cambios: las dos son trabajo de alguien que se perdía sin
     * decir nada.
     */
    progFormModificado: function() {
      return huellaDelFormProgramacion(this.prog.form)
        !== huellaDelFormProgramacion(this.prog.formOriginal);
    },
    /**
     * **Por qué no se puede traer otra regla al formulario ahora mismo**, o `null`.
     *
     * Solo el alta incierta lo impide del todo, y por lo mismo que retiene el formulario: su clave
     * puede estar gastada y lo que hay delante es la única petición que se puede repetir con ella.
     * Un borrador normal no bloquea nada —se pregunta y se descarta si quien está delante lo dice—,
     * que es la diferencia entre proteger el trabajo de alguien y estorbarle.
     */
    motivoNoSePuedeCambiarDeRegla: function() {
      if (!this.prog.altaIncierta) return null;
      return 'Primero resuelve el alta de la que no se sabe si quedó creada, abajo en el '
        + 'formulario: «Reintentar» o «Descartar este alta».';
    },
    /**
     * **LA LISTA VIGENTE DE GRUPOS**: la que el diálogo está enseñando ahora mismo, y la única de la
     * que se puede mandar nada.
     *
     * Mientras se está releyendo la lista **no hay lista vigente**, y por eso aquí sale vacía aunque
     * `envio.grupos` siga teniendo la de la apertura anterior. Esto es la tercera vuelta de una
     * misma familia que la revisión automática ha sacado en tres rondas seguidas: lo elegido
     * sobreviviendo a la lista que lo dibujaba (ronda 1), una respuesta vieja pisando a la vigente
     * (ronda 2) y el envío permitido mientras la lista se actualizaba (ronda 3). Las tres son el
     * mismo defecto: **se podía mandar el informe a destinatarios que no eran los que la pantalla
     * estaba enseñando**.
     *
     * Y se cierra aquí en vez de con una comprobación más en cada sitio porque de esto cuelga TODO
     * lo demás: lo que se dibuja, las personas elegibles, lo que viaja en la petición y por qué el
     * botón está apagado. Con una sola lista vigente, «lo que se ve» y «lo que se puede mandar» no
     * son dos cosas que haya que mantener iguales: son la misma.
     */
    gruposVisibles: function() {
      return this.envio.cargandoDestinatarios ? [] : (this.envio.grupos || []);
    },
    /**
     * Las personas que se pueden elegir una a una: las de **los grupos vigentes**, **sin repetir**
     * —alguien puede estar en dos— y en el orden en que vienen.
     */
    personasElegibles: function() {
      const vistos = {};
      const personas = [];
      this.gruposVisibles.forEach(function(grupo) {
        (grupo.personas || []).forEach(function(persona) {
          if (persona && persona.username && !vistos[persona.username]) {
            vistos[persona.username] = true;
            personas.push(persona);
          }
        });
      });
      return personas;
    },
    /**
     * **LO QUE DE VERDAD SE MANDARÍA**, o `null` si no hay a quién: el único sitio donde se compone
     * el destino del correo.
     *
     * Los grupos y las personas salen **cruzados con la lista vigente**, no de lo que haya quedado
     * en `elegidos*`: así, aunque un estado viejo sobreviva a una reapertura o una respuesta llegue
     * tarde, lo que viaja no puede incluir a nadie que el diálogo no esté enseñando. Es la
     * diferencia entre una garantía y una comprobación que hay que acordarse de hacer: `enviarReporte`
     * no filtra nada, coge esto.
     *
     * Los **correos escritos a mano no se cruzan con nada**: están delante, en su campo, y son los
     * que permiten mandar el informe con la lista de grupos caída.
     */
    destinatariosDelEnvio: function() {
      const codigos = this.gruposVisibles.map(function(grupo) { return grupo.codigo; });
      const usernames = this.personasElegibles.map(function(persona) { return persona.username; });
      const grupos = this.envio.elegidosGrupos.filter(function(codigo) {
        return codigos.indexOf(codigo) > -1;
      });
      const personas = this.envio.elegidosPersonas.filter(function(username) {
        return usernames.indexOf(username) > -1;
      });
      const emails = correosDe(this.envio.emails);
      if (!grupos.length && !personas.length && !emails.length) return null;
      return {
        grupos: grupos,
        personas: personas,
        emails: emails,
        emailsCc: correosDe(this.envio.emailsCc)
      };
    },
    /**
     * Si hay algún correo **escrito a mano**, en «correos sueltos» o en copia.
     *
     * Es la tercera vía de elegir destinatarios, y está aquí porque las decisiones del diálogo que
     * miran «¿hay elección de quien está delante?» tienen que contar las tres: mirando solo los
     * grupos y las personas, un correo escrito a mano no contaba como elección y el diálogo se daba
     * por nuevo teniéndolo delante (P2 Codex ronda 4).
     */
    hayCorreosAMano: function() {
      return correosDe(this.envio.emails).length > 0 || correosDe(this.envio.emailsCc).length > 0;
    },
    /**
     * **Si el diálogo está como nuevo**: no hay nada elegido por ninguna de las tres vías.
     *
     * Es lo único que decide si al traer la lista se propone lo de por omisión, y se **deriva del
     * estado** en vez de apuntarse en una bandera de «ya lo han tocado». La bandera tenía que
     * acordarse de marcarse en cada vía, y se olvidaba justo en la que no es un chip: quien había
     * quitado los grupos, elegido a una persona y escrito además un correo a mano se quedaba, al
     * desaparecer esa persona, con el diálogo dado por nuevo —el correo a mano no contaba—, y la
     * apertura siguiente volvía a marcar ADMIN y DIRECTOR encima, así que el informe salía a más
     * gente de la elegida y sin decirlo (P2 Codex ronda 4). Derivado no hay nada que acordarse: la
     * vía que se añada mañana cuenta sola.
     *
     * Y al revés también vale: si no queda nada —ni un grupo, ni una persona, ni un correo—, el
     * diálogo está como nuevo de verdad y volver a proponer lo del servidor es lo que evita que se
     * quede vacío para siempre por una elección que ya no existe (P1 Codex ronda 1).
     */
    eleccionVacia: function() {
      return !this.envio.elegidosGrupos.length && !this.envio.elegidosPersonas.length
        && !this.hayCorreosAMano;
    },
    /**
     * **Si lo que va a ir en el correo está retenido**: mientras el correo sale, no se toca.
     *
     * Vale para las tres vías de destinatarios y para el asunto, que es todo lo que `enviarReporte`
     * captura al entrar. Una vez capturado, el correo ya viaja con eso: un chip que se apagara
     * —o un correo borrado del campo— enseñaría una elección que no es la del informe que se está
     * entregando, y quien lo mirase creería que a ese grupo no se le ha mandado (P2 Codex ronda 6).
     *
     * Es **una sola propiedad** por la que pasan los seis controles del diálogo: los dos grupos de
     * chips, los dos campos de correos, el asunto y `alternarElegido`. Los tres campos de texto ya
     * se apagaban con `enviandoReporte` y los chips no, que es exactamente la forma de fallo que
     * sale cuando cada control decide por su cuenta; con un nombre para la regla, el control que se
     * añada mañana lo mira sin que nadie se acuerde de repetir la condición.
     */
    eleccionRetenida: function() {
      return this.enviandoReporte;
    },
    /**
     * Si hay a quién mandarlo. Las tres vías cuentan y basta una: un grupo, una persona o un correo
     * escrito a mano. Sale de `destinatariosDelEnvio` y no de `elegidos*` para que el botón no pueda
     * estar encendido por una elección que no viajaría —o, lo que es peor, que viajaría sin estar a
     * la vista—. Y lo vuelve a decidir el servidor al enviar, que es quien resuelve los grupos,
     * porque un grupo puede no tener a nadie.
     */
    hayDestinatarios: function() {
      return this.destinatariosDelEnvio !== null;
    },
    /**
     * **Por qué «Enviar» está apagado en el diálogo**, dicho junto al botón (§9: el motivo, quién lo
     * desbloquea y dónde).
     *
     * Uno solo para los dos sitios que deciden —el `:disabled` del botón y la puerta de
     * `enviarReporte`—, así que no puede quedar un camino que mande cuando el botón dice que no se
     * puede. Lo que se explica solo no lleva texto: mientras el correo sale, el rótulo del botón ya
     * dice «Enviando…».
     */
    motivoNoSePuedeEnviarYa: function() {
      if (this.enviandoReporte) return null;
      // **Lo primero, la condición de la pantalla** (`loQueSeVeEsLoGuardado`), que es la que de
      // verdad decide si hay un mes que mandar: `enviarReporte` la vuelve a comprobar antes de
      // pedir nada, así que sin esto el botón del diálogo quedaba encendido y el envío se negaba en
      // silencio —el control mudo que dirección registró el 05/10/2026—. Pasa con la pantalla
      // descuadrada mientras el diálogo está abierto: una casilla a medio escribir, un mes que no se
      // ha podido releer. El texto es el de los dos botones de la cabecera, para no contar lo mismo
      // de dos maneras; el genérico es para los estados que ese no explica porque se explican solos
      // —cargando, guardando— y que aquí sí hay que decir, porque desde el diálogo no se ve ni el
      // preloader ni el rótulo del botón de guardar.
      if (!this.loQueSeVeEsLoGuardado) {
        return this.motivoNoSaleElDocumento
          || 'Espera a que la pantalla termine de cargar o de guardar: el correo lo compone el '
            + 'servidor con lo que consta guardado, y a mitad de eso no sería lo que estás viendo. '
            + 'Se desbloquea solo.';
      }
      // La lista se está releyendo: lo que había es de la apertura anterior y todavía no se sabe si
      // sigue valiendo. Son dos segundos y se desbloquea solo, pero mandar aquí es mandar a una
      // lista que puede acabar de cambiar —una baja de personal de hoy—.
      if (this.envio.cargandoDestinatarios) {
        return 'Espera a que termine de actualizarse la lista de destinatarios: hasta entonces no '
          + 'se sabe si quien tenías elegido sigue estando. Se desbloquea solo.';
      }
      if (!this.hayDestinatarios) {
        return 'Elige al menos un grupo o una persona, o escribe un correo, para poder enviarlo.';
      }
      // Y el mes retenido por un envío del que no se sabe si salió. El texto es el del aviso de la
      // pantalla, que es el que lleva la salida: ver `motivoNoEnviable`.
      if (this.envioIncierto) return this.motivoNoEnviable;
      return null;
    },
    /**
     * Por qué «Guardar» está apagado teniendo algo que guardar, dicho **junto al botón**.
     *
     * Solo para la espera del recálculo, que es la que no se explica sola: el botón que está
     * guardando ya lo dice en su rótulo, mientras la pantalla carga hay un preloader, y un mes que
     * no admite datos tecleados lo explica su propio aviso. Un control apagado y mudo es el defecto
     * que dirección registró el 05/10/2026.
     */
    porQueNoSePuedeGuardar: function() {
      if (!this.cuantosSinGuardar || this.guardando || this.cargando) return null;
      if (this.objetivoEnCurso) {
        return 'Espera a que termine de calcularse ' + this.cualSeEstaCalculando + ', más abajo. Se '
          + 'escribe una cosa a la vez: las dos respuestas traen el mes entero, y a la vez una se '
          + 'llevaría por delante lo que trae la otra. Se desbloquea solo al acabar y lo que has '
          + 'tecleado no se pierde.';
      }
      return this.porQueHayAlgoAMedias;
    },
    /**
     * Por qué las casillas de la tabla están apagadas, dicho **junto a la tabla**.
     *
     * Las casillas se apagan mientras se calcula un objetivo por la misma razón por la que se apaga
     * «Guardar» (ver {@code sePuedeGuardar}): el recálculo contesta con el mes entero y lo aplica, y
     * teclear mientras eso vuelve es teclear en una tabla que está a punto de repintarse —lo que
     * quedara a medio escribir en una casilla, el `12.` que `editar` no apunta porque todavía no es
     * un número, se iría con el repintado—. Pero un control que no se puede usar dice por qué, y
     * sesenta casillas apagadas sin una palabra son el defecto que dirección registró el 05/10/2026.
     *
     * Solo para esa espera, que es la que no se explica sola: mientras se guarda lo dice el rótulo
     * del propio botón, mientras la pantalla carga hay un preloader, y en un mes que no admite datos
     * tecleados las casillas están apagadas todo el rato y lo que lo explica es la nota del mes.
     */
    porQueLasCasillasEsperan: function() {
      if (!this.objetivoEnCurso || this.guardando || this.cargando) return null;
      if (!this.mes || this.mes.editable !== true) return null;
      return 'Las casillas no se pueden escribir mientras se calcula ' + this.cualSeEstaCalculando
        + ': al acabar, la pantalla recibe el mes entero y las vuelve a pintar. Se desbloquean solas'
        + ' en unos segundos y lo que ya has tecleado no se pierde.';
    },
    /**
     * Cuál de los dos objetivos se está calculando, para los avisos. Sale del resumen que el
     * servidor manda y no de un nombre escrito aquí, igual que la magnitud que se le pide.
     */
    cualSeEstaCalculando: function() {
      const enCurso = this.resumenes.filter(function(r) { return r.recalculando; })[0];
      return enCurso && enCurso.etiqueta
        ? 'el objetivo de ' + enCurso.etiqueta
        : 'el objetivo que se está calculando';
    },
    textoBotonGuardar: function() {
      if (this.guardando) return 'Guardando…';
      if (!this.cuantosSinGuardar) return 'Guardar';
      return 'Guardar (' + this.cuantosSinGuardar + ')';
    },
    /** Si hay mes anterior al que ir. El suelo es el primer año que trajo la carga. */
    hayAnterior: function() {
      if (this.anio === null) return false;
      return this.anio > PRIMER_ANIO || this.numeroMes > 1;
    },
    /**
     * Si hay mes siguiente. El techo es un año por delante del último cargado o del actual, el
     * mismo que acepta la API: ofrecer más sería ofrecer un mes que el servidor rechaza.
     */
    haySiguiente: function() {
      if (this.anio === null) return false;
      return this.anio < this.topeAnio || this.numeroMes < 12;
    },
    /**
     * El techo del selector: un año por delante del último cargado **o del actual**, el que sea
     * mayor. El del servidor es el año actual + 1 y no depende de lo cargado, así que mirar solo
     * lo cargado deja el selector por debajo del contrato en cuanto la carga se queda atrás: con
     * datos hasta 2026 y el navegador en 2028 no se podría abrir ni el mes en curso.
     */
    topeAnio: function() {
      const cargados = this.aniosDisponibles || [];
      const ultimoCargado = cargados.length ? cargados[cargados.length - 1] : 0;
      return Math.max(ultimoCargado, new Date().getFullYear()) + 1;
    }
  },
  methods: {
    onPageInit: function() {
      // Se abre en el mes en curso según el navegador, y el servidor es quien dice si ese mes
      // existe: aquí solo se elige a qué mes entrar, no qué cifras se enseñan.
      const hoy = new Date();
      this.anio = hoy.getFullYear();
      this.numeroMes = hoy.getMonth() + 1;
      this.cargar();
    },
    /**
     * Recargar con el botón: trae el mes otra vez y tira lo tecleado sin guardar.
     *
     * No mientras se está guardando. Vacía el borrador, y con un lote a medias eso se llevaría los
     * cambios que todavía no han salido —y los que fallaron, que es justo lo que hay que poder
     * reintentar—. El botón sale apagado, y esta guarda es la que lo garantiza también si se llega
     * por otro camino.
     */
    recargar: function() {
      if (this.ocupado) return;
      this.casillas = {};
      this.avisoDelLote = null;
      // Aquí NO se toca nada de la exportación, y ya no hay nada que se pudiera tocar: el bloqueo y
      // el fallo viven en el registro del módulo, que es de la operación. Recargar no termina
      // ninguna petición, así que no puede soltar ningún bloqueo —eso dejaba volver a pedir el PDF
      // del mismo mes con el primero en vuelo, los dos escribiendo el mismo fichero (ronda 2)— ni
      // borrar un aviso que pertenece al mes y no a esta carga.
      // Los fallos de recálculo son de la pantalla que se deja de enseñar: el motivo por el que no
      // entró el objetivo de octubre no dice nada al lado de la cifra de septiembre.
      this.fallosObjetivo = {};
      this.cargar();
    },

    /**
     * Pide el PDF del mes y lo descarga (#173).
     *
     * **No toca nada de la pantalla**: no recarga, no vacía el borrador y no vuelve a pedir el mes.
     * Es una lectura más, y el servidor tampoco recalcula al servirla, así que el documento enseña
     * exactamente las cifras que están en pantalla. Por eso exportar no puede cambiar ninguna cifra
     * ni ningún dato manual, que es lo que exige la incidencia.
     *
     * El `sePuedeExportar` se vuelve a comprobar aquí y no solo en el botón: si se llegara por otro
     * camino —un atajo, una recarga a mitad— exportar con cifras tecleadas sin guardar daría un
     * papel que no es lo que se está viendo.
     */
    exportar: function() {
      const self = this;
      if (!self.sePuedeExportar) return Promise.resolve();
      // La identidad de ESTA exportación, capturada antes de pedirla: las flechas del mes siguen
      // vivas mientras se genera, así que al contestar `self.numeroMes` puede ser ya otro, y la
      // pantalla puede no existir. Con ella se apuntan las dos cosas que la respuesta tiene que
      // decir: que ha terminado y cómo acabó.
      const operacion = self.operacionDelMes;
      empiezaLaExportacion(operacion);

      // De aquí en adelante **no se toca nada de esta instancia**, y es el fondo del arreglo: la
      // respuesta puede llegar con la pantalla ya destruida —se salió al menú— y lo que tiene que
      // sobrevivir no es la pantalla, es la operación. Por eso el bloqueo y el fallo se sueltan en el
      // registro del módulo y no en `data()`, y el motivo lo calcula una función del módulo.
      return FactCobro.pdfMes(self.anio, self.numeroMes).then(function() {
        // Se suelta aquí y no antes porque la promesa del adaptador se resuelve **después** de
        // escribir y abrir el fichero (`descargar`), que es lo que de verdad choca entre dos
        // exportaciones del mismo mes. Y se suelta siempre, también cuando ya se está viendo otro
        // mes o la pantalla es otra: lo que esto dice es que la petición ha terminado, no si se está
        // mirando. Sin ello el botón de ese mes se quedaría apagado para siempre.
        terminaLaExportacion(operacion);
      }).catch(function(err) {
        // **Los dos rechazos que no se le enseñan a nadie**, y el bloqueo se suelta igual porque la
        // petición ha terminado:
        //
        //  - `sesionCaducada`: la sesión la ha cerrado el adaptador y la aplicación está navegando
        //    al login, así que un aviso aquí saldría encima de una pantalla que ya no es esta.
        //    Mismo criterio que la pantalla de los boletines de festivos.
        //  - `otraSesion`: la sesión que pidió el PDF ya no es la que está delante —se cerró, o
        //    entró otra cuenta—. El documento lo ha descartado el adaptador antes de descargarlo y
        //    el motivo no es de nadie: ni de quien se fue ni de quien ha entrado.
        if (err && (err.sesionCaducada || err.otraSesion)) {
          terminaLaExportacion(operacion);
          return;
        }
        // El motivo se apunta en la exportación que falló, se esté viendo o no: quien enseña el
        // aviso es la pantalla que esté viva, y solo cuando ese mes está delante. Así el fallo no se
        // pierde por haber mirado otro mes —ni por haber salido de la pantalla— mientras se
        // generaba, ni se escribe sobre un mes que nadie ha exportado.
        terminaLaExportacion(operacion, motivoDelFallo(err));
      });
    },

    // ======================================================================== enviar reporte

    /**
     * Abre el diálogo de envío (#547) y trae la lista de destinatarios.
     *
     * El `sePuedeEnviar` se vuelve a comprobar aquí y no solo en el botón, por lo mismo que en
     * `exportar`: si se llegara por otro camino, mandar con cifras tecleadas sin guardar daría un
     * correo que no es lo que se está viendo.
     *
     * Lo elegido **no se limpia al abrir** si alguien ya lo tocó: cerrar el diálogo con la X —o sin
     * querer— no es pedir que se tire la elección. Se propone lo de por omisión cuando está como
     * nuevo, que es al entrar y después de un envío que salió bien.
     */
    abrirEnvio: function() {
      if (!this.sePuedeEnviar) return Promise.resolve();
      this.envio.error = null;
      this.envio.abierto = true;
      // Se devuelve la promesa de la lectura, no se deja suelta: así quien abre el diálogo —y el
      // banco de pruebas— puede esperar a que la lista esté, en vez de mirar un estado a medias.
      return this.cargarDestinatarios();
    },
    /** Cerrar el diálogo no cancela nada: si hay un envío en vuelo, sigue y el correo sale. */
    cerrarEnvio: function() {
      this.envio.abierto = false;
    },
    /**
     * Los grupos y las personas a las que se puede mandar, **leídos cada vez que se abre**: un alta o
     * una baja de personal de hoy tiene que estar, y esta pantalla puede llevar horas abierta.
     *
     * Si la lectura falla, el diálogo **no se cierra**: los correos escritos a mano siguen
     * funcionando y el aviso lo dice. Lo que no se hace es dejar el diálogo mudo con los grupos
     * vacíos, que se leería como «no hay nadie a quien mandarlo».
     *
     * Y los dos caminos acaban en `descartarLoQueNoSeVe`, que es lo que impide que una elección de
     * una apertura anterior sobreviva a la lista que la dibujaba.
     */
    cargarDestinatarios: function() {
      const self = this;
      // El nº de esta lectura, como el del mes: cerrar el diálogo y volver a abrirlo antes de que
      // conteste la primera deja DOS en vuelo, y las dos escriben la misma lista, el mismo aviso y
      // la misma elección. Lo que llegue de una lectura que ya no es la vigente se descarta aquí y
      // no se mezcla con lo que la última acabó de pintar (P2 Codex ronda 2).
      const lectura = ++self.envio.lecturaEnCurso;
      cerrojoDeLaSesion(self.envio, 'cargandoDestinatarios');
      self.envio.errorDestinatarios = null;
      self.envio.descartados = null;
      return FactCobro.destinatariosInforme().then(function(res) {
        if (lectura !== self.envio.lecturaEnCurso) return;
        const dto = unwrapDto(res) || {};
        self.envio.cargandoDestinatarios = false;
        self.envio.grupos = dto.grupos || [];
        self.envio.notaGrupos = dto.nota || null;
        if (self.eleccionVacia) {
          // Lo que el servidor propone, que es quien sabe quién ve la pestaña: la pantalla no
          // escribe esa lista. Ver `porOmision` en el DTO.
          // Y solo con el diálogo como nuevo: lo que haya elegido quien está delante —un grupo, una
          // persona o un correo escrito a mano— no se pisa proponiendo nada encima.
          self.envio.elegidosGrupos = self.envio.grupos
            .filter(function(grupo) { return grupo.porOmision; })
            .map(function(grupo) { return grupo.codigo; });
        }
        self.descartarLoQueNoSeVe();
      }).catch(function(err) {
        if (lectura !== self.envio.lecturaEnCurso) return;
        self.envio.cargandoDestinatarios = false;
        self.envio.grupos = [];
        self.envio.notaGrupos = null;
        self.envio.errorDestinatarios = err && err.message
          ? 'No se ha podido cargar la lista de destinatarios: ' + err.message
          : 'No se ha podido cargar la lista de destinatarios.';
        self.descartarLoQueNoSeVe();
      });
    },
    /**
     * Deja elegido **solo lo que el diálogo está dibujando**.
     *
     * La lista se relee cada vez que se abre, así que entre dos aperturas puede desaparecer un grupo
     * o una persona —una baja de hoy— y puede no llegar nada porque la lectura falló. Lo elegido en
     * la apertura anterior seguía en memoria: el diálogo enseñaba cero destinatarios y el botón
     * «Enviar» seguía encendido, así que el informe —con las cifras de facturación de la empresa—
     * salía a grupos y a personas que quien pulsó no tenía delante, incluidos los que se propusieron
     * solos la primera vez. Es el mismo defecto que dirección registró el 05/10/2026 contado al
     * revés: dos partes de la misma pantalla diciendo lo contrario la una de la otra.
     *
     * Los **correos escritos a mano no se tocan**: esos sí están delante, en su campo, y son justo
     * lo que permite mandar el informe con la lista caída.
     *
     * Va aquí y no en el `catch` porque la forma del fallo es la misma en los dos caminos —una
     * elección que ya no se dibuja—, y en el que sí responde también ocurre: con el `catch` solo, un
     * username que acaba de darse de baja seguía elegido y el envío se iba a morir en la API, que lo
     * rechaza por no poder resolverlo.
     */
    descartarLoQueNoSeVe: function() {
      const codigos = this.gruposVisibles.map(function(grupo) { return grupo.codigo; });
      const usernames = this.personasElegibles.map(function(persona) { return persona.username; });
      const grupos = this.envio.elegidosGrupos.filter(function(codigo) {
        return codigos.indexOf(codigo) > -1;
      });
      const personas = this.envio.elegidosPersonas.filter(function(username) {
        return usernames.indexOf(username) > -1;
      });
      if (grupos.length === this.envio.elegidosGrupos.length
        && personas.length === this.envio.elegidosPersonas.length) return;
      this.envio.elegidosGrupos = grupos;
      this.envio.elegidosPersonas = personas;
      // Se dice, y junto a los destinatarios: quitar una elección sin avisar es la otra mitad del
      // mismo defecto —quien abrió el diálogo con Dirección marcada tiene que enterarse de que ya
      // no lo está—, y el aviso es lo que explica el botón apagado si no queda nada.
      this.envio.descartados = 'Lo que tenías elegido ya no está en esta lista, así que se ha '
        + 'quitado. Vuelve a elegir a quién se lo mandas antes de enviarlo.';
      // Lo que no se hace aquí es volver a proponer lo de por omisión en el sitio del descarte: con
      // el aviso de arriba delante, marcar ADMIN y DIRECTOR a la vez sería la pantalla
      // contradiciéndose —«se ha quitado lo que tenías, vuelve a elegir» junto a dos grupos
      // marcados—. Lo propone la apertura siguiente, que ya encuentra el diálogo como nuevo: ver
      // `eleccionVacia`.
    },
    grupoElegido: function(codigo) {
      return this.envio.elegidosGrupos.indexOf(codigo) > -1;
    },
    alternarGrupo: function(codigo) {
      this.alternarElegido(this.envio.elegidosGrupos, codigo);
    },
    personaElegida: function(username) {
      return this.envio.elegidosPersonas.indexOf(username) > -1;
    },
    alternarPersona: function(username) {
      this.alternarElegido(this.envio.elegidosPersonas, username);
    },
    /**
     * Marca o desmarca un chip, y es **el único sitio** que cambia la elección de destinatarios.
     *
     * Los dos `alternar*` eran la misma función con otra lista, y cada uno tenía que acordarse de
     * mirar si el correo está saliendo: uno de los dos se habría olvidado, igual que los chips se
     * olvidaron del `:disabled` que sí tenían los campos de texto. Con la puerta aquí, la mira
     * también el que se añada mañana.
     *
     * No se usa `:disabled` porque un chip es un `<span>` y no lo tiene; el clic entra igual, así
     * que es este método el que decide. El chip apagado lo dice con su estilo y el porqué está
     * escrito encima, junto a los chips (ver `eleccionRetenida`).
     */
    alternarElegido: function(lista, valor) {
      if (this.eleccionRetenida) return;
      const donde = lista.indexOf(valor);
      if (donde > -1) lista.splice(donde, 1);
      else lista.push(valor);
    },
    /**
     * Cuánta gente hay en un grupo, al lado de su nombre. Un grupo vacío se puede elegir igual y no
     * manda nada: ver el número es lo que lo dice antes de pulsar «Enviar», en vez de después.
     */
    cuantosEnElGrupo: function(grupo) {
      const cuantos = ((grupo && grupo.personas) || []).length;
      return cuantos === 1 ? '1 persona' : cuantos + ' personas';
    },
    /**
     * Manda el informe del mes que está en pantalla (#547).
     *
     * Lo que viaja son **los códigos de los grupos y los usernames**, no los correos: los resuelve la
     * API en el momento del envío. Y el mes se captura antes de pedirlo, como en `exportar`: las
     * flechas siguen vivas mientras el correo sale, así que al contestar `numeroMes` puede ser ya
     * otro, y el aviso tiene que decir de qué mes era el correo que salió.
     *
     * **El cerrojo y el resultado no son de esta pantalla, son del envío** (P2 Codex ronda 2): viven
     * en el registro del módulo, así que salir al menú y volver a entrar mientras el `POST` sigue en
     * vuelo ni suelta el botón ni borra la única señal de que el correo salió. Ver `elEnvioDe`.
     *
     * **Y a quién se manda no se compone aquí**: se coge de `destinatariosDelEnvio`, que es lo único
     * que la pantalla está enseñando (P1 Codex ronda 3). Aquí no hay ningún filtro que acordarse de
     * repetir, ni ninguna bandera de carga que acordarse de mirar: las dos cosas viven en esa
     * propiedad y en `motivoNoSePuedeEnviarYa`, por las que pasan el botón y este método.
     */
    enviarReporte: function() {
      const self = this;
      // La misma puerta que el `:disabled` del botón, y no una copia: entre la pulsación y el
      // repintado hay un hueco, y el teclado llega al botón sin pasar por el ratón. Cada petición
      // manda un correo de verdad, así que dos son dos informes al mismo buzón.
      if (self.enviandoReporte || self.motivoNoSePuedeEnviarYa) return Promise.resolve();
      if (!self.sePuedeEnviar) return Promise.resolve();
      const destinatarios = self.destinatariosDelEnvio;
      if (!destinatarios) return Promise.resolve();
      const anio = self.anio;
      const numeroMes = self.numeroMes;
      const deQueMes = self.etiquetaMes;
      // La identidad de ESTE envío, capturada antes de pedirlo, igual que en `exportar`: al contestar
      // puede haber otro mes en pantalla, otra sesión, o ninguna pantalla.
      const operacion = self.envioDelMes;
      empiezaElEnvio(operacion);
      self.envio.error = null;
      return FactCobro.enviarInformeMes(anio, numeroMes, {
        grupos: destinatarios.grupos,
        personas: destinatarios.personas,
        emails: destinatarios.emails,
        emailsCc: destinatarios.emailsCc,
        asunto: self.envio.asunto && self.envio.asunto.trim() ? self.envio.asunto.trim() : null
      }).then(function(res) {
        const buzones = unwrapDto(res);
        // Cómo acabó se apunta en el registro y no se enseña desde aquí: lo pinta la pantalla que
        // esté viva cuando tenga ese mes delante —ver `avisoEnvio`—, que puede no ser esta. Antes era
        // un toast de esta instancia, y quien salía al menú mientras el correo salía volvía a una
        // pantalla que no decía nada: ni que había salido, ni que no.
        terminaElEnvio(operacion, {
          ok: true,
          texto: 'El informe de ' + deQueMes + ' ha salido por correo'
            + (typeof buzones === 'number' ? ' a ' + buzones
              + (buzones === 1 ? ' buzón' : ' buzones') : '') + '.'
        });
        self.envio.abierto = false;
        self.limpiarEnvio();
      }, function(err) {
        // Cómo acabó lo decide `comoAcaboElEnvio` y no este `catch`: es lo que impide que un
        // resultado del que no consta que el correo no salió se cuente como un fallo y se repita a
        // ciegas (P2 Codex ronda 3). Lo que se apunta es lo que esa función dice, con su marca.
        const comoAcabo = comoAcaboElEnvio(err);
        // El motivo se queda además EN EL DIÁLOGO, con el diálogo abierto y lo elegido dentro: los
        // rechazos de la API —sin destinatarios, una persona sin correo— se arreglan ahí mismo, y
        // cerrarlo obligaría a elegirlo todo otra vez para saber qué pasó.
        terminaElEnvio(operacion, {
          ok: false, incierto: comoAcabo.incierto, texto: comoAcabo.texto
        });
        self.envio.error = comoAcabo.texto;
        // Y de un envío incierto el diálogo se cierra: lo que hay que hacer —comprobar el buzón y,
        // si hace falta, mandarlo a sabiendas— está en el aviso de la pantalla, junto al botón, y no
        // dentro de un diálogo cuyo «Enviar» ya no manda nada. Dejarlo abierto con el botón apagado
        // es el control mudo que dirección registró el 05/10/2026.
        if (comoAcabo.incierto) self.envio.abierto = false;
      });
    },
    /**
     * **Mandarlo otra vez aun sabiendo que la primera copia pudo salir** (P2 Codex ronda 3).
     *
     * Es la salida del aviso de un envío incierto, y es deliberada: la pantalla no puede saber si el
     * correo llegó, así que quien lo mira comprueba el buzón y decide. Lo que no se hace es decidirlo
     * por él —ni repetir solo, ni dejar el botón apagado para siempre—, que son los dos extremos
     * entre los que esto está.
     *
     * Levanta la retención y **no manda nada**: el envío lo pide la persona con «Enviar reporte»,
     * con su diálogo y sus destinatarios, como cualquier otro.
     */
    repetirAunqueNoSeSepa: function() {
      asumirElRiesgoDeRepetir(this.envioDelMes);
    },
    /**
     * Deja el diálogo como nuevo. Solo después de un envío que salió: ver `abrirEnvio`.
     *
     * Vaciar las tres vías es lo que lo deja como nuevo, sin ninguna bandera que poner aparte: con
     * nada elegido, la apertura siguiente vuelve a proponer lo del servidor (ver `eleccionVacia`).
     */
    limpiarEnvio: function() {
      this.envio.elegidosGrupos = [];
      this.envio.elegidosPersonas = [];
      this.envio.emails = '';
      this.envio.emailsCc = '';
      this.envio.asunto = '';
      this.envio.error = null;
      this.envio.descartados = null;
    },
    // ==================================================== programar reporte (#549)

    /**
     * Abre el diálogo de programación y trae las tres cosas que enseña: las reglas que hay, la
     * lista de destinatarios y el historial de envíos.
     *
     * Las tres salen a la vez y ninguna espera a la otra: son independientes, y con el historial
     * detrás de las reglas una lectura lenta dejaría el diálogo a medias sin motivo.
     *
     * El formulario **no se limpia al abrir**: cerrar el diálogo con la X —o sin querer— no es
     * pedir que se tire lo que se estaba escribiendo. Lo limpia un guardado que entra, o el botón.
     */
    abrirProgramaciones: function() {
      this.prog.abierto = true;
      this.prog.errorForm = null;
      // El espejo del almacén, ANTES de las tres lecturas: entre dos aperturas puede haber aparecido
      // un alta sin resolver de otra pestaña, o de la vez anterior en esta pantalla, y de él cuelga
      // la puerta de «Guardar» (ver `prog.altaPendienteEnElNavegador`).
      this.repasarElAltaPendiente();
      this.cargarDestinatariosProgramacion();
      this.cargarEnviosProgramados();
      this.recuperarAltaSinResolver();
      return this.cargarProgramaciones();
    },
    /** Cerrar el diálogo no cancela nada: una escritura en vuelo sigue y se aplica. */
    cerrarProgramaciones: function() {
      this.prog.abierto = false;
    },
    /**
     * **EL ALTA QUE SE QUEDÓ SIN RESOLVER**, recuperada del navegador y preguntada al servidor.
     *
     * Esto es lo que hace que la clave de un alta sirva de algo después de una recarga. Hasta ahora
     * vivía solo en `prog.form`: al recargar el navegador, o al salir de la pantalla y volver con el
     * `POST` todavía en vuelo, el formulario se recreaba con `claveAlta: null` y la siguiente
     * pulsación generaba otra clave. Si el primer intento sí llegó a aplicarse, el servidor no podía
     * reconocer el segundo como reintento y creaba **una segunda regla activa**, con lo que el
     * informe salía dos veces, cada día, hasta que alguien lo notara.
     *
     * Y lo que se hace con lo recuperado es **preguntar, nunca repetir**: reenviar solo al abrir el
     * diálogo sería dar de alta algo que nadie ha pulsado. Las dos salidas siguen siendo las de
     * siempre y las decide una persona: «Reintentar» —que el servidor no duplica— o «Descartar este
     * alta».
     *
     * No se pisa lo que haya escrito delante: si el formulario está a medias o se está editando una
     * regla, lo recuperado espera a la apertura siguiente. Lo que no puede pasar es que recuperar un
     * borrador se lleve por delante el trabajo de alguien, que es el defecto de al lado.
     */
    recuperarAltaSinResolver: function() {
      const self = this;
      // Con el diálogo cerrado no se pregunta por nada. Hace falta porque esto lo llama también
      // `limpiarFormProgramacion`, y a ese se llega además desde el guardado que entra: cerrar el
      // diálogo no cancela una escritura en vuelo, así que la respuesta puede llegar con el diálogo
      // ya cerrado y una consulta lanzada ahí escribiría en un formulario que nadie está viendo.
      if (!self.prog.abierto) return Promise.resolve();
      // Y no dos a la vez: cerrar y reabrir el diálogo vuelve a entrar aquí, y dos consultas en
      // vuelo acabarían con la segunda contestando sobre un formulario que ya decidió la primera.
      if (self.prog.recuperando) return Promise.resolve();
      if (self.prog.altaIncierta || self.prog.guardando) return Promise.resolve();
      if (self.prog.form.id || self.progFormModificado) return Promise.resolve();
      const pendientes = self.altasPendientesApuntadas();
      if (!pendientes.length) return Promise.resolve();
      // La más antigua primero, que es como las ordena el registro: es la que lleva más tiempo sin
      // saberse, y las demás se irán resolviendo en las aperturas siguientes.
      const apunte = pendientes[0];
      // MIENTRAS ESTO NO SE RESUELVA NO SE DA DE ALTA NADA (P1 Codex ronda 4). Se pone ANTES de
      // lanzar la consulta: corre en paralelo con las dos listas, y si estas contestaban primero el
      // botón «Guardar» se encendía y se podía crear un alta nueva con la anterior todavía sin
      // saberse. Eso es justo lo que este mecanismo venía a evitar.
      cerrojoDeLaSesion(self.prog, 'recuperando');
      // El estado de la sesión con el que sale la consulta. Lo que vuelva solo escribe en el
      // formulario si sigue siendo este: ver `recordarFormProgramacion`.
      const sesion = self.prog.sesionForm;
      return FactCobro.estadoOperacionControlDiario(apunte.clave).then(function(res) {
        self.prog.recuperando = false;
        self.resolverAltaRecuperada(apunte, unwrapDto(res) || {}, sesion);
      }, function(err) {
        self.prog.recuperando = false;
        // No se ha podido preguntar: el apunte **se conserva** —es la única manera de volver a
        // preguntar— y lo que se dice no afirma nada de lo que pasó.
        if (!self.laSesionSigueSiendo(sesion)) return;
        self.traerBorradorRecuperado(apunte);
        self.retenerAltaIncierta('Quedó un alta sin resolver de antes y no se ha podido comprobar '
          + 'cómo acabó' + (err && err.message ? ' (' + err.message + ')' : '') + '. Mira la lista '
          + 'de arriba: si ya está, pulsa «Descartar este alta»; si no está, «Reintentar» la manda '
          + 'tal cual y el servidor NO la duplica.');
      });
    },
    /**
     * **Si lo que hay delante sigue siendo lo que había** cuando salió una consulta que al volver va
     * a escribir en el formulario (decisión de @Donchurru, punto 2).
     *
     * Una sola propiedad por la que pasan los dos retornos de `recuperarAltaSinResolver`, y no una
     * lista de condiciones repetida en cada uno: es la misma forma de fallo que ya costó rondas
     * —cada camino decidiendo por su cuenta— y lo que se mide es siempre lo mismo.
     *
     * Las cuatro cosas que lo invalidan, y ninguna de más:
     *
     *  - **el formulario se ha vuelto a cargar** (`sesionForm`): se está editando una regla, se ha
     *    limpiado, o otra recuperación ha entrado antes;
     *  - **hay una escritura en vuelo** (`guardando`): sustituir el formulario mientras se manda
     *    dejaría en pantalla unos datos distintos de los que se están guardando;
     *  - **ya hay un alta incierta puesta**: la que esté delante es la que se puede repetir con su
     *    clave, y pisarla con otra dejaría una de las dos sin salida;
     *  - **quien está delante ha escrito algo**: su borrador no se tira por una respuesta tardía, que
     *    es la regla que ya gobierna «Editar».
     *
     * Cuando no se puede aplicar **no se pierde nada y no se abre nada**. No se pierde porque el
     * apunte sigue en el navegador, así que «Limpiar» —o la apertura siguiente del diálogo— vuelve a
     * preguntar por él. Y no se abre porque de ese mismo apunte cuelga la puerta de «Guardar»
     * (`progAltaApuntadaSinAtender`): hasta aquí, salir por este `return` dejaba `recuperando` en
     * falso y la retención sin poner, y «Guardar» quedaba habilitado para mandar el borrador nuevo
     * con una clave nueva mientras la anterior podía estar ya creada (P1 Codex 19/10/2026). Eso ya no
     * depende de que este camino —ni el que se añada mañana— se acuerde de cerrar nada.
     */
    laSesionSigueSiendo: function(sesion) {
      return this.prog.sesionForm === sesion
        && !this.prog.guardando
        && !this.prog.altaIncierta
        && !this.prog.form.id
        && !this.progFormModificado;
    },
    /**
     * Qué hacer con el alta recuperada, según lo que conteste el servidor.
     *
     * Las cinco situaciones del registro, y ninguna se interpreta aquí de más: el texto que se
     * enseña es **el del servidor**, porque es una afirmación sobre lo que sabe y sobre lo que no.
     *
     *  - `HECHA`: quedó creada. Se suelta el apunte y se dice; la regla está en la lista.
     *  - `NO_CONSTA` y `RETOMABLE`: no se aplicó nada, así que repetirla es seguro… **si ya se puede
     *    afirmar**. Un `NO_CONSTA` de hace cinco segundos puede ser «todavía no me consta» con el
     *    `POST` llegando, y ahí lo que toca es esperar.
     *  - `EN_CURSO` y `DESCONOCIDO`: puede estar creada. No se repite por su cuenta.
     */
    resolverAltaRecuperada: function(apunte, estado, sesion) {
      const self = this;
      // «HECHA» se aplica SIEMPRE: suelta el apunte y avisa, no escribe en el formulario. Es la
      // única de las cinco salidas que no toca lo que hay delante, y callarla porque entretanto
      // alguien se puso a escribir dejaría la clave en el navegador para volver a preguntar por un
      // alta que ya se sabe que quedó creada.
      if (estado.situacion === 'HECHA' && (!estado.operacion || estado.operacion === apunte.clave)) {
        self.olvidarElAlta(apunte.clave);
        self.prog.aviso = 'El alta que se quedó a medias la última vez SÍ quedó creada: está en la '
          + 'lista de arriba. No se ha vuelto a crear.';
        return;
      }
      // LAS DEMÁS ESCRIBEN EN EL FORMULARIO, así que solo entran si lo que hay delante sigue siendo
      // lo que había cuando salió la consulta (P1 Codex ronda 4). Antes esto sustituía `prog.form`
      // sin condición: una respuesta que llegaba tarde se llevaba por delante el alta que alguien
      // acababa de empezar —o la que ya estaba en vuelo— y lo que se guardaba después no era lo que
      // estaba en pantalla. El apunte se conserva, así que «Limpiar» o la apertura siguiente vuelven
      // a preguntar, y mientras tanto es ese apunte el que mantiene «Guardar» apagado: ver
      // `progAltaApuntadaSinAtender`.
      if (sesion !== undefined && !self.laSesionSigueSiendo(sesion)) return;
      // La respuesta tiene que ser de ESTA operación: una sin identificar aplicada al formulario
      // diría de un alta lo que se sabe de otra.
      if (!estado.situacion || (estado.operacion && estado.operacion !== apunte.clave)) {
        self.traerBorradorRecuperado(apunte);
        self.retenerAltaIncierta('Quedó un alta sin resolver de antes y el servidor no ha dicho '
          + 'cómo acabó. Mira la lista de arriba: si ya está, pulsa «Descartar este alta»; si no '
          + 'está, «Reintentar» la manda tal cual y el servidor NO la duplica.');
        return;
      }
      self.traerBorradorRecuperado(apunte);
      const sinEfecto = SITUACIONES_SIN_EFECTO.indexOf(estado.situacion) > -1;
      const acreditado = Date.now() - (apunte.intentadaEn || apunte.cuando || 0)
        >= MARGEN_HASTA_QUE_PUDO_LLEGAR;
      if (sinEfecto && acreditado) {
        self.retenerAltaIncierta((estado.texto ? estado.texto + ' ' : '')
          + 'Lo que quedó escrito está abajo tal cual: pulsa «Reintentar» para darla de alta, o '
          + '«Descartar este alta» si ya no la quieres.');
        return;
      }
      self.retenerAltaIncierta((estado.texto ? estado.texto + ' ' : 'No se sabe cómo acabó un alta '
        + 'de antes. ') + 'Mira la lista de arriba: si ya está, pulsa «Descartar este alta»; si no '
        + 'está, «Reintentar» la manda tal cual y el servidor NO la duplica.');
    },
    /**
     * Trae al formulario el borrador del alta recuperada, **con su clave**.
     *
     * Con la clave y no sin ella: es lo único que hace que «Reintentar» sea un reintento y no un
     * alta nueva. Y se recuerda como foto, para que un borrador recuperado no cuente como «hay algo
     * sin guardar» antes de que nadie lo toque.
     *
     * Y NO se poda contra la lista de destinatarios de ahora, al contrario que al editar una regla:
     * un reintento tiene que mandar **exactamente** lo mismo que se mandó, porque el servidor
     * reconoce el reintento por el contenido —con un grupo de menos sería otra cosa con la clave
     * repetida y lo rechazaría—. Si de verdad ya no existe un grupo, lo dice la API al reintentar,
     * con el motivo dentro, y entonces se descarta el alta y se hace una nueva.
     *
     * **Y con el cuerpo que se mandó, no con el formulario** (decisión de @Donchurru, punto 3). El
     * formulario es lo que se enseña; lo que se vuelve a mandar es `prog.cuerpoComprometido`, que
     * viene del apunte y no se recompone. Son dos cosas distintas a propósito: el formulario pasa por
     * los chips, que se dibujan contra el catálogo de ahora, y el cuerpo no puede depender de eso.
     */
    traerBorradorRecuperado: function(apunte) {
      this.prog.form = Object.assign(formProgramacionNuevo(), apunte.borrador,
        { id: null, visto: null, claveAlta: apunte.clave });
      // El cuerpo de verdad, el que se comprometió con esa clave. Los apuntes de antes de este
      // cambio no lo llevan: entonces se queda a nulo y «Reintentar» lo dice en vez de recomponerlo
      // del catálogo de ahora, que es justo lo que no puede hacer.
      this.prog.cuerpoComprometido = apunte.cuerpo || null;
      // Y el apunte entero, que es con lo que se repone si otra pestaña se lo lleva del almacén
      // mientras esta sigue en duda: ver `retenerAltaIncierta`.
      this.prog.apunteDelAlta = apunte;
      this.prog.altaSinApunte = false;
      this.recordarFormProgramacion();
    },
    /**
     * Las reglas de este informe, las más nuevas primero. Si la lectura falla, el diálogo lo dice y
     * no se queda con la lista de la apertura anterior: una lista vieja se leería como «estas son
     * las programaciones que hay», que es justo lo que no se sabe.
     *
     * **Y solo escribe la lectura vigente** (P2 Codex ronda 2). Esta lista se relee al abrir el
     * diálogo y después de cada guardado, pausa o borrado, así que es fácil tener dos GET en vuelo:
     * cerrar y volver a abrir antes de que conteste el primero, o guardar mientras llega. Sin
     * numerarlas, la respuesta antigua que llegue al final pisa la lista recién cargada —o borra su
     * error— y el diálogo enseña reglas que ya no están así. Es el mismo mecanismo que la lista de
     * destinatarios (`lecturaEnCurso`) y está aquí porque era el mismo olvido.
     */
    cargarProgramaciones: function() {
      const self = this;
      const lectura = ++self.prog.lecturaLista;
      cerrojoDeLaSesion(self.prog, 'cargando');
      self.prog.error = null;
      return FactCobro.programacionesControlDiario().then(function(res) {
        if (lectura !== self.prog.lecturaLista) return;
        self.prog.cargando = false;
        self.prog.lista = (res && res.list) || [];
      }).catch(function(err) {
        if (lectura !== self.prog.lecturaLista) return;
        self.prog.cargando = false;
        self.prog.lista = [];
        self.prog.error = motivoDeLaProgramacion(err,
          'No se han podido cargar las programaciones.');
      });
    },
    /**
     * El historial. Su fallo es suyo: no impide ver ni tocar las reglas.
     *
     * Numerado por lo mismo que la lista de reglas, aunque la revisión no lo señalara: se lee en la
     * misma apertura y escribe el mismo estado, así que tiene exactamente el mismo problema. Lo que
     * aquí se vería es un envío fallido que desaparece —o uno antiguo que reaparece— al reabrir el
     * diálogo, y este historial es lo único que cuenta que un envío no salió (criterio 8).
     */
    cargarEnviosProgramados: function() {
      const self = this;
      const lectura = ++self.prog.lecturaEnvios;
      self.prog.envios.error = null;
      return FactCobro.enviosProgramadosControlDiario(20).then(function(res) {
        if (lectura !== self.prog.lecturaEnvios) return;
        self.prog.envios.lista = (res && res.list) || [];
      }).catch(function(err) {
        if (lectura !== self.prog.lecturaEnvios) return;
        self.prog.envios.lista = [];
        self.prog.envios.error = motivoDeLaProgramacion(err,
          'No se ha podido cargar el historial de envíos.');
      });
    },
    /**
     * Los grupos y las personas a las que se puede programar el informe, de **la misma lectura**
     * que usa el diálogo de «Enviar reporte» (`FactCobro.destinatariosInforme`). No hay una lista
     * nueva: es la misma, y por eso un alta o una baja se ven igual en los dos sitios.
     *
     * Si la lectura falla, el formulario **no se bloquea del todo**: una programación a correos
     * escritos a mano se puede seguir dando de alta, y el aviso lo dice. Lo que sí se bloquea es
     * guardar lo que cuelga de la lista —grupos y personas—, porque sin ella no se puede cruzar:
     * ver `motivoNoSePuedeGuardarLaProgramacion`.
     *
     * Y los dos caminos acaban en `descartarLoQueNoSeVeEnLaProgramacion`, que es lo que impide que
     * una elección cargada de una regla sobreviva a la lista que la dibujaba… **cuando hay lista**.
     * Con la lectura caída esa misma llamada no poda nada, a propósito: ver su cabecera.
     */
    cargarDestinatariosProgramacion: function() {
      const self = this;
      const lectura = ++self.prog.lecturaEnCurso;
      cerrojoDeLaSesion(self.prog, 'cargandoDestinatarios');
      self.prog.errorDestinatarios = null;
      self.prog.descartados = null;
      return FactCobro.destinatariosInforme().then(function(res) {
        if (lectura !== self.prog.lecturaEnCurso) return;
        const dto = unwrapDto(res) || {};
        self.prog.cargandoDestinatarios = false;
        self.prog.grupos = dto.grupos || [];
        self.prog.notaGrupos = dto.nota || null;
        // Lo que el servidor propone —ADMIN y DIRECTOR, los que ven la pestaña— y solo con el
        // formulario como nuevo: lo que haya elegido quien está delante, o lo que traiga la regla
        // que se está editando, no se pisa proponiendo nada encima.
        //
        // Se mira también que NO sea una edición, y no solo que el formulario esté vacío: una regla
        // que se guardó sin ninguna de las tres vías —no se puede guardar así hoy, pero una regla
        // antigua o escrita por otro camino sí puede estar— se abriría a editar y recibiría los
        // grupos por omisión encima, o sea destinatarios que nadie ha elegido.
        if (!self.prog.form.id && self.progFormComoNuevo()) {
          const porOmision = self.prog.grupos
            .filter(function(grupo) { return grupo.porOmision; })
            .map(function(grupo) { return grupo.codigo; });
          self.prog.form.elegidosGrupos = porOmision;
          // Y la foto va con ello: lo propone el servidor, no lo escribe quien está delante, así
          // que no puede contar como «hay un borrador sin guardar». Sin esto, abrir el diálogo
          // dejaba el formulario modificado de entrada y «Editar» preguntaba siempre.
          if (self.prog.formOriginal) {
            self.prog.formOriginal.elegidosGrupos = porOmision.slice();
          }
        }
        self.descartarLoQueNoSeVeEnLaProgramacion();
      }).catch(function(err) {
        if (lectura !== self.prog.lecturaEnCurso) return;
        self.prog.cargandoDestinatarios = false;
        self.prog.grupos = [];
        self.prog.notaGrupos = null;
        self.prog.errorDestinatarios = err && err.message
          ? 'No se ha podido cargar la lista de destinatarios: ' + err.message
          : 'No se ha podido cargar la lista de destinatarios.';
        self.descartarLoQueNoSeVeEnLaProgramacion();
      });
    },
    /** Si el formulario está como recién abierto: sin nada elegido por ninguna de las tres vías. */
    progFormComoNuevo: function() {
      return !this.prog.form.elegidosGrupos.length
        && !this.prog.form.elegidosPersonas.length
        && !correosDe(this.prog.form.emails).length;
    },
    /**
     * Deja elegido **solo lo que el formulario está dibujando**, y lo dice si ha quitado algo.
     *
     * Mismo criterio que `descartarLoQueNoSeVe` en el diálogo de envío, y hace falta por lo mismo y
     * por una razón más: al **editar una regla** se cargan los grupos y las personas que se
     * guardaron, y entre medias puede haber una baja. Sin esto, el formulario enseñaría menos
     * destinatarios de los que iba a guardar y guardaría un `username` que la API rechaza.
     *
     * Los correos escritos a mano no se tocan: esos sí están delante, en su campo.
     *
     * **Y no poda un alta incierta** (decisión de @Donchurru, punto 3). Lo que ese formulario enseña
     * es lo que se pidió, con su clave: quitarle un grupo que ya no está en el catálogo cambiaría en
     * pantalla lo que se mandó, y lo que «Reintentar» tiene que volver a mandar es exactamente
     * aquello (`prog.cuerpoComprometido`). Si de verdad ese destinatario ya no existe, lo dice el
     * servidor al reintentar y entonces la salida es «Descartar este alta» y hacer una nueva, que es
     * un desenlace claro y una operación nueva. Antes se podaba desde las dos salidas de la lectura
     * de destinatarios, que son las que corren justo después de recuperar el borrador.
     *
     * **Y no poda si no hay lista vigente** (P1 Codex 19/10/2026). Es lo mismo contado de otra
     * manera: podar es quitar lo que la lista ya no tiene, y una lista que no se ha podido leer no
     * dice que no tenga nada —dice que no se sabe—. El `catch` de la lectura dejaba `prog.grupos` a
     * vacío y llamaba aquí, así que una regla abierta para editar perdía en el formulario todos sus
     * grupos y todas sus personas, con el aviso afirmando que «ya no están en esta lista». Lo
     * que se hace mientras no se sabe es conservarlo y decirlo (`progElegidosQueNoSeDibujan`),
     * con «Guardar» apagado y explicando por qué.
     */
    descartarLoQueNoSeVeEnLaProgramacion: function() {
      if (this.prog.altaIncierta) return;
      if (this.progSinListaDeDestinatarios) return;
      const codigos = this.progGruposVisibles.map(function(grupo) { return grupo.codigo; });
      const usernames = this.progPersonasElegibles.map(function(p) { return p.username; });
      const grupos = this.prog.form.elegidosGrupos.filter(function(codigo) {
        return codigos.indexOf(codigo) > -1;
      });
      const personas = this.prog.form.elegidosPersonas.filter(function(username) {
        return usernames.indexOf(username) > -1;
      });
      if (grupos.length === this.prog.form.elegidosGrupos.length
        && personas.length === this.prog.form.elegidosPersonas.length) return;
      this.prog.form.elegidosGrupos = grupos;
      this.prog.form.elegidosPersonas = personas;
      // La misma poda en la foto con la que se mira si hay un borrador sin guardar: esto lo quita
      // la pantalla porque ya no está en la lista, no quien está delante, así que no puede
      // convertir un formulario intacto en uno «modificado» —y al revés, un borrador de verdad
      // sigue siéndolo, porque de la foto solo se poda lo mismo que del formulario—.
      if (this.prog.formOriginal) {
        this.prog.formOriginal.elegidosGrupos = (this.prog.formOriginal.elegidosGrupos || [])
          .filter(function(codigo) { return codigos.indexOf(codigo) > -1; });
        this.prog.formOriginal.elegidosPersonas = (this.prog.formOriginal.elegidosPersonas || [])
          .filter(function(username) { return usernames.indexOf(username) > -1; });
      }
      this.prog.descartados = 'Algo de lo que tenías elegido ya no está en esta lista, así que se '
        + 'ha quitado. Vuelve a elegir a quién se lo mandas antes de guardar.';
    },
    progGrupoElegido: function(codigo) {
      return this.prog.form.elegidosGrupos.indexOf(codigo) > -1;
    },
    alternarGrupoProg: function(codigo) {
      this.alternarElegidoProg(this.prog.form.elegidosGrupos, codigo);
    },
    progPersonaElegida: function(username) {
      return this.prog.form.elegidosPersonas.indexOf(username) > -1;
    },
    alternarPersonaProg: function(username) {
      this.alternarElegidoProg(this.prog.form.elegidosPersonas, username);
    },
    /**
     * Marca o desmarca un chip del formulario, y es **el único sitio** que cambia su elección.
     *
     * Un chip es un `<span>` y no tiene `:disabled`, así que el clic entra igual mientras se
     * guarda: es este método el que lo para. Con la comprobación repartida en los dos `alternar*`,
     * uno de los dos se habría olvidado.
     *
     * Y mira `progFormRetenido` y no `prog.guardando`, que es lo mismo que miran los `:disabled` de
     * los campos: con un alta de resultado incierto pendiente, cambiar los destinatarios sería
     * escribir unos datos que esa clave ya no puede guardar.
     */
    alternarElegidoProg: function(lista, valor) {
      if (this.progFormRetenido) return;
      const donde = lista.indexOf(valor);
      if (donde > -1) lista.splice(donde, 1);
      else lista.push(valor);
    },
    /**
     * Trae una regla al formulario para editarla.
     *
     * Se guarda su `modificadaEn` en `visto`: es lo que la API compara al escribir, así que si
     * alguien la ha cambiado mientras tanto no se escribe nada y se contesta que recargues, en vez
     * de pisar lo suyo con la foto que esta pantalla tenía abierta.
     */
    editarRegla: function(regla) {
      const self = this;
      if (self.bloqueoDeRegla(regla)) return;
      if (self.motivoNoSePuedeCambiarDeRegla) return;
      // UN BORRADOR A MEDIO ESCRIBIR NO SE TIRA EN SILENCIO (P2 Codex ronda 2). Antes esta
      // asignación sustituía el formulario entero sin preguntar, así que pulsar «Editar» en
      // cualquier ficha se llevaba por delante el nombre, la periodicidad y los destinatarios que
      // alguien acababa de escribir. No se bloquea el botón —estorbaría el caso normal, que es
      // cambiar de regla sin nada pendiente—: se pregunta, y solo cuando hay algo que perder.
      if (self.progFormModificado) {
        self.$f7.dialog.confirm(
          'Lo que estás escribiendo en el formulario de abajo no se ha guardado, y al editar «'
            + (regla.nombre || 'sin nombre') + '» se perderá. ¿Lo descarto?',
          '¿Descartar lo que estabas escribiendo?',
          function() { self.traerReglaAlFormulario(regla); });
        return;
      }
      self.traerReglaAlFormulario(regla);
    },
    /** Trae la regla al formulario. Lo de verdad de `editarRegla`, ya resuelto si había borrador. */
    traerReglaAlFormulario: function(regla) {
      this.prog.errorForm = null;
      this.prog.aviso = null;
      this.prog.descartados = null;
      // Editar es otra cosa: una edición va condicionada al `visto` y no lleva clave de alta, así
      // que nada del alta puede quedarse colgando aquí. Si quedara el cuerpo, la pulsación siguiente
      // se tomaría por un reintento y mandaría el de otra operación; y si quedaran la entrada o el
      // aviso de «su clave no está guardada», la pantalla seguiría hablando de un alta que ya no
      // tiene delante. Las cuatro juntas y por el mismo sitio, que es lo que las mantiene iguales.
      //
      // **Y no se borra nada del navegador**: aquí no hay prueba de que esa operación acabara. Si
      // alguna quedara sin resolver, su apunte sigue donde estaba y es `progAltaApuntadaSinAtender`
      // quien frena el alta siguiente hasta que se resuelva.
      this.soltarLasReferenciasDelAlta();
      this.prog.form = {
        id: regla.id,
        visto: regla.modificadaEn ? new Date(regla.modificadaEn).getTime() : null,
        // Una edición no lleva clave de alta: va condicionada al `visto`, que es lo que hace que su
        // reintento no pueda escribir dos veces. Y se suelta la que hubiera del alta a medias, para
        // que no se reutilice en otra cosa.
        claveAlta: null,
        nombre: regla.nombre || '',
        frecuencia: regla.frecuencia || 'DIARIA',
        hora: regla.hora || '08:00',
        diaSemana: regla.diaSemana || 'LUNES',
        diaMes: regla.diaMes || 1,
        soloLaborables: !!regla.soloLaborables,
        elegidosGrupos: (regla.grupos || []).slice(),
        elegidosPersonas: (regla.personasCodigo || []).slice(),
        emails: (regla.emailsExtra || []).join(', '),
        activa: !!regla.activa
      };
      // Lo que la regla traía puede no estar ya en la lista: se quita y se dice, igual que en el
      // diálogo de envío.
      //
      // PERO NO MIENTRAS LA LISTA SE ESTÁ LEYENDO. Si la lista de reglas contesta antes que la de
      // destinatarios, aquí `progGruposVisibles` todavía está vacía —a propósito: mientras se relee
      // no hay lista vigente—, y descartar contra una lista vacía **borra todos los grupos y las
      // personas de la regla** y además deja el formulario «como nuevo», con lo que la lectura, al
      // llegar, le propondría encima los grupos por omisión. Guardar después cambiaría en silencio
      // a quién se le manda el informe. Así que el descarte se aplaza: lo hace la propia lectura al
      // terminar —por su `then` y por su `catch`, los dos llaman aquí—, y entonces sí hay lista
      // contra la que descartar. Entre tanto, «Guardar» está deshabilitado y dice por qué
      // (`motivoNoSePuedeGuardarLaProgramacion`).
      if (!this.prog.cargandoDestinatarios) this.descartarLoQueNoSeVeEnLaProgramacion();
      // La foto con la que se compara si después se ha tocado algo. Se toma AQUÍ y no al abrir el
      // diálogo: lo que cuenta como «modificado» es lo que cambie respecto a la regla que se
      // acaba de cargar, no respecto al formulario vacío.
      this.recordarFormProgramacion();
    },
    /**
     * Guarda la foto del formulario tal como está, que es con la que `progFormModificado` compara.
     *
     * Lo llaman los tres sitios que cargan el formulario —`traerReglaAlFormulario`,
     * `limpiarFormProgramacion` y `traerBorradorRecuperado`— y nadie más: cada sitio que ponga el
     * formulario y se olvide de esto dejaría un borrador que se cree modificado desde el principio, y
     * entonces «Editar» preguntaría siempre y la pregunta dejaría de significar nada.
     *
     * **Y por eso aquí sube el nº de sesión** (`prog.sesionForm`), y no en cada uno de los tres: es
     * el único sitio por el que pasan todos, así que el camino que cargue el formulario mañana entra
     * en el contrato sin que nadie se acuerde. Lo que ese número significa es «lo que hay delante ya
     * no es lo que había cuando tú saliste», y lo miran los dos llamantes que escriben el formulario
     * desde una respuesta que llega tarde (`recuperarAltaSinResolver`).
     */
    recordarFormProgramacion: function() {
      this.prog.formOriginal = JSON.parse(JSON.stringify(this.prog.form));
      this.prog.sesionForm += 1;
    },
    /**
     * Deja el formulario como nuevo. No borra nada del servidor: solo deja de editar.
     *
     * Las tres vías se vacían, y con eso la propuesta de por omisión vuelve a aplicarse la próxima
     * vez que se lea la lista (ver `progFormComoNuevo`). No se propone aquí a mano para no tener
     * dos sitios que decidan lo mismo.
     *
     * Y **suelta la clave del alta**, que es lo que corresponde: esto se llama cuando el alta ya ha
     * salido bien —y entonces su clave está gastada— o cuando quien está delante pulsa «Limpiar»
     * para empezar otra, que es un alta distinta y no el reintento de la anterior.
     */
    limpiarFormProgramacion: function() {
      this.prog.errorForm = null;
      this.prog.descartados = null;
      // La operación que el formulario tenía delante queda resuelta AQUÍ, porque esto es **el único
      // sitio donde una persona dice que da el alta por vista**: o salió bien, o ha pulsado
      // «Limpiar» o «Descartar este alta» a sabiendas. Suelta su apunte del navegador y con él las
      // cuatro referencias —la clave, el cuerpo, la entrada y la retención—, que es lo que impide
      // que la siguiente pulsación se tome por el reintento de esta o reutilice su clave.
      this.darPorResueltaLaOperacionDelAlta();
      this.prog.form = formProgramacionNuevo();
      this.recordarFormProgramacion();
      // Y SI QUEDA OTRA SIN RESOLVER, SE VUELVE A PREGUNTAR POR ELLA sin tener que cerrar y abrir
      // el diálogo: es la salida que el motivo de «Guardar» promete cuando manda pulsar «Limpiar»
      // (ver `progAltaApuntadaSinAtender`). Con el almacén ya limpio no hace nada.
      this.recuperarAltaSinResolver();
    },
    /**
     * **LA ÚNICA PUERTA DE LA PETICIÓN QUE ESCRIBE LA PROGRAMACIÓN**, o `null` si se puede mandar.
     *
     * Por aquí pasan los cuatro: el `:disabled` de «Guardar», el `:disabled` de «Reintentar» y los
     * dos métodos que mandan. Es un método y no dos propiedades porque las dos pulsaciones acaban
     * en la misma petición y lo único que cambia entre ellas es si **esta** es la que resuelve el
     * alta retenida: con dos listas de condiciones separadas, una se queda vieja —y la que se quedó
     * vieja fue justo la de «Reintentar»—.
     *
     * @param {boolean} esResolucionDeAltaIncierta si lo que se va a mandar es la repetición del alta
     *        que está retenida, y no una escritura nueva.
     */
    motivoParaNoMandarLaProgramacion: function(esResolucionDeAltaIncierta) {
      // Un alta de resultado incierto no se guarda otra vez por «Guardar»: se resuelve con sus dos
      // botones, que son los únicos que saben que la clave ya puede estar gastada. Va primero
      // porque es lo único que hay que hacer mientras esté puesto.
      if (this.prog.altaIncierta && !esResolucionDeAltaIncierta) {
        return 'Hay un alta de la que no se sabe si quedó creada. Resuélvela ahí abajo —«Reintentar» '
          + 'o «Descartar este alta»— antes de seguir.';
      }
      // Y TAMPOCO SE GUARDA CON UNA SIN RESOLVER QUE EL FORMULARIO NO TIENE DELANTE, que es la
      // misma regla dicha donde no hace falta que nadie se acuerde: ver
      // `prog.altaPendienteEnElNavegador` y `progAltaApuntadaSinAtender`. Un reintento no pasa por
      // aquí —es la resolución de la suya— y una edición tampoco: va condicionada al `visto`, así
      // que no puede escribir dos veces.
      if (this.progAltaApuntadaSinAtender && !esResolucionDeAltaIncierta && !this.prog.form.id) {
        return 'Quedó un alta de antes de la que no se sabe si llegó a crearse, y el formulario no '
          + 'la tiene delante. Hasta resolverla no se puede crear otra, porque podrían quedar dos '
          + 'iguales mandando el informe dos veces. Pulsa «Limpiar» aquí abajo: la pantalla volverá '
          + 'a preguntar por ella y te dará «Reintentar» o «Descartar este alta».';
      }
      // Y resolverla es REPETIR LA MISMA PETICIÓN, así que hace falta el cuerpo con el que se
      // comprometió: recomponerlo del catálogo de ahora mandaría otro contenido con la misma clave
      // y el servidor lo rechazaría (decisión de @Donchurru, punto 3). Pasa con un apunte guardado
      // por una versión anterior de la pantalla, y la salida es la otra: mirar la lista y descartar.
      if (esResolucionDeAltaIncierta && !this.prog.cuerpoComprometido) {
        return 'No se puede reintentar este alta: de la vez anterior no quedó guardado el contenido '
          + 'exacto que se mandó, y mandar algo distinto con el mismo identificador lo rechazaría el '
          + 'servidor. Mira la lista de arriba: si la programación está, pulsa «Descartar este '
          + 'alta»; si no está, pulsa «Descartar este alta» y vuelve a darla de alta, que será una '
          + 'operación nueva.';
      }
      if (!this.prog.form.id && this.motivoNoSePuedeDecidirUnAlta) {
        return this.motivoNoSePuedeDecidirUnAlta;
      }
      // SIN LISTA VIGENTE NO SE ESCRIBE LO QUE CUELGA DE ELLA (P1 Codex 19/10/2026). Lo que se
      // guarda sale de `progDestinatarios`, que cruza contra la lista, así que guardar sin ella
      // sustituye los grupos y las personas de la regla por lo único que sobrevive al cruce: los
      // correos escritos a mano. En silencio, y para siempre.
      //
      // Vale para las dos maneras de no tener lista y para el alta y la edición por igual, que es
      // lo que antes estaba repartido: la edición solo miraba `cargandoDestinatarios` y el alta
      // solo pasaba por `motivoNoSePuedeDecidirUnAlta`, así que el fallo de la lectura —que no es
      // ninguna de las dos— se colaba por los dos lados.
      //
      // Y solo si hay algo elegido que cruzar: una regla de correos sueltos no depende de la lista,
      // y bloquearla sería apagar «Guardar» sin que haya nada que perder. Un reintento tampoco
      // pasa por aquí: manda `cuerpoComprometido` tal cual.
      if (this.progSinListaDeDestinatarios && this.progElegidosDeLaLista
        && !this.progEsReintentoDeAlta) {
        if (this.prog.errorDestinatarios) {
          return 'No se ha podido leer la lista de destinatarios, y esta programación manda a '
            + 'grupos o a personas que salen de ella: guardarla ahora los quitaría. Pulsa '
            + '«Recargar» ahí arriba, junto al aviso de los destinatarios, y vuelve a intentarlo.';
        }
        return 'Espera a que termine de actualizarse la lista de destinatarios: hasta entonces no '
          + 'se puede saber a quién se le va a mandar.';
      }
      // Y LOS CAMPOS TAMPOCO MIDEN UN REINTENTO, por lo mismo que los destinatarios: lo que va a
      // viajar es `cuerpoComprometido`, no lo que digan los campos de ahora. Midiéndolos, un
      // borrador recuperado con un campo que esta versión ya no acepta dejaba «Reintentar» apagado
      // para siempre, con la clave gastada en el servidor y sin más salida que descartar.
      if (!this.progEsReintentoDeAlta) {
        const deLosCampos = motivoDeLosCampos(this.prog.form);
        if (deLosCampos) return deLosCampos;
      }
      // Un reintento tampoco se mide por el catálogo de ahora, por lo mismo: con la lista caída —o
      // con un grupo que ha desaparecido de ella— `progDestinatarios` se queda a nulo, y esta línea
      // dejaba el alta incierta sin ninguna de sus dos salidas: ni reintentar ni reconciliar. La
      // exención estaba puesta en el método y no en la puerta que el método consulta primero, así
      // que no llegaba a ejercerse nunca.
      if (!this.progDestinatarios && !this.progEsReintentoDeAlta) {
        return 'Elige a quién se le manda: al menos un grupo, una persona o un correo escrito a '
          + 'mano.';
      }
      return null;
    },
    /**
     * **Las altas de esta persona que siguen apuntadas en el navegador**, las más antiguas primero.
     *
     * Una sola definición de «qué cuenta como un alta sin resolver», y la miran los dos: la
     * recuperación, que las trae al formulario, y el espejo del que cuelga la puerta. Tienen que ser
     * **la misma** lista, porque si la puerta contara una que la recuperación no sabe traer, esa
     * cerraría «Guardar» sin que nada pudiera abrirlo: un callejón sin salida.
     */
    altasPendientesApuntadas: function() {
      return operacionesPendientes.pendientesDe(usuarioDeAhora())
        .filter(function(apunte) { return apunte.accion === 'ALTA' && apunte.borrador; });
    },
    /** Refresca el espejo del almacén: ver `prog.altaPendienteEnElNavegador`. */
    repasarElAltaPendiente: function() {
      const pendientes = this.altasPendientesApuntadas();
      this.prog.altaPendienteEnElNavegador = pendientes.length ? pendientes[0] : null;
    },
    /**
     * **EL ALMACÉN ES DEL NAVEGADOR, NO DE ESTA PESTAÑA**, así que lo que cambie en otra tiene que
     * entrar aquí (P1 Codex 19/10/2026).
     *
     * Las tres puertas de escritura refrescan el espejo —`apuntarElAlta`, `olvidarElAlta`,
     * `reponerElApunteDelAlta`— y la apertura del diálogo lo repasa, pero las cuatro son cosas que
     * pasan **en esta pestaña**. `localStorage` no es reactivo: con el diálogo abierto aquí, un alta
     * que otra pestaña apunte después no movía nada, y «Guardar» seguía encendido pudiendo mandar la
     * misma programación otra vez. Es la misma regla que el informe de abonos abiertos ya cumplía
     * con `escucharOtrasPestanas`.
     *
     * Y lo que se hace con el aviso es **lo mismo que en cualquier otro momento**: repasar el
     * espejo, conservar lo que esta pantalla tenga en duda y preguntar al servidor por lo que haya
     * quedado sin resolver. Ninguna de las tres decide nada por su cuenta y las tres tienen ya sus
     * cerrojos, así que esto no es un camino nuevo: es el mismo, disparado por otro motivo.
     */
    escucharElRegistroDeOtrasPestanas: function() {
      const self = this;
      // Fuera del navegador no hay nada que escuchar —el banco de pruebas monta el componente en
      // Node—, y registrarlo dos veces atendería cada cambio dos veces.
      if (typeof window === 'undefined' || !window.addEventListener) return;
      if (self.alCambiarOtraPestana) return;
      self.alCambiarOtraPestana = function(evento) {
        const clave = evento && evento.key;
        // El navegador avisa de TODAS las claves que cambian. Quién es de este registro lo contesta
        // el módulo que las nombra, no un prefijo copiado aquí. Un `key` vacío es un `clear()`: ahí
        // sí hay que mirar, porque puede haberse llevado los apuntes.
        if (clave && !operacionesPendientes.esSuya(clave)) return;
        self.alCambiarElRegistroDeOtraPestana();
      };
      window.addEventListener('storage', self.alCambiarOtraPestana);
    },
    dejarDeEscucharElRegistroDeOtrasPestanas: function() {
      if (typeof window === 'undefined' || !window.removeEventListener) return;
      if (!this.alCambiarOtraPestana) return;
      window.removeEventListener('storage', this.alCambiarOtraPestana);
      this.alCambiarOtraPestana = null;
    },
    /**
     * Otra pestaña ha tocado el registro. Las tres cosas, y en este orden:
     *
     *  - **el espejo**, lo primero: de él cuelga la puerta de «Guardar», y hasta que no se refresca
     *    la puerta está decidiendo con una foto vieja;
     *  - **lo que esta pantalla tenga en duda sigue apuntado**, porque el cambio de la otra pestaña
     *    puede haber sido precisamente llevarse su apunte;
     *  - y **se pregunta** por lo que haya quedado sin resolver, que es lo único que cierra el aviso.
     *    Con el diálogo cerrado, con una escritura en vuelo o con algo escrito delante no hace nada:
     *    lo frena `recuperarAltaSinResolver`, que es donde esa decisión vive.
     */
    alCambiarElRegistroDeOtraPestana: function() {
      this.repasarElAltaPendiente();
      this.conservarElApunteDelAltaEnDuda();
      this.recuperarAltaSinResolver();
    },
    /**
     * **Apunta el alta en el navegador antes de lanzarla.** Contesta si ha quedado apuntada.
     *
     * Una de las tres puertas por las que esta pantalla escribe en el almacén, y existen para que el
     * espejo no pueda quedarse viejo: escribir en el almacén y refrescar el espejo son **la misma
     * operación**, no dos que haya que acordarse de hacer seguidas.
     */
    apuntarElAlta: function(entrada) {
      const apuntada = operacionesPendientes.guardar(entrada);
      this.repasarElAltaPendiente();
      return apuntada;
    },
    /** Suelta el apunte de esa clave. Solo con la prueba de que la operación acabó. */
    olvidarElAlta: function(clave) {
      operacionesPendientes.olvidar(clave);
      this.repasarElAltaPendiente();
    },
    /** Repone el apunte que otra pestaña se llevó. Contesta si ha quedado apuntado. */
    reponerElApunteDelAlta: function(entrada) {
      const apuntado = operacionesPendientes.reapuntar(entrada);
      this.repasarElAltaPendiente();
      return apuntado;
    },
    /**
     * **Suelta las cuatro referencias al alta comprometida**: su clave, su cuerpo, su apunte en
     * memoria y el aviso. Las cuatro o ninguna.
     *
     * Están juntas porque son **una sola cosa dicha cuatro veces** —«el formulario lleva delante
     * esta operación»— y soltarlas de una en una es lo que ha costado rondas: un camino soltaba el
     * apunte y el cuerpo y se dejaba la clave, y la pulsación siguiente mandaba el cuerpo corregido
     * con la clave ya gastada, que el servidor solo puede rechazar.
     *
     * **No borra nada del navegador**: eso es afirmar que la operación acabó, y lo hace
     * `darPorResueltaLaOperacionDelAlta`, que es quien tiene la prueba.
     */
    soltarLasReferenciasDelAlta: function() {
      this.prog.form.claveAlta = null;
      this.prog.cuerpoComprometido = null;
      this.prog.apunteDelAlta = null;
      this.prog.altaSinApunte = false;
      this.prog.altaIncierta = null;
    },
    /**
     * **Da por resuelta la operación que el formulario tiene delante**: suelta su apunte del
     * navegador y, con él, las cuatro referencias.
     *
     * Solo se llama donde hay prueba de que la operación acabó: el alta entró, el servidor acreditó
     * que no escribió nada, o una persona lo ha dicho con «Limpiar» o «Descartar este alta».
     */
    darPorResueltaLaOperacionDelAlta: function() {
      if (this.prog.form.claveAlta) this.olvidarElAlta(this.prog.form.claveAlta);
      this.soltarLasReferenciasDelAlta();
    },
    /**
     * **RETIENE EL ALTA DE LA QUE NO SE SABE SI QUEDÓ CREADA**, y la deja apuntada en el navegador.
     *
     * El único sitio desde el que se pone `prog.altaIncierta`, y es a propósito: la retención y el
     * apunte son **la misma cosa dicha dos veces** —«esta operación sigue sin resolverse»— y hasta
     * ahora cada camino ponía la primera y se olvidaba de la segunda.
     *
     * POR QUÉ HAY QUE REPONER UN APUNTE QUE SE ESCRIBIÓ ANTES DE LANZAR (P1 Codex 19/10/2026). Dos
     * pestañas comparten el almacén del navegador y **comparten la clave** de un alta recuperada: si
     * la pestaña A recibe un éxito, su `olvidar` borra esa entrada aunque la petición de B termine
     * después sin saberse. Un `clear()` del navegador hace lo mismo. En los dos casos la operación de
     * B sigue en duda y su clave ya no está en el almacén, así que al recargar B no puede ni
     * preguntar por ella ni repetirla como reintento: el intento siguiente lleva otra clave, el
     * servidor no lo reconoce y quedan **dos programaciones iguales** mandando el informe dos veces.
     *
     * Es la misma regla que ya cerró esto en el informe de abonos (`conservarApuntes`): **si la
     * pantalla lo tiene en duda, está apuntado**. Y va en un solo sentido —repone y nunca borra—,
     * porque borrar es afirmar que la operación acabó y eso solo lo dice el servidor o una persona.
     */
    retenerAltaIncierta: function(texto) {
      this.prog.altaIncierta = texto;
      this.conservarElApunteDelAltaEnDuda();
    },
    /**
     * **SI ESTA PANTALLA LO TIENE EN DUDA, ESTÁ APUNTADO.** La regla entera, en un solo sitio.
     *
     * Está aparte de `retenerAltaIncierta` porque poner la retención no es el único momento en que
     * el apunte puede faltar: también falta cuando es **otra pestaña** la que se lo lleva mientras
     * este ya estaba puesto, y de eso nos enteramos por `alCambiarElRegistroDeOtraPestana`. Con la
     * comprobación dentro de la retención, solo se cumplía en el instante de retener y el minuto
     * siguiente ya no la cumplía nadie.
     *
     * Va en un solo sentido —repone y **nunca borra**—, porque borrar es afirmar que la operación
     * acabó y eso solo lo dice el servidor o una persona.
     */
    conservarElApunteDelAltaEnDuda: function() {
      if (!this.prog.altaIncierta) return;
      const entrada = this.prog.apunteDelAlta;
      // Sin la entrada no hay nada que reponer: es el caso del apunte guardado por una versión
      // anterior, que `motivoParaNoMandarLaProgramacion` ya resuelve mandando a descartar.
      if (!entrada || !entrada.clave) return;
      if (operacionesPendientes.tieneApunte(entrada.clave)) {
        this.prog.altaSinApunte = false;
        return;
      }
      // Si el navegador no puede guardarlo no se puede hacer nada más —la petición ya salió— pero sí
      // se puede decir, y se dice junto a los dos botones que resuelven el alta (§9).
      this.prog.altaSinApunte = !this.reponerElApunteDelAlta(entrada);
    },
    /**
     * Guarda la programación: alta si no hay `id`, edición si lo hay. Lo llama «Guardar».
     *
     * Lo que viaja son **los códigos de los grupos y los usernames**, no los correos: los resuelve
     * la API en cada envío, así que un alta o una baja de personal cuentan sin tocar la regla. Y no
     * viaja de qué mes es el correo: el envío programado manda siempre el mes en curso.
     *
     * Un **alta** lleva además su clave, conservada entre reintentos: ver `mandarLaProgramacion` y
     * `nuevaClaveDeAlta`. Una edición no la necesita porque va condicionada al `visto`.
     */
    guardarRegla: function() {
      return this.mandarLaProgramacion(false);
    },
    /**
     * **Manda la programación**: lo que hacen «Guardar» y «Reintentar», que es la misma petición.
     *
     * La misma puerta que los dos `:disabled` (`motivoParaNoMandarLaProgramacion`) y no una copia:
     * entre la pulsación y el repintado hay un hueco, y el teclado llega al botón sin pasar por el
     * ratón.
     *
     * @param {boolean} esResolucionDeAltaIncierta si esto es «Reintentar» el alta retenida. Entonces
     *        —y solo entonces, y **después** de pasar la puerta— se suelta la retención: ver abajo.
     */
    mandarLaProgramacion: function(esResolucionDeAltaIncierta) {
      const self = this;
      if (self.prog.guardando
        || self.motivoParaNoMandarLaProgramacion(esResolucionDeAltaIncierta)) {
        return Promise.resolve();
      }
      const eraEdicion = !!self.prog.form.id;
      // **Si esto es el reintento de un alta ya comprometida**, que es lo que decide de dónde sale el
      // cuerpo y qué se puede concluir de un rechazo. Se mira ANTES de tocar nada.
      const esReintento = self.progEsReintentoDeAlta;
      const destinatarios = self.progDestinatarios;
      // Un reintento no pasa por aquí: lo que va a mandar son los destinatarios que ya se pidieron,
      // no los que se puedan elegir hoy. Si uno de ellos ha desaparecido del catálogo,
      // `progDestinatarios` se queda a nulo y el reintento quedaba bloqueado sin salida —ni mandar
      // ni reconciliar—, que es el caso de la ronda 4.
      if (!destinatarios && !esReintento) return Promise.resolve();
      // LA CLAVE DEL ALTA SE SACA UNA VEZ Y SE CONSERVA, que es lo que hace que un reintento sea un
      // reintento. Si se sacara una nueva en cada pulsación, el servidor no podría reconocerlo y un
      // «Guardar» cuya respuesta se pierde —un 408, un 504, un `status === 0`— acabaría creando dos
      // reglas activas iguales: el informe saldría dos veces en cada ejecución. El `catch` de abajo
      // NO la suelta cuando el resultado es incierto, a propósito.
      if (!eraEdicion && !self.prog.form.claveAlta) {
        self.prog.form.claveAlta = nuevaClaveDeAlta();
      }
      const claveAlta = self.prog.form.claveAlta;
      // EL CUERPO SE COMPONE UNA VEZ Y SE ATA A LA CLAVE (decisión de @Donchurru, punto 3). Lo que
      // viaja es esto, y un reintento vuelve a mandar esto mismo: `cuerpoDeLaProgramacion` sale de
      // `progDestinatarios`, que filtra contra el catálogo de ahora, así que recomponerlo en el
      // reintento mandaba otro contenido con la misma clave —y el servidor contesta 409 en vez de
      // reconciliar—. Una edición no lo necesita: va condicionada al `visto`.
      const cuerpo = esReintento ? self.prog.cuerpoComprometido
        : cuerpoDeLaProgramacion(self.prog.form, destinatarios);
      // Y SE GUARDA EN EL NAVEGADOR ANTES DE LANZAR, que es la otra mitad: en el formulario vive
      // solo mientras viva la página, así que una recarga, una pestaña cerrada o un «ir atrás» a
      // mitad del guardado se la llevaban y el intento siguiente iba con otra clave —el servidor ya
      // no lo reconoce como reintento y crea una segunda regla—. Antes de lanzar y no al volver la
      // respuesta: el caso que hay que poder recuperar es justo aquel cuya respuesta no llega.
      //
      // Y si no se puede apuntar, NO SE MANDA: una clave que no sobrevive a una recarga no es una
      // clave. Vale más no dar de alta que dar de alta sin poder saber después si quedó creada.
      const entradaDelApunte = eraEdicion ? null : {
        ambito: 'FORM',
        usuario: usuarioDeAhora(),
        accion: 'ALTA',
        entidad: null,
        clave: claveAlta,
        borrador: JSON.parse(JSON.stringify(self.prog.form)),
        // Y el cuerpo comprometido va DENTRO del apunte: es lo que lo hace sobrevivir a un F5, que
        // es cuando el formulario y los chips no existen y el catálogo se vuelve a leer de cero.
        cuerpo: JSON.parse(JSON.stringify(cuerpo)),
        intentadaEn: Date.now()
      };
      if (entradaDelApunte && !self.apuntarElAlta(entradaDelApunte)) {
        // UN REINTENTO NO SE PARA POR ESTO, y es la diferencia con un alta nueva: la petición de la
        // que viene YA SALIÓ y su clave puede estar gastada en el servidor, así que negarse a
        // repetirla le quita su única salida segura. Y su apunte sigue donde estaba —un `setItem`
        // que no cabe no borra lo que había—, así que lo que se refresca y se pierde es solo
        // `intentadaEn`. Lo que sí se mira es si la clave está apuntada, porque si no lo está hay
        // que decirlo antes de que alguien recargue (§9, junto a los dos botones).
        if (!esResolucionDeAltaIncierta) {
          self.prog.errorForm = NO_SE_PUDO_APUNTAR_EL_ALTA;
          return Promise.resolve();
        }
        self.prog.altaSinApunte = !operacionesPendientes.tieneApunte(claveAlta);
      }
      if (!eraEdicion) {
        self.prog.cuerpoComprometido = cuerpo;
        // Y la entrada con la que se repone ese apunte si otra pestaña se lo lleva: ver
        // `retenerAltaIncierta`.
        self.prog.apunteDelAlta = entradaDelApunte;
      }
      // LA RETENCIÓN SE SUELTA AQUÍ Y NO ANTES (P1 Codex 19/10/2026). Soltándola en «Reintentar»,
      // antes de llamar, cualquiera de las salidas de arriba —la puerta, el apunte que no cabe—
      // dejaba el formulario suelto, sin aviso y sin los dos botones, con la clave ya gastada en el
      // servidor: justo el estado del que la retención existe para protegerlo. Y la puerta se cierra
      // a menudo, porque las dos listas se leen en paralelo al abrir el diálogo. Desde esta línea la
      // petición sale; si vuelve sin respuesta, el `catch` la pone otra vez.
      if (esResolucionDeAltaIncierta) self.prog.altaIncierta = null;
      cerrojoDeLaSesion(self.prog, 'guardando');
      self.prog.errorForm = null;
      self.prog.aviso = null;
      return FactCobro.guardarProgramacionControlDiario(cuerpo, claveAlta).then(function() {
        self.prog.guardando = false;
        self.prog.aviso = eraEdicion ? 'La programación se ha guardado.'
          : 'La programación se ha dado de alta.';
        // Salió: ya no hay nada que reconciliar, así que el apunte del navegador se suelta. Se
        // suelta aquí con la clave que DE VERDAD se mandó, y no solo en `limpiarFormProgramacion`,
        // porque es aquí donde está la prueba; el de ahí abajo es entonces un no-op.
        if (!eraEdicion) self.olvidarElAlta(claveAlta);
        self.limpiarFormProgramacion();
        return self.cargarProgramaciones();
      }, function(err) {
        self.prog.guardando = false;
        const comoAcabo = comoAcaboLaProgramacion(err, eraEdicion);
        // El apunte del navegador se suelta SOLO con la prueba de que no se escribió nada, que es
        // la misma regla que sigue el registro del servidor. Lo demás se conserva —y es lo que hace
        // que esto sobreviva a un F5—, porque soltar lo que no se sabe deja la operación sin clave
        // con la que preguntar.
        //
        // **Y UN REINTENTO NO SUELTA NUNCA LA REFERENCIA** (decisión de @Donchurru, punto 3). Un
        // `4xx` acredita que ESTA petición no escribió, y para el primer intento eso basta. Para un
        // reintento no dice nada del primero, que es el que podía haber quedado escrito: el 409 de
        // clave reutilizada significa precisamente que esa operación ya existe en el servidor.
        // Soltándola, la pantalla fingía que no se escribió y se quedaba sin nada con lo que volver
        // a preguntar.
        //
        // Y SE SUELTA **LA CLAVE TAMBIÉN**, no solo el apunte y el cuerpo (P2 Codex 19/10/2026).
        // Conservándola, la corrección siguiente —el rechazo es casi siempre un dato que hay que
        // arreglar: una hora que no sale a su hora, un grupo que ya no existe— viajaba con la clave
        // de la petición anterior y otro contenido. Eso el servidor solo lo puede rechazar, así que
        // cada corrección recibía un conflicto y el formulario se quedaba sin salida. Un rechazo
        // acreditado es el final de esa operación: lo siguiente es un alta nueva, con su clave.
        if (!eraEdicion && !esReintento && seAcreditaQueNoSeEscribio(err)) {
          self.darPorResueltaLaOperacionDelAlta();
        }
        // El motivo se queda en el formulario, con lo escrito dentro: los rechazos de la API —una
        // hora que no sale a su hora, una persona sin correo, un grupo que no existe— se arreglan
        // ahí mismo, y limpiarlo obligaría a escribirlo todo otra vez para saber qué pasó.
        self.prog.errorForm = comoAcabo.texto;
        // UN ALTA CUYO RESULTADO NO SE SABE RETIENE EL FORMULARIO TAL COMO SE MANDÓ (P1 ronda 2).
        // La clave ya puede estar gastada en el servidor, así que lo único que se puede hacer con
        // ella es repetir la misma petición —el servidor contesta la regla que creó, sin
        // duplicarla— o descartarla a sabiendas. Dejando el formulario suelto, quien está delante
        // cambiaba la hora y volvía a pulsar: el servidor rechazaba por clave reutilizada y el
        // formulario se quedaba sin salida, repitiendo el mismo rechazo con cada pulsación.
        //
        // Una edición no retiene nada: va condicionada al `visto`, así que su reintento no puede
        // escribir dos veces y lo que contesta el servidor es «recarga».
        //
        // Y lo que decide la retención es **el mismo predicado** que decide si el apunte se suelta,
        // no una lista de estados aparte: un 500, o el 503 con el que el servidor dice que la
        // escritura se puso en marcha y no sabe cómo acabó, son tan inciertos como un timeout.
        // Contándolos como rechazos, el formulario quedaba suelto con la clave ya gastada y
        // cambiarlo dejaba el alta sin salida.
        //
        // Un reintento cuenta siempre como incierto, por lo mismo que no suelta la referencia: lo
        // que no se sabe es si el PRIMER intento escribió, y el rechazo de este no lo contesta. Y el
        // texto lleva entonces la salida, porque un rechazo repetido sin ella es un callejón: lo que
        // queda es «Descartar este alta» y darla de alta de nuevo, que es el desenlace claro que
        // pide una modificación intencional.
        // Y SE RETIENE POR `retenerAltaIncierta`, que es lo que deja su clave apuntada en el
        // navegador aunque otra pestaña se la haya llevado (P1 Codex 19/10/2026): aquí solo se
        // conservaba en memoria, así que al recargar esa pestaña ya no podía ni preguntar por el alta
        // ni repetirla con su clave.
        const noSeSabe = !eraEdicion && (esReintento || !seAcreditaQueNoSeEscribio(err));
        if (noSeSabe) {
          self.retenerAltaIncierta(esReintento && seAcreditaQueNoSeEscribio(err)
            ? comoAcabo.texto + ' Esto es el reintento de un alta de la que no se sabe si quedó '
              + 'creada, así que el rechazo es de este intento y no dice qué pasó con el primero. '
              + 'Mira la lista de arriba: si la programación está, pulsa «Descartar este alta». Si '
              + 'no está y hay que cambiar algo de lo que pedías, pulsa «Descartar este alta» y '
              + 'dala de alta otra vez: será una operación nueva.'
            : comoAcabo.texto);
        }
        // Y en los dos casos se relee la lista, que es donde se ve si quedó escrita: es la mitad de
        // la respuesta que la pantalla sí puede dar.
        if (noSeSabe || comoAcabo.incierto) return self.cargarProgramaciones();
        return undefined;
      });
    },
    /**
     * **Repite el alta incierta tal cual**, sin tocar nada de lo que se mandó.
     *
     * Es seguro y por eso es el camino recomendado: el alta viaja con la misma clave, así que si el
     * primer intento sí llegó a crearla el servidor contesta esa misma regla en vez de crear otra.
     * No hay nada que decidir —ni comprobar un buzón, como en el envío—: aquí la garantía la da el
     * servidor, no la persona.
     *
     * **Y NO SUELTA LA RETENCIÓN**: la suelta `mandarLaProgramacion`, y solo cuando la petición ya
     * va a salir (P1 Codex 19/10/2026). Soltándola aquí, cualquiera de las salidas de ese método
     * —la puerta con las listas a medias, el apunte que no cabe en el navegador— dejaba el
     * formulario suelto, sin aviso y sin los dos botones, con la clave ya gastada en el servidor. Lo
     * que impide pulsar es lo mismo que lo que impide mandar, y se dice junto al botón:
     * `motivoNoSePuedeReintentarElAlta`. Si el reintento vuelve a quedarse sin respuesta, el propio
     * `catch` pone la retención otra vez.
     */
    reintentarAltaIncierta: function() {
      return this.mandarLaProgramacion(true);
    },
    /**
     * **Descarta el alta incierta**, y lo dice claro: puede estar creada.
     *
     * Es la otra salida, para cuando la regla ya se ve en la lista de arriba. Suelta la clave con el
     * formulario, así que lo siguiente que se dé de alta será un alta nueva y no el reintento de
     * esta: es lo correcto —ya no se está reintentando nada— y es también lo que impide que una
     * clave gastada se reutilice con otro contenido.
     */
    descartarAltaIncierta: function() {
      const self = this;
      self.$f7.dialog.confirm(
        'Esa programación puede haberse creado en el servidor. Si la ves en la lista de arriba, '
          + 'descartar el formulario es lo correcto. Si NO la ves, al descartarlo no quedará '
          + 'creada y habrá que darla de alta otra vez. ¿Descarto el formulario?',
        '¿Descartar este alta?',
        function() { self.limpiarFormProgramacion(); });
    },
    /**
     * Activa o pausa una regla. Se manda **lo que la pantalla tenía delante** y el servidor escribe
     * el contrario solo si sigue así: con un «lee, niega y guarda», dos pulsaciones casi a la vez
     * leen lo mismo y la segunda deshace la primera sin que nadie lo diga.
     */
    alternarRegla: function(regla) {
      const self = this;
      if (self.bloqueoDeRegla(regla)) return Promise.resolve();
      const activaActual = !!regla.activa;
      cerrojoDeLaSesion(self.prog, 'guardando');
      self.prog.errorForm = null;
      self.prog.aviso = null;
      return FactCobro.alternarProgramacionControlDiario(regla.id, activaActual).then(function() {
        self.prog.guardando = false;
        self.prog.aviso = activaActual ? 'La programación se ha pausado: no se manda nada hasta '
          + 'que se vuelva a activar.' : 'La programación se ha activado.';
        return self.cargarProgramaciones();
      }, function(err) {
        self.prog.guardando = false;
        self.prog.errorForm = motivoDeLaProgramacion(err,
          'No se ha podido cambiar la programación.');
      });
    },
    /**
     * Borra una regla, y con eso no queda ningún envío suelto: el planificador lee las reglas
     * activas en cada pasada, así que al día siguiente no llega nada.
     *
     * Se manda el `modificadaEn` que se tenía delante: entre ver la lista y pulsar «Eliminar» cabe
     * que alguien le haya cambiado la hora o los destinatarios, y entonces lo que se borraría no es
     * lo que se estaba mirando.
     *
     * **Y se pregunta antes** (P2 Codex ronda 2). Es destructivo, no tiene deshacer y detiene unos
     * informes periódicos que alguien espera recibir: un toque de más en una lista de fichas con
     * tres botones pegados no puede bastar. La pregunta lleva el nombre de la regla y su
     * periodicidad, para que lo que se confirme sea la que se quería y no la de al lado.
     */
    borrarRegla: function(regla) {
      const self = this;
      if (self.bloqueoDeRegla(regla)) return Promise.resolve();
      self.$f7.dialog.confirm(
        'Se dejará de enviar «' + (regla.nombre || 'sin nombre') + '» ('
          + self.textoFrecuencia(regla) + '). No se puede deshacer: habría que volver a darla de '
          + 'alta. Los envíos que ya salieron siguen en «Últimos envíos».',
        '¿Eliminar este envío programado?',
        function() { self.borrarReglaSinPreguntar(regla); });
      return Promise.resolve();
    },
    /** El borrado de verdad, ya confirmado. Lo llama solo `borrarRegla`. */
    borrarReglaSinPreguntar: function(regla) {
      const self = this;
      if (self.bloqueoDeRegla(regla)) return Promise.resolve();
      const visto = regla.modificadaEn ? new Date(regla.modificadaEn).getTime() : null;
      cerrojoDeLaSesion(self.prog, 'guardando');
      self.prog.errorForm = null;
      self.prog.aviso = null;
      return FactCobro.borrarProgramacionControlDiario(regla.id, visto).then(function() {
        self.prog.guardando = false;
        self.prog.aviso = 'La programación se ha eliminado: no volverá a salir ningún envío suyo.';
        // Si se estaba editando justo esa, el formulario deja de hablar de algo que ya no existe.
        if (self.prog.form.id === regla.id) self.limpiarFormProgramacion();
        return self.cargarProgramaciones();
      }, function(err) {
        self.prog.guardando = false;
        self.prog.errorForm = motivoDeLaProgramacion(err,
          'La programación no se ha podido eliminar.');
      });
    },
    /**
     * **Por qué no se puede tocar esa regla ahora mismo**, o `null`.
     *
     * Un solo sitio para los tres botones y para los tres métodos, a propósito: cada vez que el
     * cerrojo se reparte por botón, el siguiente se queda fuera —en «Abonos abiertos» pasó dos
     * veces, primero con Pausar y Eliminar y después con Pausar mientras se editaba—. El
     * `:disabled` y el método miran este mismo texto, que es además lo que la pantalla enseña
     * debajo (§9).
     */
    bloqueoDeRegla: function(regla) {
      if (this.prog.guardando) {
        return 'Espera a que termine lo que se está guardando: hasta entonces no se puede tocar '
          + 'ninguna programación.';
      }
      if (!regla || !regla.id) {
        return 'Esta programación no se puede tocar: no tiene identificador. Recarga el diálogo.';
      }
      return null;
    },
    /** Cada cuánto sale una regla, en una línea que se lee. */
    textoFrecuencia: function(regla) {
      if (!regla) return '—';
      const hora = regla.hora || '—';
      if (regla.frecuencia === 'SEMANAL') {
        return 'Cada ' + (regla.diaSemana || '—').toLowerCase() + ' a las ' + hora;
      }
      if (regla.frecuencia === 'MENSUAL') {
        return 'El día ' + (regla.diaMes || '—') + ' de cada mes a las ' + hora;
      }
      return (regla.soloLaborables ? 'Cada día laborable' : 'Cada día') + ' a las ' + hora;
    },
    /**
     * A quién le llega esa regla, con las tres vías contadas por separado.
     *
     * Se cuentan y no se resuelven: lo que la regla guarda son códigos de grupo y usernames, y
     * quién está en cada grupo lo decide la API en el momento del envío. Escribir aquí los buzones
     * sería anunciar una lista que puede no ser la que reciba el correo.
     */
    textoDestinatarios: function(regla) {
      const partes = [];
      const grupos = (regla && regla.grupos) || [];
      const personas = (regla && regla.personasCodigo) || [];
      const emails = (regla && regla.emailsExtra) || [];
      if (grupos.length) partes.push('grupos: ' + grupos.join(', '));
      if (personas.length) {
        partes.push(personas.length === 1 ? '1 persona' : personas.length + ' personas');
      }
      if (emails.length) partes.push(emails.join(', '));
      // Que no quede ninguna vía no debería pasar —la API lo rechaza al guardar—, pero una regla
      // antigua o editada por otro camino se leería como una fila vacía, que no dice nada.
      return partes.length ? 'A ' + partes.join(' · ')
        : 'Sin destinatarios: esta programación no manda nada. Edítala y elige a quién se le manda.';
    },
    /** Cómo acabó un envío del historial, con las palabras de la pantalla y no las de la API. */
    textoEstadoEnvio: function(envio) {
      const estado = envio && envio.estado;
      if (estado === 'ENVIADO') return 'Salió';
      if (estado === 'PARCIAL') return 'Salió a medias';
      if (estado === 'ERROR') return 'No salió';
      return estado || '—';
    },
    /** A cuántos buzones salió. Un cero se dice con palabras: «a ninguno» y no «0». */
    textoBuzonesEnvio: function(envio) {
      const cuantos = (envio && envio.enviadosOk) || 0;
      if (!cuantos) return 'A ninguno';
      return cuantos === 1 ? '1 buzón' : cuantos + ' buzones';
    },

    /**
     * Pide el mes y lo pone en pantalla.
     *
     * `opciones.conservarLoTecleado` lo pasa **solo** la relectura del final de un «Guardar»
     * (#215). Es la diferencia entre las dos lecturas de esta pantalla: la de entrar o cambiar de
     * mes no tiene nada que conservar, y la del final de un lote sí —las cifras que no entraron
     * están escritas en sus casillas, y esas casillas solo existen mientras haya tabla—. Con ella,
     * un fallo de lectura deja el mes anterior en pantalla y lo dice, en vez de llevarse por
     * delante lo que no se ha podido guardar todavía.
     *
     * Es el mismo criterio que ya seguía el recálculo de un objetivo (#249), que es la otra
     * escritura de la pantalla: cuando falla, apunta su motivo al lado del botón y **no** vacía el
     * mes.
     */
    cargar: function(opciones) {
      const self = this;
      const conservarLoTecleado = !!(opciones && opciones.conservarLoTecleado);
      const peticion = ++self.peticionEnCurso;
      // #663 · El cerrojo de la sesión: si la sesión cambia en vuelo, la frontera calla el `then`
      // que lo soltaba y la instancia (`keepAlive`) lo conservaría puesto en la cuenta siguiente.
      cerrojoDeLaSesion(self, 'cargando');
      self.error = null;
      self.errorRelectura = null;
      self.pestanaCerrada = null;
      // El detalle desplegado es el del mes que se deja de enseñar: abierto sobre el mes nuevo
      // enseñaría unos documentos que no son los de la línea que tiene al lado.
      self.detalleFueraDeMesAbierto = false;

      return FactCobro.mes(self.anio, self.numeroMes).then(function(res) {
        if (peticion !== self.peticionEnCurso) return;
        const dto = unwrapDto(res);
        self.cargando = false;
        // `unwrapDto` devuelve null cuando el servidor contesta el envoltorio con el dto vacío.
        // Aquí eso no debería pasar —la API contesta siempre un mes, aunque no exista— pero si
        // pasara, lo correcto es no dejar en pantalla el mes anterior con el rótulo del nuevo.
        if (!dto) {
          self.mes = null;
          self.error = 'El servidor no ha devuelto el mes. Vuelve a intentarlo con el botón de recargar.';
          return;
        }
        self.aplicarMes(dto);
      }).catch(function(err) {
        if (peticion !== self.peticionEnCurso) return;
        self.cargando = false;
        // **Los tres que dejan la pantalla sin cifras aunque haya algo tecleado**, y van primero
        // justo por eso (#215): no son un fallo de lectura, son tres respuestas en firme sobre lo
        // que esta pantalla puede enseñar de ese mes —la pestaña está cerrada, esta cuenta no
        // puede verlo, ese mes no es un mes del que hablar—. Dejar la tabla puesta sería enseñar
        // cifras que el servidor acaba de decir que no puede respaldar, o enseñárselas a quien ya
        // no tiene permiso. Conservar lo tecleado solo tiene sentido con el fallo que no dice nada
        // del mes: un corte de red, una caída.
        //
        // La pestaña cerrada (#169) trae su propia explicación: qué pasa, por qué está cerrada y a
        // quién avisar. Se enseña TAL CUAL, porque el genérico de abajo manda hacer justo lo que no
        // lo arregla.
        if (err && err.code === FactCobro.CODIGO_PESTANA_CERRADA && err.message) {
          self.mes = null;
          self.pestanaCerrada = err.message;
          return;
        }
        // Un 403 aquí no es un error de la pantalla: es la restricción de rol (D-ROL).
        if (err && err.status === 403) {
          self.mes = null;
          self.error = 'Esta pantalla es solo de ADMIN y DIRECTOR.';
          return;
        }
        // Un 400 lo manda el servidor con su motivo (un mes que no existe, un año fuera de la
        // pestaña): se enseña el motivo y no el genérico, que no diría cuál de los dos es.
        if (err && err.status === 400 && err.message) {
          self.mes = null;
          self.error = err.message;
          return;
        }
        // Y el fallo de lectura de la relectura de un lote, con cifras tecleadas sin guardar: la
        // tabla se queda —es donde están escritas— y lo que se dice es que el mes no se ha podido
        // releer. Sin `mes` no hay casillas, y sin casillas lo tecleado no se puede ni ver ni
        // volver a guardar (#215).
        if (conservarLoTecleado && self.mes && self.cuantosSinGuardar > 0) {
          self.errorRelectura = 'No se ha podido volver a leer el mes después de guardar: las '
            + 'cifras de la tabla son las de antes y pueden no incluir lo que acabe de entrar. Lo '
            + 'que tienes tecleado sigue escrito en su casilla, así que puedes volver a pulsar '
            + '«Guardar» sin teclearlo otra vez. Si prefieres partir de lo que hay guardado, '
            + 'recarga con el botón de arriba: eso sí tira lo que tengas sin guardar.';
          return;
        }
        self.mes = null;
        self.error = 'No se ha podido cargar el mes. Vuelve a intentarlo con el botón de recargar.';
      });
    },

    /**
     * Pone en pantalla un mes que acaba de llegar del servidor.
     *
     * Son **las dos respuestas** que traen un mes entero —la lectura y el recálculo de un objetivo—,
     * y por eso está en un método: lo tecleado se vuelve a cuadrar contra las cifras nuevas en los
     * dos casos, y es la ÚNICA puerta por la que entran cifras nuevas del servidor (ver
     * `reconciliarCasillas`). Con la reconciliación escrita en cada camino, el que se añadiera
     * mañana se la olvidaría y dejaría una casilla avisando de un conflicto que ya no existe.
     *
     * Y por lo mismo retira aquí el aviso de «no he podido releer el mes» (#215): lo que ese aviso
     * dice es que las cifras de la tabla son viejas, así que lo que lo desmiente es que lleguen
     * cifras nuevas —por el camino que sea—. Puesto solo en el éxito de `cargar` se quedaría
     * mintiendo después de un recálculo de objetivo, que también contesta con el mes entero.
     */
    aplicarMes: function(dto) {
      this.errorRelectura = null;
      this.mes = dto;
      // De cuándo son las cifras que se van a pintar. Aquí y no en el éxito de `cargar`, por lo
      // mismo que la línea de arriba: el recálculo de un objetivo también trae el mes entero.
      this.leidoEn = new Date();
      this.aniosDisponibles = dto.aniosDisponibles || [];
      this.reconciliarCasillas(dto);
    },

    // ------------------------------------------------- los objetivos a botón (#249, D-OBJ-BOTON)

    /**
     * Si el botón de **este** objetivo se puede pulsar ahora mismo.
     *
     * Tampoco con una casilla a medias: el clic le quita el foco, `ocupado` apaga las casillas y al
     * volver a sincronizar el `:value` ese texto se sustituye por el último valor apuntado, así que
     * la edición se perdería antes incluso de que saliera la petición (ver `hayAlgoAMedias`).
     */
    sePuedeRecalcular: function(resumen) {
      return !!resumen && resumen.recalculable && !this.ocupado && !this.hayAlgoAMedias
        && !this.cargando;
    },

    /**
     * Por qué el botón de este objetivo está apagado, dicho junto al botón.
     *
     * Solo para las esperas, que son las que no se explican solas: el botón que está calculando ya
     * lo dice en su rótulo, y mientras la pantalla entera carga hay un preloader. Un control
     * apagado y mudo es el defecto que dirección registró el 05/10/2026.
     */
    porQueEsperar: function(resumen) {
      if (!resumen || !resumen.recalculable || resumen.recalculando || this.cargando) return null;
      if (this.objetivoEnCurso) {
        return 'Espera a que termine el otro objetivo: se calcula uno a la vez, para que la '
          + 'pantalla no se quede enseñando la cifra de antes.';
      }
      if (this.guardando) return 'Espera a que acabe de guardarse lo que has tecleado.';
      return this.porQueHayAlgoAMedias;
    },

    /**
     * Recalcula **uno** de los dos objetivos y refresca su cifra y su fecha sin recargar la
     * pantalla.
     *
     * Se aplica la respuesta y no se vuelve a leer el mes: el servidor contesta con el mes releído
     * después de escribir, así que es la misma lectura que haría el GET y una menos. Y se descarta
     * si por el camino se ha cambiado de mes o ha entrado otra lectura: la respuesta habla del mes
     * que ya no está en pantalla.
     */
    recalcularObjetivo: function(resumen) {
      const self = this;
      if (!self.sePuedeRecalcular(resumen)) return Promise.resolve();

      const magnitud = resumen.magnitud;
      const delAnio = self.anio;
      const delMes = self.numeroMes;
      const peticion = self.peticionEnCurso;
      self.objetivoEnCurso = magnitud;
      self.anotarFalloObjetivo(magnitud, null);

      return FactCobro.recalcularObjetivo(delAnio, delMes, magnitud).then(function(res) {
        self.objetivoEnCurso = null;
        if (self.esDeOtroMes(peticion, delAnio, delMes)) return;
        const dto = unwrapDto(res);
        if (!dto) {
          self.anotarFalloObjetivo(magnitud, 'El objetivo puede haberse recalculado, pero el '
            + 'servidor no ha devuelto el mes: recarga la pantalla para ver cómo ha quedado.');
          return;
        }
        self.aplicarMes(dto);
      }).catch(function(err) {
        self.objetivoEnCurso = null;
        if (self.esDeOtroMes(peticion, delAnio, delMes)) return;
        self.anotarFalloObjetivo(magnitud, (err && err.message)
          || 'No se ha podido recalcular y el servidor no ha dicho el motivo. Vuelve a intentarlo '
          + 'y, si sigue igual, dilo.');
      });
    },

    /** Si una respuesta que llega habla de un mes que ya no es el que está en pantalla. */
    esDeOtroMes: function(peticion, anio, mes) {
      return peticion !== this.peticionEnCurso || anio !== this.anio || mes !== this.numeroMes;
    },

    /**
     * Apunta —o retira— el motivo del último recálculo de un objetivo. Se sustituye el objeto
     * entero porque en Vue 2 una clave nueva en un objeto no es reactiva.
     */
    anotarFalloObjetivo: function(magnitud, motivo) {
      const fallos = Object.assign({}, this.fallosObjetivo);
      if (motivo) {
        fallos[magnitud] = motivo;
      } else {
        delete fallos[magnitud];
      }
      this.fallosObjetivo = fallos;
    },

    // ------------------------------------------------------------- el selector de mes

    irAlMesAnterior: function() {
      if (!this.hayAnterior || this.ocupado) return;
      if (this.numeroMes === 1) { this.anio -= 1; this.numeroMes = 12; } else { this.numeroMes -= 1; }
      this.recargar();
    },
    irAlMesSiguiente: function() {
      if (!this.haySiguiente || this.ocupado) return;
      if (this.numeroMes === 12) { this.anio += 1; this.numeroMes = 1; } else { this.numeroMes += 1; }
      this.recargar();
    },

    // ----------------------------------------------------------- los datos manuales

    /**
     * **El único sitio que escribe `casillas`**, y donde viven las dos reglas de qué sobrevive en
     * una casilla y qué no.
     *
     * Son las dos que pidió dirección el 04/10/2026 (opción A), y están aquí y no repartidas por
     * cada camino porque repartidas es como se quedaron puestos los avisos: el camino que se
     * olvidaba de retirar el suyo dejaba la pantalla diciendo algo que ya no era verdad.
     *
     * - **Una edición nueva retira el éxito anterior.** Un «Dato guardado» encima de una casilla
     *   que se acaba de volver a teclear habla de una cifra que ya no es la que está escrita.
     * - **Sin nada pendiente no hay error ni conflicto que enseñar.** Los dos avisos dicen «tienes
     *   una cifra sin guardar»: si se ha deshecho hasta el valor guardado, no la hay.
     * - **El conflicto retira el fallo** (#215). Son los dos extremos de un mismo camino: el
     *   servidor rechaza la escritura con un conflicto, eso se apunta como fallo porque es lo
     *   único que se sabe en ese momento, y la relectura del final lo reclasifica al ver que la
     *   cifra guardada ha cambiado. Sin esta regla la casilla salía **en los dos avisos a la vez**
     *   —el naranja «un dato no se ha guardado» y el azul «una casilla que estabas escribiendo ha
     *   cambiado»— con instrucciones distintas, cuando el flujo entero trata el conflicto como un
     *   caso distinto de un fallo. Y está aquí y no en la reconciliación por lo mismo que las otras
     *   dos: el camino que mañana apunte un conflicto no tiene que acordarse de retirar el fallo.
     *

     * Y una casilla que se queda sin nada que decir se borra, para que no cuente en ninguno de los
     * derivados ni marque la celda. **Tener texto a medias es algo que decir**: esa casilla se queda
     * aunque no haya ninguna cifra pendiente, porque es lo que impide que una escritura empiece y se
     * lleve por delante lo que se está escribiendo (ver `editar` y `hayAlgoAMedias`).
     */
    fijarCasilla: function(clave, estado) {
      const pendiente = !!estado.pendiente;
      const aMedias = !!estado.aMedias;
      const conflicto = pendiente ? (estado.conflicto || null) : null;
      const casilla = {
        clave: clave,
        fecha: estado.fecha,
        dato: estado.dato,
        pendiente: pendiente,
        aMedias: aMedias,
        valor: pendiente ? estado.valor : null,
        valorVisto: pendiente ? estado.valorVisto : null,
        // Un conflicto se lleva el fallo: es el mismo rechazo contado mejor, y la casilla no puede
        // salir en el aviso naranja y en el azul a la vez (#215).
        fallo: pendiente && !conflicto ? (estado.fallo || null) : null,
        conflicto: conflicto,
        // Teclear retira el éxito anterior, y teclear media cifra es teclear: un «Dato guardado»
        // encima de una casilla con texto a medias habla de una cifra que ya no es la que se ve.
        exito: pendiente || aMedias ? false : !!estado.exito
      };

      if (!casilla.pendiente && !casilla.exito && !casilla.aMedias) {
        this.$delete(this.casillas, clave);
        return;
      }
      this.$set(this.casillas, clave, casilla);
    },

    /**
     * Apunta —o retira— que esa casilla tiene **texto a medias**: lo que el navegador marca como
     * `badInput` y no devuelve, o sea lo único de esta pantalla que se ve escrito y no está en el
     * borrador. Pasa por `fijarCasilla`, que sigue siendo el único sitio que escribe `casillas`.
     *
     * No crea ninguna casilla para decir que NO está a medias, que es lo que pasa en cada tecla
     * normal: sustituir el objeto de una casilla que no tiene nada que decir la metería en el
     * borrador, y en medio de un lote la sacaría de él por identidad (ver `guardar`).
     */
    anotarAMedias: function(fila, dato, aMedias) {
      const clave = this.clave(fila, dato);
      const anterior = this.casillas[clave];
      if (!anterior && !aMedias) return;
      if (anterior && !!anterior.aMedias === aMedias) return;
      this.fijarCasilla(clave, Object.assign({ fecha: fila.fecha, dato: dato }, anterior,
        { aMedias: aMedias }));
    },

    /**
     * Retira de todas las casillas los avisos del lote anterior. Se llama al empezar un «Guardar»:
     * lo que diga el aviso de hace un rato no describe este lote, y dejarlo mezclaría los dos.
     */
    limpiarAvisosDeLasCasillas: function() {
      const self = this;
      Object.keys(self.casillas).forEach(function(clave) {
        const casilla = self.casillas[clave];
        // Las que no llevan aviso se dejan tal cual, y no por ahorrar: `fijarCasilla` sustituye el
        // objeto de la casilla, y lo que distingue «esta casilla sigue siendo la que mandé» de «se
        // ha vuelto a teclear» es precisamente la identidad de ese objeto.
        if (!casilla.fallo && !casilla.conflicto && !casilla.exito) return;
        self.fijarCasilla(clave, Object.assign({}, casilla,
          { fallo: null, conflicto: null, exito: false }));
      });
    },

    /**
     * Cuadra lo tecleado contra el mes que el servidor acaba de servir.
     *
     * **Por qué está en un solo sitio.** Lo que se teclea y lo que el servidor tiene son dos cosas
     * que se separan por muchos caminos —un 409 porque otra persona tocó la misma casilla, un
     * guardado que entró a medias, una fila que deja de existir al releer, el mes que cambia— y
     * cada uno de esos caminos acababa cuadrando su trozo a su manera. Cuadrarlo en cada sitio es
     * lo que deja huecos: el camino que se olvida vuelve a separarlos. Aquí hay un solo sitio por
     * el que pasan TODOS, porque `cargar()` es la única puerta por la que entran cifras del
     * servidor, y cuadra las casillas enteras, no la de la que se venía hablando.
     *
     * Lo que hace con cada casilla, y por qué:
     *
     * - **Su fila ya no está** en el mes servido: se tira. Una casilla que no se pinta no se puede
     *   ni ver ni corregir, pero sí contaría en «Guardar (n)» y se mandaría al servidor para
     *   siempre. Esto cubre también lo que se queda de otro mes.
     * - **No tiene nada pendiente**: no hay nada que cuadrar. Lo único que lleva es el éxito de su
     *   último guardado, que habla de lo que el servidor acaba de servir.
     * - **Lo guardado ya es lo tecleado**: deja de ser un cambio, así que se retira —y con él su
     *   error o su conflicto, que hablaban de una cifra sin guardar que ya no existe—. Da igual
     *   quién lo escribió, nosotros en el lote que acaba de pasar u otra persona con la misma cifra.
     * - **Lo guardado sigue siendo lo que se vio**: se queda tal cual, y si llevaba aviso de
     *   conflicto se retira: la base volvió a su sitio. El `valorVisto` no se mueve, que es lo que
     *   hace que el guardado condicional cubra todo el rato que la pantalla lleva abierta.
     * - **Lo guardado ha cambiado por debajo**: se reapunta el `valorVisto` a lo que hay ahora
     *   —si no, el siguiente «Guardar» repetiría el mismo 409 eternamente con un valor visto que ya
     *   no existe— y la casilla se marca y se avisa. Reapuntarlo en silencio sería escribir encima
     *   de otra persona sin decirlo, que es justo lo que el guardado condicional evita. Si esa
     *   casilla venía con el fallo del rechazo que la trajo aquí, el fallo se va con el conflicto:
     *   lo decide `fijarCasilla`, no este camino (#215).
     */
    reconciliarCasillas: function(dto) {
      const self = this;
      const porFecha = {};
      ((dto && dto.dias) || []).forEach(function(fila) {
        if (fila && fila.fecha) porFecha[fila.fecha] = fila;
      });

      Object.keys(self.casillas).forEach(function(clave) {
        const casilla = self.casillas[clave];
        const fila = porFecha[casilla.fecha];

        if (!fila) {
          self.$delete(self.casillas, clave);
          return;
        }
        if (!casilla.pendiente) return;

        const guardado = self.valorGuardado(fila, casilla.dato);
        if (mismoImporte(casilla.valor, guardado)) {
          self.fijarCasilla(clave, Object.assign({}, casilla, { pendiente: false }));
          return;
        }
        if (mismoImporte(casilla.valorVisto, guardado)) {
          if (casilla.conflicto) {
            self.fijarCasilla(clave, Object.assign({}, casilla, { conflicto: null }));
          }
          return;
        }
        self.fijarCasilla(clave, Object.assign({}, casilla, {
          valorVisto: guardado,
          conflicto: { guardado: guardado }
        }));
      });
    },

    clave: function(fila, dato) {
      return fila.fecha + '|' + dato;
    },
    rotulo: function(dato) {
      return ROTULOS[dato] || dato;
    },
    /** El valor guardado de ese dato, tal como lo trae el servidor. */
    valorGuardado: function(fila, dato) {
      const valor = fila[CAMPOS[dato]];
      return valor === undefined ? null : valor;
    },
    /**
     * Lo que se pinta en la casilla: lo tecleado si hay algo sin guardar, y si no lo guardado.
     *
     * Devuelve cadena vacía para un hueco y no `0`: un día sin cobro tecleado no es un día de cobro
     * cero, y escribir un 0 en la casilla haría que al pulsar «Guardar» se guardara ese cero.
     */
    valorEnPantalla: function(fila, dato) {
      const casilla = this.casillas[this.clave(fila, dato)];
      const valor = casilla && casilla.pendiente ? casilla.valor : this.valorGuardado(fila, dato);
      return valor === null || valor === undefined ? '' : valor;
    },
    /**
     * Los cinco estados de una casilla, cada uno leído de la propia casilla y no de una lista
     * aparte: así una casilla que deja de tener algo pendiente deja de estar marcada en el mismo
     * momento, sin que ningún camino tenga que acordarse de desmarcarla.
     */
    estaTocada: function(fila, dato) {
      const casilla = this.casillas[this.clave(fila, dato)];
      return !!(casilla && casilla.pendiente);
    },
    /**
     * La casilla con texto a medias se marca: es la que hay que terminar para volver a guardar o a
     * recalcular, y el aviso de los botones la nombra. Sin marcarla habría que buscarla a ojo entre
     * sesenta casillas.
     */
    estaAMedias: function(fila, dato) {
      const casilla = this.casillas[this.clave(fila, dato)];
      return !!(casilla && casilla.aMedias);
    },
    haFallado: function(fila, dato) {
      const casilla = this.casillas[this.clave(fila, dato)];
      return !!(casilla && casilla.fallo);
    },
    laBaseSeHaMovido: function(fila, dato) {
      const casilla = this.casillas[this.clave(fila, dato)];
      return !!(casilla && casilla.conflicto);
    },
    seHaGuardado: function(fila, dato) {
      const casilla = this.casillas[this.clave(fila, dato)];
      return !!(casilla && casilla.exito);
    },
    /**
     * Apunta lo tecleado en el borrador, sin mandar nada: lo manda el botón «Guardar» de la
     * cabecera, que es lo que pidió dirección en la maqueta.
     *
     * Va en `@input`, o sea en **cada tecla**, y no en `@change`. Con `change` no se enteraba hasta
     * que la casilla perdía el foco, así que una cifra a medio escribir existía solo en el DOM: el
     * contador del botón no la contaba, el fondo de «tocada» no se encendía, y cualquier cosa que
     * repintara o deshabilitara esa casilla —el mes que se cierra y deja de ser editable, una fila
     * que desaparece al releer— se la llevaba sin que nadie la hubiera guardado. Con `input`, lo que
     * se ve escrito está en el borrador.
     *
     * El `valorVisto` se captura **la primera vez** que se toca esa casilla y no se vuelve a mover:
     * es el importe sobre el que quien edita ha decidido, y es lo que el servidor compara. Si se
     * refrescara en cada tecla, el guardado condicional dejaría de cubrir el rato que la pantalla
     * lleva abierta, que es donde de verdad se pisan dos personas.
     *
     * **Y una cifra a medio teclear no se apunta como valor, pero sí se apunta que está a medias.**
     * Teclear los céntimos pasa por `12.`, que no es un número válido: un `type=number` devuelve
     * entonces `value` vacío y lo marca en `badInput`. Vacío aquí significa «esta casilla se queda
     * sin dato», así que tomarlo al pie de la letra apunta un borrado en el borrador en cuanto se
     * teclea el separador decimal —y es ese borrado lo que se guardaría si el lote sale justo en ese
     * instante—. Así que el valor no se toca: el borrador se queda con la última cifra completa.
     *
     * Lo que sí se apunta es el **estado**: esa casilla tiene texto a medias. El texto en sí no se
     * puede guardar —un `type=number` con `badInput` no lo devuelve por ninguna vía—, así que el
     * único sitio donde existe es el DOM y cualquier repintado se lo lleva. Marcándolo, la pantalla
     * puede hacer lo único que de verdad lo protege: **no empezar ninguna escritura mientras haya
     * una casilla a medias**, y decirlo (ver `hayAlgoAMedias`). Antes se descartaba en silencio, y
     * el repintado de la respuesta —o el que provoca apagar las casillas al pulsar «Recalcular»— lo
     * sustituía por el último valor apuntado (P2 Codex #1777, rondas 2 y 3).
     */
    editar: function(fila, dato, evento) {
      if (evento.target.validity && evento.target.validity.badInput) {
        this.anotarAMedias(fila, dato, true);
        return;
      }
      this.anotarAMedias(fila, dato, false);

      const clave = this.clave(fila, dato);
      const texto = String(evento.target.value).trim();
      const valor = texto === '' ? null : Number(texto);

      if (valor !== null && !isFinite(valor)) {
        // Un `type=number` solo devuelve cadena vacía o un número, así que esto no se dispara
        // escribiendo —y por eso puede devolver la casilla a su valor sin pelearse con las teclas—.
        // Es el cinturón para que una entrada que no sea numérica no acabe en el borrador.
        evento.target.value = this.valorEnPantalla(fila, dato);
        return;
      }

      // El aviso del último lote habla de otro momento: cualquier tecla lo retira.
      this.avisoDelLote = null;

      const anterior = this.casillas[clave];
      const visto = anterior && anterior.pendiente
        ? anterior.valorVisto
        : this.valorGuardado(fila, dato);

      // Lo que esta casilla pasa a tener, y **todo lo que ya tenía tal cual**: qué sobrevive de
      // eso no se decide aquí. Lo decide `fijarCasilla`, que es el único sitio donde están las dos
      // reglas; repartirlas por cada camino es lo que dejaba los avisos puestos.
      if (mismoImporte(valor, visto)) {
        // Volver al valor que había no es un cambio: dejarlo pendiente mandaría una petición que el
        // servidor contestaría sin registrar nada, y el contador del botón mentiría.
        this.fijarCasilla(clave, Object.assign({ fecha: fila.fecha, dato: dato }, anterior,
          { pendiente: false }));
        return;
      }
      this.fijarCasilla(clave, Object.assign({ fecha: fila.fecha, dato: dato }, anterior, {
        pendiente: true,
        valor: valor,
        valorVisto: visto
      }));
    },

    /**
     * Manda lo tecleado, **un dato por petición y en orden**, y después trae el mes releído.
     *
     * En orden y no en paralelo: cada petición contesta el mes entero, y lanzadas a la vez la
     * última respuesta que llegara pisaría a las demás sin que el orden tuviera nada que ver con el
     * de las escrituras. Y una que falle no corta las siguientes: lo que sí se puede guardar se
     * guarda, y al final la pantalla dice exactamente qué no entró y por qué (#164).
     *
     * **El mes se fija aquí y no se vuelve a leer.** El lote son varias peticiones seguidas y cada
     * una tarda, así que leer `this.anio` dentro del bucle significaba que si alguien pulsa una
     * flecha de mes a mitad del lote, las peticiones que quedaran se mandarían al mes nuevo con las
     * fechas y los valores del anterior. Con el año y el mes capturados, cada dato va al mes del
     * que salió. Las flechas, el recargar y las casillas están además apagados mientras haya algo
     * escribiendo (`ocupado`), que es lo que evita además que `recargar()` tire el borrador con
     * peticiones en vuelo.
     *
     * **Y la instantánea no se da por buena: se vuelve a mirar antes de cada petición.** Lo que vale
     * es el borrador en el momento de mandar, no la foto que se tomó al pulsar el botón. Las casillas
     * salen apagadas mientras se guarda, así que hoy esto no debería poder pasar; se comprueba igual,
     * porque el día que alguien vuelva a dejarlas escribibles el fallo sería guardar una cifra que el
     * usuario había deshecho, y eso no avisa de nada.
     */
    guardar: function() {
      const self = this;
      if (!self.sePuedeGuardar) return Promise.resolve();

      cerrojoDeLaSesion(self, 'guardando');
      // Los avisos de hace un rato no describen este lote: se retiran de las casillas antes de
      // empezar, y los de este los pondrá cada respuesta. Los derivados se apagan con ellos.
      // Va ANTES de la instantánea: retirar un aviso sustituye la casilla, y con la instantánea ya
      // tomada esas casillas dejarían de ser las que se apuntaron y el lote no mandaría ninguna.
      self.limpiarAvisosDeLasCasillas();
      self.avisoDelLote = null;

      // El mes del lote, capturado antes de la primera petición. Y la casilla tal cual, por
      // referencia, no una copia: es lo que permite saber al responder si lo que hay ahora en esa
      // casilla sigue siendo lo que se mandó.
      const delAnio = self.anio;
      const delMes = self.numeroMes;
      const pendientes = self.pendientes.map(function(casilla) {
        return {
          clave: casilla.clave, casilla: casilla,
          fecha: casilla.fecha, dato: casilla.dato,
          valor: casilla.valor, valorVisto: casilla.valorVisto
        };
      });

      let fallos = 0;
      let guardados = 0;
      // Los que al llegar su turno ya no estaban pendientes: no son un fallo, el usuario los
      // retiró. No se cuentan como guardados para que el aviso no diga que entró algo que no salió.
      let retirados = 0;

      return pendientes.reduce(function(cadena, cambio) {
        return cadena.then(function() {
          // La instantánea se tomó antes de la primera petición y el lote tarda. Si lo que hay
          // ahora en esa casilla ya no es lo que se apuntó, no se manda: el caso que importa es el
          // de quien devuelve una casilla a su valor original —`editar` la deja sin nada
          // pendiente— y vería guardarse igual la cifra que acababa de retirar.
          if (self.casillas[cambio.clave] !== cambio.casilla) {
            retirados++;
            return;
          }
          return FactCobro.guardarDatoManual(delAnio, delMes, cambio.fecha, cambio.dato,
            cambio.valorVisto, cambio.valor).then(function() {
              guardados++;
              self.apuntarLoQueEntro(cambio);
            }).catch(function(err) {
              fallos++;
              // El fallo se cuelga de la casilla, y `fijarCasilla` decide si aún aplica: si quien
              // edita la ha devuelto a su valor guardado mientras volaba la petición, ya no hay
              // ninguna cifra sin guardar de la que avisar.
              self.fijarCasilla(cambio.clave, Object.assign(
                { fecha: cambio.fecha, dato: cambio.dato, pendiente: false },
                self.casillas[cambio.clave],
                {
                  fallo: {
                    motivo: (err && err.message)
                      || 'el servidor no ha dicho el motivo. Recarga la pantalla y compruébalo antes de volver a intentarlo.'
                  }
                }));
            });
        });
      }, Promise.resolve()).then(function() {
        self.guardando = false;
        if (fallos === 0 && guardados === 0 && retirados > 0) {
          // Todo lo que había se retiró antes de llegar su turno: no se ha guardado nada y decirlo
          // es lo honrado. «0 datos guardados» se leería como un fallo. Este es el único aviso que
          // no habla de ninguna casilla, y por eso es el único que no se deriva de ellas.
          self.avisoDelLote = 'No había nada que guardar: los cambios se habían deshecho.';
        }
        // Se vuelve a leer el mes en cualquier caso: con el dato nuevo cambian las columnas
        // derivadas y el pie, y si algo falló por un conflicto hay que ver qué hay guardado de
        // verdad antes de volver a intentarlo. No se usa `recargar()`, que tiraría lo tecleado con
        // los cambios que no entraron.
        //
        // Y **conservando lo tecleado si esta lectura también falla** (#215): dos fallos seguidos
        // —la escritura y la relectura— dejaban la pantalla sin mes, o sea sin las casillas donde
        // están las cifras que no entraron, y entonces el único camino era teclearlas otra vez.
        return self.cargar({ conservarLoTecleado: true });
      });
    },

    /**
     * Apunta en su casilla que el cambio **ha entrado**, y solo si sigue siendo el que se mandó.
     *
     * Mientras se guarda las casillas salen apagadas, así que por el camino normal lo que vuelve es
     * lo que salió. La comprobación se queda por lo mismo que la de `guardar`: si alguien vuelve a
     * dejarlas escribibles, dar el cambio por cerrado sin mirar se lleva la segunda cifra que se
     * tecleó sin haberla mandado —desaparece de la pantalla y nadie la ha guardado—, y eso no se
     * nota.
     *
     * Y si sigue habiendo una edición nueva, se le pone como `valorVisto` lo que acabamos de
     * escribir, que es lo que hay ahora en la base. Sin eso, el siguiente «Guardar» mandaría el
     * valor que se vio al empezar y el guardado condicional lo rechazaría con un 409 diciendo que
     * otra persona lo ha cambiado, cuando lo había cambiado quien está escribiendo. Esa casilla
     * tampoco se queda con el «guardado»: lo retira `fijarCasilla`, porque lo que hay escrito ya no
     * es lo que entró.
     *
     * Esto sigue haciendo falta con `reconciliarCasillas` puesto, y no es lo mismo: el lote son
     * varias peticiones seguidas y la lectura del mes llega **al final**, así que entre una
     * petición y la siguiente esta es la única que sabe qué se acaba de escribir. Y es además lo
     * que distingue «lo movió otra persona» de «lo moví yo en este lote»: sin reapuntarlo aquí, la
     * lectura del final vería la cifra cambiada y avisaría de un conflicto que no ha habido.
     */
    apuntarLoQueEntro: function(cambio) {
      const casilla = this.casillas[cambio.clave];
      if (casilla === cambio.casilla) {
        this.fijarCasilla(cambio.clave, {
          fecha: cambio.fecha, dato: cambio.dato, pendiente: false, exito: true
        });
        return;
      }
      if (casilla) {
        this.fijarCasilla(cambio.clave,
          Object.assign({}, casilla, { valorVisto: cambio.valor }));
      }
    },

    // ------------------------------ la línea de registrado fuera de mes (#289, D-DIA-EMISION)

    /** Despliega o pliega el detalle de la línea. Volver a pulsar lo cierra. */
    alternarDetalleFueraDeMes: function() {
      this.detalleFueraDeMesAbierto = !this.detalleFueraDeMesAbierto;
    },
    /**
     * Por qué estos documentos están en una línea del mes y no en un día, dicho junto al detalle y
     * no en la cabecera de la pantalla.
     *
     * Son las dos mitades que evitan que la pantalla se contradiga: que **sí** cuentan en el TOTAL
     * del mes —si no, la línea se leería como algo que se ha quitado— y que **no** están en ningún
     * día, que es lo que hace que cada día diga lo mismo que la consulta de FACTURACIÓN. La frase de
     * quiénes son esos documentos la manda la API (`registradoFueraDeMes.nota`), que es la que tiene
     * el criterio de a qué mes cuenta cada uno.
     */
    notaFueraDeMes: function() {
      return 'Son documentos emitidos en un mes ya cerrado —cuya cifra no se mueve— y registrados '
        + 'en este, así que cuentan en la facturación de este mes y están sumados en el TOTAL. No '
        + 'se reparten en ningún día: cada día enseña lo que se emitió en él, igual que la consulta '
        + 'de FACTURACIÓN.';
    },

    // ------------------------------ el aviso de que la cifra no recoge todo (#295)

    /**
     * El rótulo del aviso junto a la cifra, y **no dice siempre lo mismo** a propósito.
     *
     * «Incompleta» se afirma solo cuando falta dinero. Si lo que se ha quedado fuera no llevaba
     * importe —una factura sin tipo no aporta nada— la cifra en euros está completa y lo que falta
     * son documentos del desglose: decir «incompleta» ahí sería el mismo defecto que el aviso mudo,
     * al revés, porque justo debajo se leería «falta por recoger 0 €».
     *
     * Y son **tres** casos y no dos, porque el importe puede no saberse: ahí la cifra sí está
     * incompleta —solo que no se sabe en cuánto—, así que vale este mismo rótulo y lo que cambia es
     * la nota (P2 Codex, ronda 2).
     */
    tituloDescuadre: function(descuadre) {
      if (!descuadre) return '';
      return this.noSeSabeCuantoFalta(descuadre) || this.llevaDinero(descuadre.importe)
        ? 'Esta cifra está incompleta.'
        : 'Esta cifra no recoge todos los documentos del mes.';
    },
    /**
     * Cuándo el aviso tiene documentos fuera y **no sabe cuánto falta**: el servidor manda el
     * importe a null en cuanto uno de esos documentos no trae un «Total factura» que se pueda leer,
     * porque la suma de los demás se leería como el total de lo que falta.
     *
     * Se pregunta aparte de `llevaDinero` y no se confunde con el cero: un importe cero es un dato
     * —lo que se cayó no llevaba dinero— y «no se sabe» es lo contrario de un dato. `llevaDinero`
     * convierte null en 0 por su `Number(valor) || 0`, así que por sí sola los juntaría y la
     * pantalla acabaría diciendo «no les falta importe» de unos euros a los que no se sabe cuánto
     * les falta. Mismo criterio que `noSeSabeCuantoFalta` del PDF, palabra por palabra.
     */
    noSeSabeCuantoFalta: function(descuadre) {
      return !!descuadre && (descuadre.importe === null || descuadre.importe === undefined);
    },
    /**
     * Las dos cosas que dirección pidió que dijera el aviso: cuántos documentos se han quedado
     * fuera y cuánto falta por recoger.
     *
     * Cada una solo si se sabe. El descuadre entre la cifra guardada y las filas que la explican es
     * un importe y no un número de documentos, así que ahí el servidor manda `documentos` a null; y
     * un importe que no llega al céntimo no se nombra, por lo mismo que en `tituloDescuadre`.
     */
    resumenDelDescuadre: function(descuadre) {
      const partes = [];
      if (descuadre.documentos !== null && descuadre.documentos !== undefined) {
        // Sin nombrar la causa: el porqué lo dice la frase de cada motivo, que la manda el
        // servidor, y hay más de una —una factura que se cae del reparto por días y otra que
        // ninguna consulta del mes alcanza no se han quedado fuera de lo mismo—.
        partes.push(descuadre.documentos + (Number(descuadre.documentos) === 1
          ? ' documento no está recogido en esta cifra'
          : ' documentos no están recogidos en esta cifra'));
      }
      if (this.noSeSabeCuantoFalta(descuadre)) {
        partes.push('no se sabe cuánto falta por recoger');
      } else if (this.llevaDinero(descuadre.importe)) {
        partes.push('falta por recoger ' + this.formatImporteDescuadre(descuadre.importe) + ' €');
      }
      return partes.length ? partes.join(' y ') + '.' : '';
    },
    /**
     * Las cifras de un motivo, detrás de su frase: el servidor manda la frase **sin importes
     * dentro** para que los dé formato la pantalla, que es la que sabe en qué unidades se lee la
     * tabla.
     */
    detalleDelMotivo: function(motivo) {
      const partes = [];
      if (motivo.documentos !== null && motivo.documentos !== undefined) {
        partes.push(motivo.documentos
          + (Number(motivo.documentos) === 1 ? ' documento' : ' documentos'));
      }
      if (motivo.importe !== null && motivo.importe !== undefined) {
        partes.push(this.formatImporteDescuadre(motivo.importe) + ' €');
      }
      return partes.length ? ' (' + partes.join(' · ') + ')' : '';
    },
    /**
     * Lo que el aviso **no** es, dicho con esas palabras: la cifra del mes y el TOTAL se siguen
     * leyendo tal como están.
     *
     * Hace falta decirlo porque el aviso está pegado a dos cifras: sin esta línea, un aviso al lado
     * de un importe se lee como que ese importe ya lleva la corrección aplicada, y aquí no se ha
     * reimputado nada —el encargo de la ficha es avisar, no corregir—.
     *
     * Y **tampoco dice siempre lo mismo**, por lo mismo que `tituloDescuadre` y `marcaDelTotal`:
     * cuando lo que se ha quedado fuera no llevaba importe, a los euros no les falta nada y llamarlos
     * «incompletos» aquí contradiría al rótulo de arriba, que acaba de decir que lo que falta son
     * documentos. Las tres frases salen de la misma pregunta —`llevaDinero`— para que no puedan
     * separarse (P2 Codex, ronda 1).
     *
     * Y el tercer caso —no se sabe cuánto falta— no puede caer en el segundo: decir «no les falta
     * importe» de unos euros a los que no se sabe cuánto les falta es lo contrario de lo que pasa,
     * y dicho en la misma franja que acaba de avisar (P2 Codex, ronda 2).
     *
     * Y ese tercer caso **no afirma que el documento se haya quedado fuera**: desde la ronda 4 de la
     * puesta al día puede estar dentro de la cifra con su importe tomado como cero, que es otra cosa
     * y la dice su propia línea. Afirmarlo aquí contradiría a esa línea, que es el mismo defecto que
     * el aviso mudo contado al revés.
     */
    notaDescuadre: function(descuadre) {
      // Sin tabla no hay TOTAL que nombrar: la cifra del mes es la única que se está enseñando, y
      // mandar a compararla con un pie que no se dibuja es el mismo defecto que el aviso mudo,
      // contado al revés (P2 Codex, ronda 5).
      const cifras = this.hayTabla ? 'El TOTAL de la tabla y la cifra del mes' : 'La cifra del mes';
      const seLeen = this.hayTabla
        ? 'se siguen leyendo tal como están' : 'se sigue leyendo tal como está';
      const incompletos = this.hayTabla ? 'están incompletos' : 'está incompleta';
      if (this.noSeSabeCuantoFalta(descuadre)) {
        return cifras + ' ' + seLeen + ', y ' + incompletos + ': lo que no se sabe es en cuánto, '
          + 'porque alguno de los documentos del mes no trae un «Total factura» que se pueda leer. '
          + 'Cada línea de aquí arriba dice si ese documento se ha quedado fuera de la cifra o si '
          + 'está dentro con su importe tomado como cero. Este aviso no corrige nada, y lo que se '
          + 'ha quedado fuera no se ha reimputado a ningún día.';
      }
      if (this.llevaDinero(descuadre && descuadre.importe)) {
        return cifras + ' ' + seLeen + ': este aviso dice que ' + incompletos + ', no '
          + (this.hayTabla ? 'los' : 'la') + ' corrige. Lo que se ha quedado fuera no se ha '
          + 'reimputado a ningún día.';
      }
      return cifras + (this.hayTabla
        ? ' en euros se leen tal como están y no les falta importe: '
        : ' en euros se lee tal como está y no le falta importe: ')
        + 'lo que no está completo es el desglose de documentos del mes. Este aviso no corrige '
        + 'nada, y lo que se ha quedado fuera no se ha reimputado a ningún día.';
    },
    /**
     * El rótulo del aviso largo, que **tampoco dice siempre lo mismo**: con tabla delante lo que no
     * recoge todo es la tabla, y sin ella lo que se está enseñando es la cifra del mes a secas.
     *
     * Escrito a mano en la plantilla afirmaba que había una tabla también donde no la hay, que es
     * mandar a mirar algo que no está en la pantalla. Sale del mismo `hayTabla` que la nota de
     * debajo para que las dos no puedan contarlo distinto.
     */
    tituloAvisoDescuadre: function() {
      return this.hayTabla
        ? 'Esta tabla no recoge todo lo que hay en Facturas para el mes'
        : 'La cifra del mes no recoge todo lo que hay en Facturas';
    },
    /**
     * La marca que lleva la fila TOTAL del pie, que dice **qué** le falta y no solo que le falta
     * algo.
     *
     * Misma pregunta que `tituloDescuadre`: con dinero que falta —o con un importe que no se sabe—
     * el TOTAL en euros está «incompleto»; sin él, lo que falta son documentos del desglose y el
     * TOTAL en euros es el del mes. En mayúsculas lo pone la hoja de estilo, no este texto.
     */
    marcaDelTotal: function(descuadre) {
      return this.noSeSabeCuantoFalta(descuadre) || this.llevaDinero(descuadre && descuadre.importe)
        ? 'incompleto' : 'faltan documentos';
    },
    /**
     * El importe del aviso, que **nunca se lee como cero** por redondeo.
     *
     * Mismo criterio que `formatImporteExcluido` y por el mismo motivo (#257): el resto de la
     * pantalla lee euros enteros, pero aquí redondear 0,01 € a «0 €» negaría el descuadre justo en
     * la línea que lo tiene. Con signo, porque un abono que se cae del reparto falta en negativo y
     * sin el signo parecería que falta facturación de más.
     */
    formatImporteDescuadre: function(valor) {
      const numero = Number(valor) || 0;
      if (Math.round(numero) === 0) {
        return (numero > 0 ? '+' : '') + numero.toFixed(2).replace('.', ',');
      }
      return this.formatEurosConSigno(numero);
    },

    // ------------------------------------------------------------------- formatos

    anchoBarra: function(porcentaje) {
      if (porcentaje === null || porcentaje === undefined || porcentaje === '') return 0;
      const valor = Number(porcentaje);
      if (!isFinite(valor) || valor <= 0) return 0;
      // Se recorta a 100 para que la barra no se salga de su pista, pero la cifra de al lado sí
      // enseña el porcentaje real: pasarse del objetivo es la información que se busca.
      return valor > 100 ? 100 : valor;
    },
    colorCumplimiento: function(porcentaje) {
      if (porcentaje === null || porcentaje === undefined || porcentaje === '') return '#c8d3e5';
      return Number(porcentaje) >= 100 ? '#0F6E56' : '#185FA5';
    },
    claseCumplimiento: function(porcentaje) {
      if (porcentaje === null || porcentaje === undefined || porcentaje === '') return '';
      return Number(porcentaje) >= 100 ? 'var-positivo' : '';
    },
    /** Lo que falta en verde cuando ya no falta nada —es negativo— y sin color mientras falte. */
    claseFalta: function(falta) {
      if (falta === null || falta === undefined || falta === '') return '';
      return Number(falta) <= 0 ? 'var-positivo' : '';
    },
    /** Verde por encima del objetivo, rojo por debajo. Un hueco no lleva color. */
    claseDiferencia: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '';
      return Number(valor) >= 0 ? 'var-positivo' : 'var-negativo';
    },
    /**
     * Una fecha del servidor (`2026-10-02`) como se escribe aquí: `02/10/2026`. Sin `new Date()`,
     * que interpreta la cadena en UTC y en Valladolid puede devolver el día de antes.
     */
    formatFecha: function(valor) {
      if (!valor) return '—';
      const partes = String(valor).substring(0, 10).split('-');
      if (partes.length !== 3) return String(valor);
      return partes[2] + '/' + partes[1] + '/' + partes[0];
    },
    /** El día y el mes, que es lo que lleva la primera columna: `02/10`. */
    formatDia: function(valor) {
      if (!valor) return '—';
      const partes = String(valor).substring(0, 10).split('-');
      if (partes.length !== 3) return String(valor);
      return partes[2] + '/' + partes[1];
    },
    /** Un instante del servidor como `02/10/2026 08:15`. Aquí sí hace falta la hora local. */
    formatInstante: function(valor) {
      if (!valor) return '—';
      const fecha = new Date(valor);
      if (isNaN(fecha.getTime())) return String(valor);
      const dos = function(n) { return n < 10 ? '0' + n : String(n); };
      return dos(fecha.getDate()) + '/' + dos(fecha.getMonth() + 1) + '/' + fecha.getFullYear()
        + ' ' + dos(fecha.getHours()) + ':' + dos(fecha.getMinutes());
    },
    /** Euros enteros, que es como se leen estas cifras. Un hueco es un guion, nunca un cero. */
    formatEuros: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '—';
      return Math.round(Number(valor)).toLocaleString('es-ES');
    },
    formatEurosConSigno: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '—';
      const signo = Number(valor) > 0 ? '+' : '';
      return signo + Math.round(Number(valor)).toLocaleString('es-ES');
    },
    formatPorcentaje: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '—';
      return Number(valor).toFixed(1).replace('.', ',') + ' %';
    },
    /**
     * Cuándo un importe excluido «lleva dinero»: medio céntimo, o sea lo que redondeado da al menos
     * 0,01 €. En un solo sitio porque lo preguntan dos textos —el rótulo y el detalle de cada
     * regla— y separados uno acabaría nombrando un importe que el otro calla.
     */
    llevaDinero: function(valor) {
      return Math.abs(Number(valor) || 0) >= 0.005;
    },
    /**
     * Un importe de las exclusiones, que nunca se lee como cero (#257).
     *
     * El resto de la pantalla lee euros enteros y aquí se mantiene, con una salvedad: este importe
     * sólo se escribe cuando hay dinero, así que redondearlo a euros convertiría 0,01 € en «sumaban
     * 0 €» —y un importe negativo pequeño en «-0 €»—, que es negar la anomalía justo en la línea
     * que la tiene. Cuando el euro entero sale cero se bajan los céntimos; de 1 € para arriba se
     * lee como todo lo demás. Lo señaló la revisión del 09/10.
     *
     * Los céntimos van con `toFixed`, no con `toLocaleString`: por debajo de 1 € no hay millares
     * que agrupar, y así el texto no depende de que el entorno traiga los datos de la configuración
     * regional.
     */
    formatImporteExcluido: function(valor) {
      const numero = Number(valor) || 0;
      if (Math.round(numero) === 0) return numero.toFixed(2).replace('.', ',');
      return this.formatEuros(numero);
    },
    /**
     * El rótulo de lo que una regla ha dejado fuera de este sumando (#257): cuántas líneas y, si
     * llevaban dinero, cuánto.
     *
     * El importe sólo se nombra cuando NO es cero, y eso no es por ahorrar texto: cero es la
     * respuesta esperada —son líneas que no se facturan y no valen nada— y decirlo cada vez le
     * quitaría peso justo al día en que deje de ser cero, que es cuando hay que mirarlo.
     *
     * Y cuando NINGUNA regla ha dejado fuera una sola línea, el rótulo lo dice con esas palabras en
     * vez de anunciar «0 líneas»: las reglas se enseñan igual —es lo único que avisa de que una ha
     * dejado de casar— y un rótulo que dice «no cuentan 0 líneas» encima de una lista de reglas es
     * la pantalla contradiciéndose a sí misma.
     */
    rotuloExcluidas: function(linea) {
      const exclusiones = linea.exclusiones || [];
      const lineas = exclusiones.reduce(function(suma, x) {
        return suma + (Number(x.lineas) || 0);
      }, 0);
      const importe = exclusiones.reduce(function(suma, x) {
        return suma + (Number(x.importe) || 0);
      }, 0);
      // Si de alguna regla no se sabe cuánto se llevó, la suma de las demás NO es lo que se
      // llevaron todas, así que no se nombra. Mismo criterio que `noSeSabeCuantoFalta` del aviso:
      // el `Number(null) || 0` del reduce de arriba convertiría ese «no se sabe» en un cero.
      const alguna = exclusiones.some(function(x) {
        return x.importe === null || x.importe === undefined;
      });
      if (lineas === 0) {
        return 'Las reglas de lo que no se factura no han dejado fuera ninguna línea de este dato:';
      }
      const cuantas = lineas === 1 ? '1 línea que no se factura' : lineas + ' líneas que no se facturan';
      let dinero = '';
      if (alguna) {
        dinero = ', y de alguna no se sabe cuánto dinero llevaba';
      } else if (this.llevaDinero(importe)) {
        dinero = ', que sumaban ' + this.formatImporteExcluido(importe) + ' €';
      }
      return 'No cuentan en este dato ' + cuantas + dinero + ':';
    },
    /** Una regla con lo que se llevó y el motivo con el que se decidió. */
    textoExclusion: function(exclusion) {
      const lineas = Number(exclusion.lineas) || 0;
      const motivo = exclusion.motivo || 'no se factura';
      const cual = this.deQueReglaSon(exclusion);
      if (lineas === 0) {
        // Una regla que no ha dejado fuera ninguna línea se enseña igual, y por un motivo: o este
        // mes no tuvo líneas de eso, o la regla ha dejado de casar —una materia renombrada—.
        // Esconderla haría que las dos cosas se vieran igual, que es el defecto de esta incidencia.
        return '· ninguna línea ' + cual + ' (' + motivo + ')';
      }
      // El importe puede venir sin dato —alguna de esas líneas trae un «Total con IVA» que no se
      // puede leer— y entonces se dice, en vez de callar como si no llevaran dinero.
      const sinDato = exclusion.importe === null || exclusion.importe === undefined;
      let dinero = '';
      if (sinDato) {
        dinero = ' · no se sabe cuánto dinero llevaban';
      } else if (this.llevaDinero(exclusion.importe)) {
        dinero = ' · incluían ' + this.formatImporteExcluido(exclusion.importe) + ' €';
      }
      return '· ' + lineas + (lineas === 1 ? ' línea ' : ' líneas ') + cual
        + ' — ' + motivo + dinero;
    },
    /**
     * «del cliente «SIMECAL»», «de la materia «BAJA TENSIÓN»»: la regla nombrada, con el valor que
     * de verdad casó y ya contraída con la preposición que la precede.
     *
     * La contracción va aquí y no en quien escribe la frase porque en español «de» + «el» es «del»,
     * y los dos textos que nombran una regla —el detalle y el caso de cero líneas— la necesitan
     * igual. Si la preposición se quedara fuera, los dos escribirían «de el cliente», que es lo que
     * señaló dirección el 09/10 al probar la pantalla, y arreglar uno dejaría el otro mal.
     */
    deQueReglaSon: function(exclusion) {
      const comoSeLee = {
        MATERIA: 'de la materia', SERVICIO: 'del servicio', CLIENTE: 'del cliente'
      };
      const que = comoSeLee[exclusion.tipo];
      // Una regla de una versión futura que esta pantalla no sabe nombrar dice su clave: pintar un
      // hueco donde va el «de qué» sería la base encogiendo sin explicarse.
      if (!que || !exclusion.valor) return 'de ' + (exclusion.clave || 'una regla');
      return que + ' «' + exclusion.valor + '»';
    }
  }
};
</script>
